import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import polygonClipping from 'polygon-clipping';
import { type SplitMode } from '../utils/splitModeConfig';
import { resolveWarehouseNow, isTaskDateSelectable } from '../utils/taskDateRule';
import {Button, Checkbox, ConfigProvider, Drawer, Input, Modal, Select, Steps, Table, Typography, notification, message} from 'antd';
import {
    PlusCircleOutlined,
    DeleteOutlined,
    EnvironmentOutlined,
    InboxOutlined,
    CloseOutlined,
    CopyOutlined,
    EditOutlined,
    ArrowLeftOutlined,
    ReadOutlined,
    CheckCircleFilled
} from '@ant-design/icons';
import {GoogleMap, useJsApiLoader, Marker, Polygon} from '@react-google-maps/api';
import {Spin} from 'antd';
import {useQuery, useMutation, useLazyQuery} from '@apollo/client';
import {GET_WAREHOUSE_BY_ID, GetWarehouseByIdResponse} from '../../../../graphql/queries/warehouseQueries';
import {GET_ROUTE_ORDERS, GET_ROUTE_ORDERS_FOR_DSP, GetRouteOrdersResponse, GetRouteOrdersVariables} from '../graphql/getRouteOrders';
import {LIST_DRIVER_CAPACITIES, ListDriverCapacitiesResponse} from '../graphql/listDriverCapacities';
import {GET_ZIPCODE_MAP_BOUNDARIES, GetZipcodeMapBoundariesResponse} from '../graphql/getZipcodeMapBoundaries';
import {FAST_ROUTE_PLANNING, FAST_ROUTE_PLANNING_FOR_DSP, FastRoutePlanningResponse, FastRoutePlanningVariables} from '../graphql/fastRoutePlanning';
import {IS_AREA_WITHOUT_RECENT_ALGO_SPLIT, IsAreaWithoutRecentAlgoSplitResponse, IsAreaWithoutRecentAlgoSplitVariables} from '../graphql/isAreaWithoutRecentAlgoSplit';
import {
    GET_LAST_PLANNING_ASSIGN_TYPE_DETAIL,
    GetLastPlanningAssignTypeDetailResponse,
    GetLastPlanningAssignTypeDetailVariables,
    GET_LAST_PLANNING_ASSIGN_TYPE_DETAIL_FOR_DSP,
    LastPlanningAssignTypeDetailOutput,
} from '../graphql/getLastPlanningAssignTypeDetail';
import {
    GET_DSP_TASK_DETAIL_WITH_CHILDREN,
    GET_DSP_TASK_DETAIL_WITH_CHILDREN_FOR_DSP,
    GetDspTaskDetailWithChildrenResponse,
    GetDspTaskDetailWithChildrenVariables,
    DspTaskDetailWithChildrenOutput,
} from '../graphql/getDspTaskDetailWithChildren';
import {
    FIND_DRIVERS_BY_DSP_IDS,
    FindDriversByDspIdsResponse,
    FindDriversByDspIdsVariables,
    DriverType
} from '../graphql/deliveryRoutes';
import {
    ASSIGN_DRIVERS,
    AssignTaskInput,
    AssignType,
    AssignDriversResponse,
    AssignDriversVariables,
    DriverAddressInput
} from '../graphql/assignDrivers';
import {
    SUBMIT_CLUSTERING_TASK,
    SubmitClusteringTaskResponse,
    SubmitClusteringTaskVariables,
} from '../graphql/submitClusteringTask';
import {
    SUBMIT_ROUTE_PLANNING_TASK,
    SubmitRoutePlanningTaskResponse,
    SubmitRoutePlanningTaskVariables,
} from '../graphql/submitRoutePlanningTask';
import {
    GET_ROUTE_PLANNING_TASK_RESULT,
    GetRoutePlanningTaskResultResponse,
    GetRoutePlanningTaskResultVariables,
    RoutePlanningTaskQueryInput,
} from '../graphql/getRoutePlanningTaskResult';
import {
    IS_TEAM_PICKUP_WAREHOUSE,
    IsTeamPickupWarehouseResponse,
    IsTeamPickupWarehouseVariables,
} from '../graphql/isTeamPickupWarehouse';
import {
    GET_DRIVER_START_END_ADDRESS,
    GetDriverStartEndAddressResponse,
    GetDriverStartEndAddressVariables,
    DriverAddressOutput,
} from '../graphql/getDriverStartEndAddress';
import {
    CHECK_DATA_STALE_BY_PLANNING_POOL,
    CheckDataStaleByPlanningPoolResponse,
    CheckDataStaleByPlanningPoolVariables,
    DataStaleCheckResult,
} from '../graphql/checkDataStaleByPlanningPool';
import {
    CHECK_REPUBLISH_CONFLICT,
    CheckRepublishConflictResponse,
    CheckRepublishConflictVariables,
    ScannedOrderInfo,
} from '../graphql/checkRepublishConflict';
import { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import SplitRoutePublishConfirmModal from './SplitRoutePublishConfirmModal';
import SplitOrderPublishAllModal from './SplitOrderPublishAllModal';
import DataStaleModal from './DataStaleModal';
import RepublishConflictModal from './RepublishConflictModal';
import AddressInputModal, {AddressResult} from './AddressInputModal';
import { saveSplitRouteCache, loadSplitRouteCache, deleteSplitRouteCache } from '../utils/splitRouteCacheUtil';


const {Text} = Typography;

export interface SplitRouteRecord {
    key: string;
    dspTaskId?: number;
    areaId?: number;
    areaName?: string;
    dspId?: number;
    dspName?: string;
    newParcels: number;
    plannedParcels: number;
    totalParcels: number;
    driverAccountId?: string;
    driverId?: number;
    split?: boolean;
    childTaskList?: any[];
}

interface SplitRouteDrawerProps {
    visible: boolean;
    record: SplitRouteRecord | null;
    warehouseId: number | undefined;
    /** 仓库当天日期（YYYY-MM-DD），用于「连续 3 天未使用算法分单」引导 */
    warehouseToday: string;
    /** 仓库当前时间（服务器按仓库时区换算，来自主页），决定发布确认弹窗中哪些日期可选 */
    warehouseCurrentTime?: string;
    /** 取最新仓库时间（确认发布时复核所选日期用）；未传时用 warehouseCurrentTime */
    fetchWarehouseNow?: () => Promise<Dayjs>;
    driversByDspId: Record<number, DriverType[]>;
    allowedModes?: SplitMode[];
    mode?: 'wms' | 'dsp';
    onClose: () => void;
    onSave?: () => void;
    onPublishSuccess?: () => void;
    onDataStaleRefresh?: () => void;
}

const GOOGLE_MAPS_API_KEY = 'AIzaSyD8huYA1dL8uG9O1a77bnW3JtpTjUimjmc';

const defaultCenter = {lat: 34.0522, lng: -118.2437}; // 洛杉矶

const formatAddress = (addr?: DriverAddressInput | null): string => {
    if (!addr) return '';
    return [addr.streetAddress, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ');
};

const cleanAddress = (addr?: DriverAddressInput | null): DriverAddressInput | null => {
    if (!addr) return null;
    const {__typename, ...rest} = addr as any;
    return rest as DriverAddressInput;
};

// 前12个为常见基础色，后18个为高区分度扩展色
const SEGMENT_ACCENT_COLORS = [
    // —— 基础色（日常分段够用） ——
    {name: 'blue', accent: '#3B82F6'},       // 蓝
    {name: 'orange', accent: '#F97316'},      // 橙
    {name: 'green', accent: '#22C55E'},       // 绿
    {name: 'red', accent: '#EF4444'},         // 红
    {name: 'pink', accent: '#EC4899'},        // 粉
    {name: 'purple', accent: '#A855F7'},      // 紫
    {name: 'yellow', accent: '#EAB308'},      // 黄
    {name: 'cyan', accent: '#06B6D4'},        // 青
    {name: 'indigo', accent: '#6366F1'},      // 靛蓝
    {name: 'teal', accent: '#14B8A6'},        // 鸭绿
    {name: 'rose', accent: '#F43F5E'},        // 玫红
    {name: 'lime', accent: '#84CC16'},        // 黄绿
    // —— 扩展色（已打散排序，相邻色相互相远离，不扎堆） ——
    {name: 'scarlet', accent: '#CE4422'},     // 朱红 (h12)
    {name: 'cobalt', accent: '#2244CE'},      // 钴蓝 (h228)
    {name: 'chartreuse', accent: '#A6CE22'},  // 黄绿 (h74)
    {name: 'fuchsia', accent: '#CE22CB'},     // 洋红 (h301)
    {name: 'jade', accent: '#22CE8F'},        // 玉绿 (h158)
    {name: 'tangerine', accent: '#E09C3E'},   // 橘黄 (h35)
    {name: 'iris', accent: '#513EE0'},        // 鸢尾蓝紫 (h247)
    {name: 'spring', accent: '#79E03E'},      // 春绿 (h98)
    {name: 'magenta', accent: '#E03EB4'},     // 品红 (h316)
    {name: 'cerulean', accent: '#3EA2E0'},    // 蔚蓝 (h203)
    {name: 'gold', accent: '#CEC022'},        // 金 (h55)
    {name: 'violet', accent: '#4D22CE'},      // 蓝紫 (h255)
    {name: 'grass', accent: '#36CE22'},       // 草绿 (h113)
    {name: 'raspberry', accent: '#CE225B'},   // 覆盆子红 (h340)
    {name: 'slate', accent: '#546378'},       // 石板灰（中性，h215）
    {name: 'citron', accent: '#D2E03E'},      // 柠黄绿 (h65)
    {name: 'orchid', accent: '#BA3EE0'},      // 兰紫 (h286)
    {name: 'emerald', accent: '#3EE054'},     // 翠绿 (h128)
    // —— 深/灰调扩展 10 个（饱和度更低、更暗，与亮色梯队区分） ——
    {name: 'brown', accent: '#8C5A2B'},      // 棕 (h28)
    {name: 'navy', accent: '#21407A'},       // 藏青 (h218)
    {name: 'khaki', accent: '#B3A36E'},      // 卡其 (h47)
    {name: 'plum', accent: '#5E2E6E'},       // 梅紫 (h286)
    {name: 'forest', accent: '#1F5C3A'},     // 墨绿 (h148)
    {name: 'maroon', accent: '#7C2E3E'},     // 酒红 (h348)
    {name: 'pine', accent: '#205E5E'},       // 松绿 (h180)
    {name: 'mauve', accent: '#9E6B8C'},      // 藕荷 (h322)
    {name: 'olive', accent: '#6E7A2E'},      // 橄榄 (h68)
    {name: 'charcoal', accent: '#3C424B'}  // 炭灰 (h216)
];
// bg 和 border 由 accent 派生，保持同色系
const SEGMENT_COLOR_PALETTE = SEGMENT_ACCENT_COLORS.map(c => ({
    ...c,
    bg: c.accent + '3B',      // 23% opacity
    border: c.accent + '4D'  // 30% opacity
}));

const GRAY_PALETTE = {name: 'gray', accent: '#9CA3AF', bg: '#F3F4F6', border: '#D1D5DB'};

const getSegPalette = (seg: {colorIndex: number; assignedOrderIds: number[]}) =>
    seg.assignedOrderIds.length > 0 ? SEGMENT_COLOR_PALETTE[seg.colorIndex] : GRAY_PALETTE;

export type { SplitMode } from '../utils/splitModeConfig';


/**
 * 各模式 segment 类型
 */
interface MapSegment {
    id: string;
    name: string;
    colorIndex: number;
    driverAccountId: string;
    startAddress?: DriverAddressInput | null;
    endAddress?: DriverAddressInput | null;
    polygons: google.maps.LatLngLiteral[][];
    assignedOrderIds: number[];
    totalParcelCount: number;
    parcelNumberStart?: number | null;
    parcelNumberEnd?: number | null;
}

interface ZipcodeSegment {
    id: string;
    name: string;
    colorIndex: number;
    driverAccountId: string;
    zipcodes: string[];
    assignedOrderIds: number[];
    totalParcelCount: number;
    startAddress?: DriverAddressInput | null;
    endAddress?: DriverAddressInput | null;
    parcelNumberStart?: number | null;
    parcelNumberEnd?: number | null;
}

interface StopSegment {
    id: string;
    name: string;
    colorIndex: number;
    driverAccountId: string;
    stopRanges: StopRange[];
    assignedOrderIds: number[];
    totalParcelCount: number;
    startAddress?: DriverAddressInput | null;
    endAddress?: DriverAddressInput | null;
    parcelNumberStart?: number | null;
    parcelNumberEnd?: number | null;
}

interface StopRange {
    id: string;
    parcelNumberStart: string;
    parcelNumberEnd: string;
}

interface AlgorithmSegment {
    id: string;
    name: string;
    colorIndex: number;
    driverAccountId: string;
    capacityMin: number | null;
    capacityMax: number | null;
    preferredZipcodes: string[];
    startAddress?: DriverAddressInput | null;
    endAddress?: DriverAddressInput | null;
    assignedOrderIds: number[];
    totalParcelCount: number;
    parcelNumberStart?: number | null;
    parcelNumberEnd?: number | null;
    algoRecordId?: number | null;
}

interface PinningTarget {
    segId: string;
    field: 'start' | 'end';
    segType: 'map' | 'zipcode' | 'stop' | 'algo';
}

interface ZipcodeBoundaryEntry {
    zipcode: string;
    paths: google.maps.LatLngLiteral[];
    parcelCount: number;
}

// 未取到仓库分单方式配置时默认仅开启算法分单（与 resolveAvailableModes 保持一致）
const DEFAULT_MODES: SplitMode[] = ['algo'];

const SplitRouteDrawer: React.FC<SplitRouteDrawerProps> = ({
                                                               visible,
                                                               record,
                                                               warehouseId,
                                                               warehouseToday,
                                                               warehouseCurrentTime,
                                                               fetchWarehouseNow,
                                                               driversByDspId,
                                                               allowedModes,
                                                               onClose,
                                                               onSave,
                                                               onPublishSuccess,
                                                               onDataStaleRefresh,
                                                               mode = 'dsp'
                                                           }) => {

    // 按 mode 选择 WMS / DSP 端的 query
    const routeOrdersQuery = mode === 'wms' ? GET_ROUTE_ORDERS : GET_ROUTE_ORDERS_FOR_DSP;
    const routeOrdersKey = mode === 'wms' ? 'getRouteOrders' : 'getRouteOrdersForDsp';
    const lastAssignQuery = mode === 'wms' ? GET_LAST_PLANNING_ASSIGN_TYPE_DETAIL : GET_LAST_PLANNING_ASSIGN_TYPE_DETAIL_FOR_DSP;
    const lastAssignKey = mode === 'wms' ? 'getLastPlanningAssignTypeDetail' : 'getLastPlanningAssignTypeDetailForDsp';
    const taskDetailQuery = mode === 'wms' ? GET_DSP_TASK_DETAIL_WITH_CHILDREN : GET_DSP_TASK_DETAIL_WITH_CHILDREN_FOR_DSP;
    const taskDetailKey = mode === 'wms' ? 'getDspTaskDetailWithChildren' : 'getDspTaskDetailWithChildrenForDsp';
    const fastPlanQuery = mode === 'wms' ? FAST_ROUTE_PLANNING : FAST_ROUTE_PLANNING_FOR_DSP;
    const fastPlanKey = mode === 'wms' ? 'fastRoutePlanning' : 'fastRoutePlanningForDsp';

    /**
     * 该Area是否发布过
     */
    const isPublished = Number(record?.dspTaskId ?? 0) !== 0;

    console.log("是否发布过",isPublished)

    const { t, i18n } = useTranslation();

    // 可用拆分模式（由父组件从后端配置获取后传入）
    const availableModes = useMemo<SplitMode[]>(
        () => allowedModes && allowedModes.length > 0 ? allowedModes : DEFAULT_MODES,
        [allowedModes],
    );
    const defaultMode = availableModes[0];

    /** 如果目标模式不在可用列表中，回退到默认模式 */
    const clampMode = useCallback((mode: SplitMode): SplitMode =>
        availableModes.includes(mode) ? mode : defaultMode,
    [availableModes, defaultMode]);

    const [splitMode, setSplitMode] = useState<SplitMode>(defaultMode);
    const [mapSegments, setMapSegments] = useState<MapSegment[]>([]);
    const [zipcodeSegments, setZipcodeSegments] = useState<ZipcodeSegment[]>([]);
    const [stopSegments, setStopSegments] = useState<StopSegment[]>([]);
    const [algorithmSegments, setAlgorithmSegments] = useState<AlgorithmSegment[]>([]);

    // ==================== 算法分单引导 ====================
    // 后端返回：该区域是否「连续3天未使用算法分单」（null=未查询/不满足前置条件）
    const [withoutRecentAlgoSplit, setWithoutRecentAlgoSplit] = useState<boolean | null>(null);
    const [algoGuideVisible, setAlgoGuideVisible] = useState(false);
    // 本次抽屉打开期间是否已弹过引导，避免多次切 tab 反复弹
    const algoGuideShownRef = useRef(false);

    const [fetchIsAreaWithoutRecentAlgoSplit] = useLazyQuery<
        IsAreaWithoutRecentAlgoSplitResponse,
        IsAreaWithoutRecentAlgoSplitVariables
    >(IS_AREA_WITHOUT_RECENT_ALGO_SPLIT, { fetchPolicy: 'network-only' });

    // 抽屉打开时预取「连续3天未使用算法分单」标记：仅当日未发布(dspTaskId=0)、待分配件量>500、且该仓开启算法分单时才查
    useEffect(() => {
        if (!visible) {
            algoGuideShownRef.current = false;
            setAlgoGuideVisible(false);
            setWithoutRecentAlgoSplit(null);
            return;
        }
        const notPublished = Number(record?.dspTaskId ?? 0) === 0;
        const overThreshold = (record?.totalParcels ?? 0) > 500;
        if (!notPublished || !overThreshold || !warehouseId || !record?.areaId || !availableModes.includes('algo')) {
            setWithoutRecentAlgoSplit(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await fetchIsAreaWithoutRecentAlgoSplit({
                    variables: { warehouseId, areaId: Number(record.areaId), taskDate: warehouseToday },
                });
                if (!cancelled) setWithoutRecentAlgoSplit(data?.isAreaWithoutRecentAlgoSplit ?? null);
            } catch {
                if (!cancelled) setWithoutRecentAlgoSplit(null);
            }
        })();
        return () => { cancelled = true; };
    }, [visible, record, warehouseId, warehouseToday, availableModes, fetchIsAreaWithoutRecentAlgoSplit]);

    // 切到「非算法模式」时，若满足全部条件且本次未弹过，弹出算法分单引导
    const maybeShowAlgoGuide = useCallback((targetMode: SplitMode) => {
        if (targetMode === 'algo') return;
        if (algoGuideShownRef.current) return;
        if (withoutRecentAlgoSplit !== true) return;
        if (Number(record?.dspTaskId ?? 0) !== 0) return;
        if ((record?.totalParcels ?? 0) <= 500) return;
        if (!availableModes.includes('algo')) return;
        // 弹过一次后写入一个 12 小时过期的缓存；缓存仍有效则不再弹（过期后视为无缓存，可再弹）
        const ALGO_GUIDE_KEY = 'algoGuideDismissedUntil';
        const dismissedUntil = Number(localStorage.getItem(ALGO_GUIDE_KEY) || 0);
        if (dismissedUntil && Date.now() < dismissedUntil) return;
        localStorage.setItem(ALGO_GUIDE_KEY, String(Date.now() + 12 * 60 * 60 * 1000));
        algoGuideShownRef.current = true;
        setAlgoGuideVisible(true);
    }, [withoutRecentAlgoSplit, record, availableModes]);

    // ==================== 视频教程 ====================
    // 仅算法分单 / 地图分单两种模式展示视频教程入口
    const showVideoTutorialEntry = splitMode === 'algo' || splitMode === 'map';
    // 点击教程入口：新标签打开自有播放页（Plyr 播放器，无下载入口）；页面内再换取短时签名 URL
    // forceType 指定视频类型（如引导弹窗「查看使用说明」固定打开算法视频）；不传则按当前拆分模式
    // lang 带上当前界面语言，播放页按语言取对应版本视频
    const handleOpenVideoTutorial = useCallback((forceType?: 'ALGO' | 'MAP') => {
        const videoType = forceType ?? (splitMode === 'algo' ? 'ALGO' : 'MAP');
        const lang = (i18n.resolvedLanguage || i18n.language || '').toLowerCase().split('-')[0];
        const langQuery = lang ? `&lang=${encodeURIComponent(lang)}` : '';
        window.open(`/tutorial-video?type=${videoType}${langQuery}`, '_blank');
    }, [splitMode, i18n]);

    // 算法分单状态
    type AlgoPhase = 'editing' | 'clustering' | 'clustered' | 'reEdit' | 'routePlanning' | 'routePlanned' | 'published';
    const [algoPhase, setAlgoPhase] = useState<AlgoPhase>('editing');
    const [algoClusteringTaskId, setAlgoClusteringTaskId] = useState<number | null>(null);
    const [algoStopMap, setAlgoStopMap] = useState<Map<number, number>>(new Map());
    const algoPollingCleanupRef = useRef<(() => void) | null>(null);
    const algoSnapshotRef = useRef<{segments: AlgorithmSegment[]; phase: AlgoPhase; stopMap: Map<number, number>} | null>(null);
    const algoClusteringCancelledRef = useRef(false);
    const algorithmSegmentsRef = useRef(algorithmSegments);
    algorithmSegmentsRef.current = algorithmSegments;

    // 算法分单：便捷判断
    const isAlgoEditing = algoPhase === 'editing' || algoPhase === 'reEdit';
    const isAlgoViewMode = algoPhase === 'clustered' || algoPhase === 'routePlanned' || algoPhase === 'published';

    // 矩形框选改派
    const [isRectSelecting, setIsRectSelecting] = useState(false);
    const [rectSelectedOrderIds, setRectSelectedOrderIds] = useState<number[]>([]);
    const rectOverlayRef = useRef<google.maps.Rectangle | null>(null);

    const [isDrawing, setIsDrawing] = useState(false);
    const [polygonPaths, setPolygonPaths] = useState<google.maps.LatLngLiteral[]>([]);
    const [mousePos, setMousePos] = useState<google.maps.LatLngLiteral | null>(null);

    const [editingSegId, setEditingSegId] = useState<string | null>(null);
    const [isPrePlanning, setIsPrePlanning] = useState(false);
    const hasFetchedQuickPlan = useRef(false);
    const hasPrefilledFromLastRef = useRef(false);

    // 地图标点状态
    const [pinningTarget, setPinningTarget] = useState<PinningTarget | null>(null);

    // 输入地址弹窗
    const [addressModalTarget, setAddressModalTarget] = useState<PinningTarget | null>(null);
    const handleAddressConfirm = (result: AddressResult) => {
        if (!addressModalTarget) return;
        const {segId, field, segType} = addressModalTarget;
        const isStart = field === 'start';
        const addr: DriverAddressInput = {
            streetAddress: result.streetAddress ?? null,
            unit: result.unit ?? null,
            city: result.city ?? null,
            state: result.state ?? null,
            zipCode: result.zipCode ?? null,
            county: result.county ?? null,
            latitude: result.latitude ?? null,
            longitude: result.longitude ?? null,
        };
        const patch = isStart ? {startAddress: addr} : {endAddress: addr};
        if (segType === 'map') updateMapSegment(segId, patch);
        else if (segType === 'zipcode') updateZipcodeSegment(segId, patch);
        else if (segType === 'stop') updateStopSegment(segId, patch);
        else updateAlgorithmSegment(segId, patch);
        setAddressModalTarget(null);
    };

    // 地图图层开关
    const [showZipcodeBoundary, setShowZipcodeBoundary] = useState(true);
    const [showNavTrack, setShowNavTrack] = useState(false);
    const [showParcelLocation, setShowParcelLocation] = useState(true);
    const [showStartPoint, setShowStartPoint] = useState(true);
    const [showEndPoint, setShowEndPoint] = useState(true);
    const [showPublishedParcels, setShowPublishedParcels] = useState(true);
    const [hideMapLabels, setHideMapLabels] = useState(true);

    // Zipcode 围栏叠加层
    const [hoveredZipcodeBoundary, setHoveredZipcodeBoundary] = useState<{
        zipcode: string; parcelCount: number; x: number; y: number;
    } | null>(null);

    // 选中 zipcode 高亮 3 秒
    const [highlightedZipcodes, setHighlightedZipcodes] = useState<Set<string>>(new Set());
    const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const flashZipcodes = useCallback((zipcodes: string[]) => {
        if (zipcodes.length === 0) return;
        setHighlightedZipcodes(new Set(zipcodes));
        if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = setTimeout(() => setHighlightedZipcodes(new Set()), 3000);
    }, []);

    // 命令式管理已完成分段的多边形实例
    const segPolygonRefs = useRef<Map<string, google.maps.Polygon[]>>(new Map());

    // 发布流程弹窗状态
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [publishModalVisible, setPublishModalVisible] = useState(false);
    // 本次发布在确认弹窗中选定的派送日期，确认前为 null
    const [publishTaskDate, setPublishTaskDate] = useState<Dayjs | null>(null);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [dataStaleVisible, setDataStaleVisible] = useState(false);
    const [dataStaleResult, setDataStaleResult] = useState<DataStaleCheckResult | null>(null);
    const [republishConflictVisible, setRepublishConflictVisible] = useState(false);
    const [republishConflictOrders, setRepublishConflictOrders] = useState<ScannedOrderInfo[]>([]);

    const [orderList, setOrderList] = useState<NonNullable<GetRouteOrdersResponse['getRouteOrders']>['orderList']>([]);

    /**
     * 默认分段
     */
    const resetToDefaultSegments = useCallback((areaName: string) => {
        setSplitMode(defaultMode);
        setMapSegments([]);
        setZipcodeSegments([{
            id: `seg-${Date.now()}`,
            name: `${areaName}-1`,
            colorIndex: 0,
            driverAccountId: '',
            zipcodes: [],
            assignedOrderIds: [],
            totalParcelCount: 0,
            startAddress: null,
            endAddress: null,
        }]);
        setStopSegments([{
            id: `seg-${Date.now() + 1}`,
            name: `${areaName}-1`,
            colorIndex: 0,
            driverAccountId: '',
            stopRanges: [{id: `range-${Date.now()}`, parcelNumberStart: '', parcelNumberEnd: ''}],
            assignedOrderIds: [],
            totalParcelCount: 0,
            startAddress: null,
            endAddress: null,
        }]);
        setAlgorithmSegments([{
            id: `seg-${Date.now() + 2}`,
            name: `${areaName}-1`,
            colorIndex: 0,
            driverAccountId: '',
            capacityMin: null,
            capacityMax: null,
            preferredZipcodes: [],
            startAddress: null,
            endAddress: null,
            assignedOrderIds: [],
            totalParcelCount: 0,
        }]);
    }, [defaultMode]);

    /**
     * 当 allowedModes 从父组件异步到达后，splitMode 可能还是旧的兜底值。
     * 如果当前 splitMode 不在可用列表中，自动纠正为 defaultMode。
     */
    useEffect(() => {
        if (!availableModes.includes(splitMode)) {
            setSplitMode(defaultMode);
        }
    }, [availableModes, defaultMode, splitMode]);

    // 切换到 zipcode 模式时自动开启围栏；切换到其他模式不强制关闭（用户可手动控制）
    const prevSplitModeRef = useRef<string>(splitMode);
    useEffect(() => {
        if (!visible) {
            prevSplitModeRef.current = splitMode;
            return;
        }
        if (splitMode === 'zipcode' && prevSplitModeRef.current !== 'zipcode') {
            setShowZipcodeBoundary(true);
        }
        prevSplitModeRef.current = splitMode;
    }, [visible, splitMode]);

    /**
     * 取消画图
     */
    const cancelDrawing = useCallback(() => {
        setIsDrawing(false);
        setPolygonPaths([]);
        setMousePos(null);
        setEditingSegId(null);
    }, []);

    /**
     * 返回确认：点返回按钮 或 按 ESC 时触发
     */
    const confirmAndClose = useCallback(() => {
        Modal.confirm({
            title: t('splitOrder.drawer.confirmExit'),
            content: t('splitOrder.drawer.exitWarning'),
            okText: t('splitOrder.drawer.confirmExit'),
            cancelText: t('splitOrder.drawer.continueEdit'),
            okButtonProps: {danger: true},
            onOk: onClose,
        });
    }, [t, onClose]);

    /**
     * 抽屉打开时监听 ESC，按下弹确认关闭
     */
    useEffect(() => {
        if (!visible) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                confirmAndClose();
            }
        };
        // capture 阶段拦截，避免 antd Drawer 默认的 ESC 关闭抢先触发
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [visible, confirmAndClose]);

    /**
     * 一进页面，分段赋值逻辑
     */
    useEffect(() => {
        // 每次 visible 变化时，先清理上一轮算法分单轮询
        algoPollingCleanupRef.current?.();
        algoPollingCleanupRef.current = null;

        if (!visible) {
            cancelDrawing();
            cancelRectSelect();
            setHoveredSegId(null);
            hasPrefilledFromLastRef.current = false;
            setHoveredZipcodeBoundary(null);
            return;
        }
        setHoveredSegId(null);
        setShowPublishedParcels(true);
        cancelRectSelect();
        // 清除残留多边形
        segPolygonRefs.current.forEach(polys => polys.forEach(p => p.setMap(null)));
        segPolygonRefs.current.clear();

        // 重置算法分单 state（防止跨区域继承）
        setAlgoPhase('editing');
        setAlgoStopMap(new Map());
        setAlgoClusteringTaskId(null);
        algoSnapshotRef.current = null;
        algoClusteringCancelledRef.current = false;

        // 缓存加载（异步）
        (async () => {
            // 缓存优先级最高（包括发布后修改再保存的情况）
            const parsed = (warehouseId && record?.areaId)
                ? await loadSplitRouteCache(warehouseId, Number(record.areaId))
                : null;
            if (parsed) {
                // 缓存的模式已被关闭，丢弃整份缓存
                if (parsed.lastSplitMode && !availableModes.includes(parsed.lastSplitMode)) {
                    if (warehouseId && record?.areaId) await deleteSplitRouteCache(warehouseId, Number(record.areaId));
                } else {
                setMapSegments(parsed.map ?? []);
                setZipcodeSegments(parsed.zipcode ?? []);
                setStopSegments(parsed.stop ?? []);
                // algo 缓存恢复
                if (parsed.algo) {
                    const algo = parsed.algo;
                    // 缓存中 assignedOrderIds 可能因旧 bug 变为 string[]，统一转 number
                    setAlgorithmSegments((algo.segments ?? []).map((seg: any) => ({
                        ...seg,
                        assignedOrderIds: (seg.assignedOrderIds ?? []).map(Number),
                    })));
                    setAlgoClusteringTaskId(algo.clusteringTaskId ?? null);
                    if (algo.stopMap) {
                        setAlgoStopMap(new Map(Object.entries(algo.stopMap).map(([k, v]) => [Number(k), v as number])));
                    } else {
                        setAlgoStopMap(new Map());
                    }
                    if (algo.phase === 'clustering') {
                        // 分堆中退出了 → 直接重新请求（跳过校验和确认弹窗）
                        doStartClustering();
                    } else if (algo.phase === 'routePlanning' && algo.routePlanningQueries?.length) {
                        // 路径规划轮询中退出了 → 直接恢复轮询
                        startRoutePlanningPoll(algo.routePlanningQueries);
                    } else if (algo.phase === 'routePlanning') {
                        // 没有 queries 降级
                        setAlgoPhase('clustered');
                    } else {
                        setAlgoPhase(algo.phase ?? 'editing');
                    }
                } else {
                    setAlgoPhase('editing');
                    setAlgoStopMap(new Map());
                    setAlgoClusteringTaskId(null);
                    const areaName = record?.areaName ?? 'Area';
                    setAlgorithmSegments([{
                        id: `seg-${Date.now()}`,
                        name: `${areaName}-1`,
                        colorIndex: 0,
                        driverAccountId: '',
                        capacityMin: null,
                        capacityMax: null,
                        preferredZipcodes: [],
                        startAddress: null,
                        endAddress: null,
                        assignedOrderIds: [],
                        totalParcelCount: 0,
                    }]);
                }
                setSplitMode(parsed.lastSplitMode ?? defaultMode);
                return;
                }
            }

            // 无缓存且已发布：清空，等 GET_DSP_TASK_DETAIL_WITH_CHILDREN 接口回来后填充
            if (isPublished) {
                setMapSegments([]);
                setZipcodeSegments([]);
                setStopSegments([]);
                const areaName = record?.areaName ?? 'Area';
                setAlgorithmSegments([{
                    id: `seg-${Date.now()}`,
                    name: `${areaName}-1`,
                    colorIndex: 0,
                    driverAccountId: '',
                    capacityMin: null,
                    capacityMax: null,
                    preferredZipcodes: [],
                    startAddress: null,
                    endAddress: null,
                    assignedOrderIds: [],
                    totalParcelCount: 0,
                }]);
                return;
            }

            // 无缓存且未发布：map 空，zipcode/stop 默认一个分段
            resetToDefaultSegments(record?.areaName ?? 'Area');
        })();
    }, [visible]);

    /**
     * 根据多边形从 orderList 匹配 assignedOrderIds
     * @param polygons
     * @param excludeIds
     */
    const matchOrderIdsByPolygons = (polygons: google.maps.LatLngLiteral[][], excludeIds?: Set<number>) =>
        orderList.filter(o => {
            if (o.orderId == null || o.latitude == null || o.longitude == null) return false;
            if (excludeIds?.has(o.orderId)) return false;
            return polygons.some(poly => poly.length >= 3 && isPointInPolygon({lat: o.latitude!, lng: o.longitude!}, poly));
        }).map(o => o.orderId!);

    /**
     * 根据邮编列表从 orderList 匹配 assignedOrderIds
     * @param zipcodes
     */
    const matchOrderIdsByZipcodes = (zipcodes: string[]) =>
        orderList.filter(o => {
            if (o.orderId == null || o.zipcode == null) return false;
            return zipcodes.includes(o.zipcode);
        }).map(o => o.orderId!);


    /**
     * 根据 stopRanges 从 orderList 匹配 assignedOrderIds
     * @param ranges
     */
    const matchOrderIdsByStopRanges = (ranges: StopRange[]) =>
        orderList.filter(o => {
            if (o.orderId == null || o.stopNumber == null) return false;
            return ranges.some(r => {
                const start = Number(r.parcelNumberStart), end = Number(r.parcelNumberEnd);
                return r.parcelNumberStart && r.parcelNumberEnd && o.stopNumber! >= start && o.stopNumber! <= end;
            });
        }).map(o => o.orderId!);

    /**
     * Zipcode 围栏
     */
    // 计算每个 zipcode 对应的包裹数
    const zipcodeParcelCountMap = useMemo((): Map<string, number> => {
        const m = new Map<string, number>();
        orderList.forEach(o => {
            if (o.zipcode) {
                m.set(o.zipcode, (m.get(o.zipcode) ?? 0) + 1);
            }
        });
        return m;
    }, [orderList]);

    // 从 orderList 提取当前 area 的唯一 zipcode 列表
    const areaZipcodes = useMemo(() =>
        [...new Set(orderList.map(o => o.zipcode).filter((z): z is string => !!z))].sort(),
    [orderList]);

    const { data: zipcodeBoundariesData } = useQuery<GetZipcodeMapBoundariesResponse>(
        GET_ZIPCODE_MAP_BOUNDARIES,
        {
            variables: { zipcodes: areaZipcodes },
            skip: !visible || areaZipcodes.length === 0,
            fetchPolicy: 'cache-and-network',
        }
    );

    // 解析边界数据为 Polygon paths
    const zipcodeBoundaryData = useMemo((): ZipcodeBoundaryEntry[] => {
        const items = zipcodeBoundariesData?.getZipcodeMapBoundaries;
        if (!items?.length) return [];
        const entries: ZipcodeBoundaryEntry[] = [];
        for (const item of items) {
            try {
                const coords = JSON.parse(item.boundary);
                const rawPath = Array.isArray(coords?.[0]) ? coords[0] : coords;
                const path = (rawPath as any[])
                    .filter((p: any) => typeof p.lat === 'number' && typeof p.lng === 'number')
                    .map((p: any) => ({ lat: p.lat, lng: p.lng }));
                if (path.length === 0) continue;
                entries.push({
                    zipcode: item.zipcode,
                    paths: path,
                    parcelCount: zipcodeParcelCountMap.get(item.zipcode) ?? 0,
                });
            } catch (_e) {
                // 解析失败静默跳过
            }
        }
        return entries;
    }, [zipcodeBoundariesData, zipcodeParcelCountMap]);

    const segments = splitMode === 'map' ? mapSegments : splitMode === 'zipcode' ? zipcodeSegments : splitMode === 'stop' ? stopSegments : algorithmSegments;

    const addSegment = () => {
        const id = `seg-${Date.now()}`;
        const areaName = record?.areaName ?? 'Area';
        if (splitMode === 'map') {
            setIsDrawing(true);
            setPolygonPaths([]);
    
            return;
        } else if (splitMode === 'zipcode') {
            setZipcodeSegments(prev => {
                const used = new Set(prev.map(s => s.colorIndex));
                const colorIdx = Array.from({length: SEGMENT_COLOR_PALETTE.length}, (_, i) => i)
                        .find(i => !used.has(i))
                    ?? prev.length % SEGMENT_COLOR_PALETTE.length;
                return [...prev, {
                    id,
                    name: `${areaName}-${prev.length + 1}`,
                    colorIndex: colorIdx,
                    driverAccountId: '',
                    zipcodes: [],
                    assignedOrderIds: [],
                    totalParcelCount: 0,
                    startAddress: null,
                    endAddress: null,
                }];
            });
        } else if (splitMode === 'stop') {
            setStopSegments(prev => {
                const used = new Set(prev.map(s => s.colorIndex));
                const colorIdx = Array.from({length: SEGMENT_COLOR_PALETTE.length}, (_, i) => i)
                        .find(i => !used.has(i))
                    ?? prev.length % SEGMENT_COLOR_PALETTE.length;
                return [...prev, {
                    id,
                    name: `${areaName}-${prev.length + 1}`,
                    colorIndex: colorIdx,
                    driverAccountId: '',
                    stopRanges: [{id: `range-${Date.now()}`, parcelNumberStart: '', parcelNumberEnd: ''}],
                    assignedOrderIds: [],
                    totalParcelCount: 0,
                    startAddress: null,
                    endAddress: null,
                }];
            });
        } else if (splitMode === 'algo') {
            setAlgorithmSegments(prev => {
                const used = new Set(prev.map(s => s.colorIndex));
                const colorIdx = Array.from({length: SEGMENT_COLOR_PALETTE.length}, (_, i) => i)
                        .find(i => !used.has(i))
                    ?? prev.length % SEGMENT_COLOR_PALETTE.length;
                return [...prev, {
                    id,
                    name: `${areaName}-${prev.length + 1}`,
                    colorIndex: colorIdx,
                    driverAccountId: '',
                    capacityMin: null,
                    capacityMax: null,
                    preferredZipcodes: [],
                    startAddress: null,
                    endAddress: null,
                    assignedOrderIds: [],
                    totalParcelCount: 0,
                }];
            });
        }
    };

    const removeSegment = (id: string) => {
        const areaName = record?.areaName ?? 'Area';
        const reindex = <T extends {name: string; colorIndex: number}>(list: T[]): T[] =>
            list.filter(s => (s as any).id !== id).map((s, i) => ({...s, name: `${areaName}-${i + 1}`, colorIndex: i % SEGMENT_COLOR_PALETTE.length}));
        if (splitMode === 'map') {
            setMapSegments(prev => reindex(prev));
        } else if (splitMode === 'zipcode') {
            setZipcodeSegments(prev => reindex(prev));
        } else if (splitMode === 'stop') {
            setStopSegments(prev => reindex(prev));
        } else {
            setAlgorithmSegments(prev => reindex(prev));
        }
    };

    const updateMapSegment = (id: string, patch: Partial<Omit<MapSegment, 'id'>>) => {
        setMapSegments(prev => {
            const otherIds = new Set(prev.filter(s => s.id !== id).flatMap(s => s.assignedOrderIds));
            return prev.map(s => {
                if (s.id !== id) return s;
                const merged = {...s, ...patch};
                if ('polygons' in patch) {
                    merged.assignedOrderIds = matchOrderIdsByPolygons(merged.polygons, otherIds);
                }
                merged.totalParcelCount = merged.assignedOrderIds.length;
                return merged;
            });
        });
    };

    const updateZipcodeSegment = (id: string, patch: Partial<Omit<ZipcodeSegment, 'id'>>) => {
        setZipcodeSegments(prev => {
            const otherIds = new Set(prev.filter(s => s.id !== id).flatMap(s => s.assignedOrderIds));
            return prev.map(s => {
                if (s.id !== id) return s;
                const merged = {...s, ...patch};
                if ('zipcodes' in patch) {
                    merged.assignedOrderIds = matchOrderIdsByZipcodes(merged.zipcodes).filter(id => !otherIds.has(id));
                }
                merged.totalParcelCount = merged.assignedOrderIds.length;
                return merged;
            });
        });
    };

    const updateStopSegment = (id: string, patch: Partial<Omit<StopSegment, 'id'>>) => {
        setStopSegments(prev => prev.map(s => {
            if (s.id !== id) return s;
            const merged = {...s, ...patch};
            if ('stopRanges' in patch) {
                merged.assignedOrderIds = matchOrderIdsByStopRanges(merged.stopRanges);
            }
            merged.totalParcelCount = merged.assignedOrderIds.length;
            return merged;
        }));
    };

    const updateAlgorithmSegment = (id: string, patch: Partial<Omit<AlgorithmSegment, 'id'>>) => {
        setAlgorithmSegments(prev => prev.map(s => {
            if (s.id !== id) return s;
            return {...s, ...patch};
        }));
    };

    const addStopRange = (segId: string) => {
        setStopSegments(prev => prev.map(s => s.id === segId ? {
            ...s,
            stopRanges: [...s.stopRanges, {id: `range-${Date.now()}`, parcelNumberStart: '', parcelNumberEnd: ''}],
        } : s));
    };

    const updateStopRange = (segId: string, rangeId: string, patch: Partial<Omit<StopRange, 'id'>>) => {
        const totalParcels = realTotalParcels;
        setStopSegments(prev => prev.map(s => {
            if (s.id !== segId) return s;
            const newRanges = s.stopRanges.map(r => {
                if (r.id !== rangeId) return r;
                let merged = {...r, ...patch};

                // 超过总包裹数
                if ('parcelNumberStart' in patch && merged.parcelNumberStart && Number(merged.parcelNumberStart) > totalParcels) {
                    notification.warning({message: t('splitOrder.drawer.startExceedTotal', { total: totalParcels }), duration: 2});
                    return {...merged, parcelNumberStart: ''};
                }
                if ('parcelNumberEnd' in patch && merged.parcelNumberEnd && Number(merged.parcelNumberEnd) > totalParcels) {
                    notification.warning({message: t('splitOrder.drawer.endExceedTotal', { total: totalParcels }), duration: 2});
                    return {...merged, parcelNumberEnd: ''};
                }

                // 开始 > 结束（只在编辑开始序号时检查，避免输入结束序号过程中被误清空）
                if ('parcelNumberStart' in patch && merged.parcelNumberStart && merged.parcelNumberEnd && Number(merged.parcelNumberStart) > Number(merged.parcelNumberEnd)) {
                    notification.warning({message: t('splitOrder.drawer.startMustLessEnd'), duration: 2});
                    return {...merged, parcelNumberStart: ''};
                }

                // 与其他分段冲突
                if (merged.parcelNumberStart && merged.parcelNumberEnd) {
                    const ms = Number(merged.parcelNumberStart), me = Number(merged.parcelNumberEnd);
                    const hasConflict = prev.some(seg =>
                        seg.stopRanges.some(r2 => {
                            if (r2.id === rangeId || !r2.parcelNumberStart || !r2.parcelNumberEnd) return false;
                            const rs = Number(r2.parcelNumberStart), re = Number(r2.parcelNumberEnd);
                            return ms <= re && me >= rs;
                        })
                    );
                    if (hasConflict) {
                        notification.warning({message: t('splitOrder.drawer.rangeConflict'), duration: 2});
                        return 'parcelNumberStart' in patch ? {...merged, parcelNumberStart: ''} : {
                            ...merged,
                            parcelNumberEnd: ''
                        };
                    }
                }

                return merged;
            });
            const ids = matchOrderIdsByStopRanges(newRanges);
            return {
                ...s,
                stopRanges: newRanges,
                assignedOrderIds: ids,
                totalParcelCount: ids.length,
            };
        }));
    };

    const removeStopRange = (segId: string, rangeId: string) => {
        setStopSegments(prev => prev.map(s => {
            if (s.id !== segId) return s;
            const newRanges = s.stopRanges.filter(r => r.id !== rangeId);
            const ids = matchOrderIdsByStopRanges(newRanges);
            return {
                ...s,
                stopRanges: newRanges,
                assignedOrderIds: ids,
                totalParcelCount: ids.length,
            };
        }));
    };

    /**
     * 司机起始点自动分配逻辑
     */
    const [fetchDriverStartEndAddress] = useLazyQuery<GetDriverStartEndAddressResponse, GetDriverStartEndAddressVariables>(
        GET_DRIVER_START_END_ADDRESS,
        {fetchPolicy: 'network-only'}
    );

    const toDriverAddressInput = (addr?: DriverAddressOutput | null): DriverAddressInput | null => {
        if (!addr) return null;
        const {__typename, ...rest} = addr as any;
        return rest as DriverAddressInput;
    };

    // 司机选中后从后端拉取起/终点地址回填当前 segment（覆盖已有值，后端返回 null 时清空）
    const fetchAndFillDriverStartEnd = async (
        driverAccountId: string | undefined,
        segType: 'map' | 'zipcode' | 'stop' | 'algo',
        segId: string,
    ) => {
        if (!driverAccountId || !warehouseId) return;
        const driverId = Number(driverAccountId);
        if (!Number.isFinite(driverId) || driverId <= 0) return;
        try {
            const {data} = await fetchDriverStartEndAddress({
                variables: {request: {driverId, warehouseId}},
            });
            const result = data?.getDriverStartEndAddress;
            const patch = {
                startAddress: toDriverAddressInput(result?.startAddress),
                endAddress: toDriverAddressInput(result?.endAddress),
            };
            if (segType === 'map') {
                updateMapSegment(segId, patch as Partial<Omit<MapSegment, 'id'>>);
            } else if (segType === 'zipcode') {
                updateZipcodeSegment(segId, patch as Partial<Omit<ZipcodeSegment, 'id'>>);
            } else if (segType === 'stop') {
                updateStopSegment(segId, patch as Partial<Omit<StopSegment, 'id'>>);
            } else {
                updateAlgorithmSegment(segId, patch as Partial<Omit<AlgorithmSegment, 'id'>>);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[SplitRouteDrawer] getDriverStartEndAddress failed', err);
        }
    };

    const {data: routeOrdersRawData, loading: routeOrdersLoading, refetch: refetchRouteOrders} = useQuery(
        routeOrdersQuery,
        {
            variables: {
                request: {
                    warehouseId: warehouseId!,
                    areaId: record?.areaId!,
                },
            },
            skip: !visible || !warehouseId || !record?.areaId,
        }
    );
    const routeOrdersData = useMemo(() => ({
        getRouteOrders: (routeOrdersRawData as any)?.[routeOrdersKey] ?? null
    }), [routeOrdersRawData, routeOrdersKey]);

    useEffect(() => {
        const list = routeOrdersData?.getRouteOrders?.orderList;
        if (list) {
            setOrderList(list);
        }
    }, [routeOrdersData]);


    /**
     * 未发布区域：拉取上次的拆分明细（围栏/邮编），用于回显并匹配新包裹
     */
    const { data: lastAssignRawData } = useQuery(lastAssignQuery, {
        variables: {
            request: {
                warehouseId: warehouseId!,
                areaId: record?.areaId!,
            },
        },
        skip: !visible || !warehouseId || !record?.areaId || isPublished,
        fetchPolicy: 'network-only',
    });
    const lastAssignData = useMemo(() => ({
        getLastPlanningAssignTypeDetail: (lastAssignRawData as any)?.[lastAssignKey] as LastPlanningAssignTypeDetailOutput | null ?? null
    }), [lastAssignRawData, lastAssignKey]);

    /**
     * 未发布区域：根据上次拆分明细（fenceList/zipcodeList）回显分段，并匹配当前包裹
     * - 每次打开抽屉只回显一次（hasPrefilledFromLastRef 控制）
     * - 关闭抽屉时 ref 重置
     * - 当前仓库未开启对应模式的，跳过该模式的回显（无业务意义）
     * - 有 localStorage 缓存时不回显（尊重用户已有编辑）
     */
    useEffect(() => {
        if (!visible || isPublished) return;
        if (hasPrefilledFromLastRef.current) return;
        const detail = lastAssignData?.getLastPlanningAssignTypeDetail;
        if (!detail) return;
        if (!orderList.length) return;

        // 有缓存时不回显（尊重用户已有编辑）—— 异步检查
        const checkCache = async () => {
            if (warehouseId && record?.areaId) {
                const cached = await loadSplitRouteCache(warehouseId, Number(record.areaId));
                if (cached) return true;
            }
            return false;
        };
        checkCache().then(hasCached => {
            if (hasCached) return;

        const { fenceList, zipcodeList } = detail;
        const areaName = record?.areaName ?? 'Area';

        // 1) 地图模式回显：当前仓库开启 map 且 fenceList 非空
        if (availableModes.includes('map') && fenceList?.length) {
            const assigned = new Set<number>();
            const segs: MapSegment[] = fenceList.map((fenceGroup, idx) => {
                const polygons: google.maps.LatLngLiteral[][] = (fenceGroup ?? []).map(str =>
                    str.split(';').map(p => {
                        const [lat, lng] = p.split(',').map(Number);
                        return { lat, lng };
                    })
                );
                const ids = matchOrderIdsByPolygons(polygons, assigned);
                ids.forEach(id => assigned.add(id));
                return {
                    id: `seg-hist-map-${record?.areaId}-${idx}`,
                    name: `${areaName}-${idx + 1}`,
                    colorIndex: idx % SEGMENT_COLOR_PALETTE.length,
                    driverAccountId: '',
                    startAddress: null,
                    endAddress: null,
                    polygons,
                    assignedOrderIds: ids,
                    totalParcelCount: ids.length,
                };
            });
            setMapSegments(segs);
        }

        // 2) 邮编模式回显：当前仓库开启 zipcode 且 zipcodeList 非空
        if (availableModes.includes('zipcode') && zipcodeList?.length) {
            const segs: ZipcodeSegment[] = zipcodeList.map((zipcodes, idx) => {
                const safeZips = zipcodes ?? [];
                const ids = matchOrderIdsByZipcodes(safeZips);
                return {
                    id: `seg-hist-zip-${record?.areaId}-${idx}`,
                    name: `${areaName}-${idx + 1}`,
                    colorIndex: idx % SEGMENT_COLOR_PALETTE.length,
                    driverAccountId: '',
                    startAddress: null,
                    endAddress: null,
                    zipcodes: safeZips,
                    assignedOrderIds: ids,
                    totalParcelCount: ids.length,
                };
            });
            setZipcodeSegments(segs);
        }

            hasPrefilledFromLastRef.current = true;
        });
    }, [visible, isPublished, lastAssignData, orderList, warehouseId, record, availableModes]);

    const prePlanningCompleted = routeOrdersData?.getRouteOrders?.prePlanningCompleted ?? false;

    // 实时包裹总数：以 orderList 为准，兜底用 record.totalParcels
    const realTotalParcels = orderList.length || (record?.totalParcels ?? 0);

    const {data: taskDetailRawData, loading: taskDetailLoading} = useQuery(taskDetailQuery, {
        variables: {
            request: {
                warehouseId: warehouseId!,
                dspTaskId: Number(record?.dspTaskId),
                dspId: record?.dspId ?? undefined,
            },
        },
        skip: !visible || !warehouseId || !isPublished,
        fetchPolicy: 'network-only',
    });
    const taskDetail: DspTaskDetailWithChildrenOutput | null = (taskDetailRawData as any)?.[taskDetailKey] ?? null;

    console.log("taskDetail",taskDetail)

    // 数据加载中：orderList 或已发布的 taskDetail 还没回来
    const dataLoading = routeOrdersLoading || (isPublished && taskDetailLoading);
    /**
     * 符合发布条件的orderId集合（包含已发布未领取 + 新增未发布）
     */
    const activeOrderIdSet = useMemo(
        () => new Set(orderList.map(o => o.orderId).filter((id): id is number => id != null)),
        [orderList]
    );

    /**
     * 已发布的orderId集合
     */
    const publishedOrderIdSet = useMemo(() => {
        if (!isPublished) return new Set<number>();
        const set = new Set<number>();
        (taskDetail?.routePlanningOrderList ?? []).forEach(o => {
            if (o.orderId != null) set.add(Number(o.orderId));
        });
        (taskDetail?.childTaskList ?? []).forEach(child => {
            (child.routePlanningOrderList ?? []).forEach(o => {
                if (o.orderId != null) set.add(Number(o.orderId));
            });
        });
        return set;
    }, [isPublished, taskDetail]);

    /**
     * 已发布且无缓存：接口数据回来后填充分段
     */
    useEffect(() => {
        if (!taskDetail || !isPublished) return;
        (async () => {
            const at = taskDetail.assignType ?? 0;

            // 有缓存说明用户修改过，已在 visible useEffect 里加载，不覆盖分段数据
            if (warehouseId && record?.areaId) {
                const cached = await loadSplitRouteCache(warehouseId, Number(record.areaId));
                if (cached) return;
            }
            const areaName = record?.areaName ?? 'Area';

        // 已发布模式被关闭：丢弃历史数据，用可用模式从头拆分
        const publishedMode: SplitMode | undefined =
            at === AssignType.BY_MAP ? 'map'
            : at === AssignType.BY_ZIPCODE ? 'zipcode'
            : at === AssignType.BY_STOP ? 'stop'
            : at === AssignType.BY_ALGORITHM ? 'algo'
            : undefined;
        if (publishedMode && !availableModes.includes(publishedMode)) {
            resetToDefaultSegments(areaName);
            return;
        }

        // 过滤掉已全部完成（无可发布包裹）的分段
        const hasActive = (ids: number[]) => ids.some(id => activeOrderIdSet.has(id));

        // 单分段发布（split:false）数据在根级，需 fallback
        const childList = taskDetail.isSplit
            ? taskDetail.childTaskList!
            : [{
                id: taskDetail.id,
                taskName: taskDetail.taskName,
                driverId: taskDetail.driverId,
                assignTypeDetail: taskDetail.assignTypeDetail,
                routePlanningOrderList: taskDetail.routePlanningOrderList,
                parcelNumberStart: taskDetail.parcelNumberStart,
                parcelNumberEnd: taskDetail.parcelNumberEnd,
                startAddress: taskDetail.startAddress,
                endAddress: taskDetail.endAddress,
            }];

        if (at === AssignType.BY_MAP) {
            setSplitMode(clampMode('map'));
            setZipcodeSegments([{
                id: `seg-${Date.now()}`,
                name: `${areaName}-1`,
                colorIndex: 0,
                driverAccountId: '',
                zipcodes: [],
                assignedOrderIds: [],
                totalParcelCount: 0,
                startAddress: null,
                endAddress: null,
            }]);
            setStopSegments([{
                id: `seg-${Date.now() + 1}`,
                name: `${areaName}-1`,
                colorIndex: 0,
                driverAccountId: '',
                stopRanges: [{id: `range-${Date.now()}`, parcelNumberStart: '', parcelNumberEnd: ''}],
                assignedOrderIds: [],
                totalParcelCount: 0,
                startAddress: null,
                endAddress: null,
            }]);
            const mapAssigned = new Set<number>();
            setMapSegments(childList.map((child, idx) => {
                const polygons = (child.assignTypeDetail ?? []).filter(Boolean).map(fenceStr =>
                    fenceStr.split(';').map(s => {
                        const [lat, lng] = s.split(',').map(Number);
                        return {lat, lng};
                    })
                );
                const assignedOrderIds = matchOrderIdsByPolygons(polygons, mapAssigned);
                assignedOrderIds.forEach(id => mapAssigned.add(id));
                return {
                    id: String(child.id ?? idx),
                    name: child.taskName ?? `${areaName}-${idx + 1}`,
                    colorIndex: idx % SEGMENT_COLOR_PALETTE.length,
                    driverAccountId: child.driverId ? String(child.driverId) : '',
                    polygons,
                    assignedOrderIds,
                    totalParcelCount: assignedOrderIds.length,
                    parcelNumberStart: child.parcelNumberStart ?? null,
                    parcelNumberEnd: child.parcelNumberEnd ?? null,
                    startAddress: child.startAddress ?? null,
                    endAddress: child.endAddress ?? null,
                };
            }).filter(seg => seg.assignedOrderIds.length > 0));
        } else if (at === AssignType.BY_ZIPCODE) {
            setSplitMode(clampMode('zipcode'));
            setMapSegments([]);
            setStopSegments([{
                id: `seg-${Date.now() + 1}`,
                name: `${areaName}-1`,
                colorIndex: 0,
                driverAccountId: '',
                stopRanges: [{id: `range-${Date.now()}`, parcelNumberStart: '', parcelNumberEnd: ''}],
                assignedOrderIds: [],
                totalParcelCount: 0,
                startAddress: null,
                endAddress: null,
            }]);
            const zipAssigned = new Set<number>();
            setZipcodeSegments(childList.map((child, idx) => {
                const zipcodes = child.assignTypeDetail ?? [];
                const assignedOrderIds = matchOrderIdsByZipcodes(zipcodes).filter(id => !zipAssigned.has(id));
                assignedOrderIds.forEach(id => zipAssigned.add(id));
                return {
                    id: String(child.id ?? idx),
                    name: child.taskName ?? `${areaName}-${idx + 1}`,
                    colorIndex: idx % SEGMENT_COLOR_PALETTE.length,
                    driverAccountId: child.driverId ? String(child.driverId) : '',
                    zipcodes,
                    assignedOrderIds,
                    totalParcelCount: assignedOrderIds.length,
                    parcelNumberStart: child.parcelNumberStart ?? null,
                    parcelNumberEnd: child.parcelNumberEnd ?? null,
                    startAddress: child.startAddress ?? null,
                    endAddress: child.endAddress ?? null,
                };
            }).filter(seg => seg.assignedOrderIds.length > 0));
        } else if (at === AssignType.BY_STOP) {
            setSplitMode(clampMode('stop'));
            setMapSegments([]);
            setZipcodeSegments([{
                id: `seg-${Date.now()}`,
                name: `${areaName}-1`,
                colorIndex: 0,
                driverAccountId: '',
                zipcodes: [],
                assignedOrderIds: [],
                totalParcelCount: 0,
                startAddress: null,
                endAddress: null,
            }]);
            setStopSegments(childList.map((child, idx) => {
                const stopRanges = (child.assignTypeDetail ?? []).length > 0
                    ? (child.assignTypeDetail ?? []).map((detail, rangeIdx) => {
                        const sep = detail.includes(',') ? ',' : '-';
                        const [start = '', end = ''] = detail.split(sep);
                        return { id: `range-${child.id ?? idx}-${rangeIdx}`, parcelNumberStart: start, parcelNumberEnd: end };
                    })
                    : [{
                        id: `range-${child.id ?? idx}`,
                        parcelNumberStart: child.parcelNumberStart != null ? String(child.parcelNumberStart) : '',
                        parcelNumberEnd: child.parcelNumberEnd != null ? String(child.parcelNumberEnd) : '',
                    }];
                const assignedOrderIds = matchOrderIdsByStopRanges(stopRanges);
                return {
                    id: String(child.id ?? idx),
                    name: child.taskName ?? `${areaName}-${idx + 1}`,
                    colorIndex: idx % SEGMENT_COLOR_PALETTE.length,
                    driverAccountId: child.driverId ? String(child.driverId) : '',
                    stopRanges,
                    assignedOrderIds,
                    totalParcelCount: assignedOrderIds.length,
                    startAddress: child.startAddress ?? null,
                    endAddress: child.endAddress ?? null,
                };
            }).filter(seg => hasActive(seg.assignedOrderIds)));
        } else if (at === AssignType.BY_ALGORITHM) {
            setSplitMode(clampMode('algo'));
            // 构建 algorithmSegments + algoStopMap
            const newStopMap = new Map<number, number>();
            const algoSegs: AlgorithmSegment[] = childList.map((child, idx) => {
                const orderIds = (child.routePlanningOrderList ?? [])
                    .map((o: any) => Number(o.orderId));
                // 构建 stopMap
                (child.routePlanningOrderList ?? []).forEach((o: any) => {
                    if (o.orderId != null && o.stopNumber != null) {
                        newStopMap.set(Number(o.orderId), o.stopNumber);
                    }
                });
                return {
                    id: String(child.id ?? idx),
                    name: child.taskName ?? `${areaName}-${idx + 1}`,
                    colorIndex: idx % SEGMENT_COLOR_PALETTE.length,
                    driverAccountId: child.driverId ? String(child.driverId) : '',
                    ...(() => {
                        const detail = child.assignTypeDetail ?? [];
                        const first = detail[0];
                        if (first && /^\d+,\d+$/.test(first)) {
                            const [min, max] = first.split(',').map(Number);
                            return {capacityMin: min, capacityMax: max, preferredZipcodes: detail.slice(1)};
                        }
                        return {capacityMin: null, capacityMax: null, preferredZipcodes: detail};
                    })(),
                    startAddress: child.startAddress ?? null,
                    endAddress: child.endAddress ?? null,
                    assignedOrderIds: orderIds,
                    totalParcelCount: orderIds.length,
                    parcelNumberStart: child.parcelNumberStart ?? null,
                    parcelNumberEnd: child.parcelNumberEnd ?? null,
                    algoRecordId: null,
                };
            });
            setAlgorithmSegments(algoSegs);
            setAlgoStopMap(newStopMap);
            // 已发布 → published（step4 打勾）
            setAlgoPhase('published');
        } else {
            resetToDefaultSegments(areaName);
        }
        })();
    }, [taskDetail, activeOrderIdSet, orderList]);

    const [fetchFastRoutePlanning] = useLazyQuery(fastPlanQuery, {fetchPolicy: 'network-only'});

    /**
     * orderList 变化 或 抽屉打开时，三种模式统一重算 assignedOrderIds
     */
    useEffect(() => {
        if (!visible || !orderList.length) return;
        setMapSegments(prev => {
            const assigned = new Set<number>();
            return prev.map(s => {
                const ids = matchOrderIdsByPolygons(s.polygons, assigned);
                ids.forEach(id => assigned.add(id));
                return { ...s, assignedOrderIds: ids, totalParcelCount: ids.length };
            });
        });
        setZipcodeSegments(prev => {
            const assigned = new Set<number>();
            return prev.map(s => {
                const ids = matchOrderIdsByZipcodes(s.zipcodes).filter(id => !assigned.has(id));
                ids.forEach(id => assigned.add(id));
                return { ...s, assignedOrderIds: ids, totalParcelCount: ids.length };
            });
        });
        setStopSegments(prev => prev.map(s => {
            const ids = matchOrderIdsByStopRanges(s.stopRanges);
            return { ...s, assignedOrderIds: ids, totalParcelCount: ids.length };
        }));
    }, [orderList, visible, algoPhase]);

    /**
     * 切到 STOP TAB 且预规划未完成时，调快速接口
     */
    useEffect(() => {
        if (splitMode !== 'stop') return;
        if (prePlanningCompleted) return;
        if (hasFetchedQuickPlan.current) return;
        if (!warehouseId || !record?.areaId) return;

        hasFetchedQuickPlan.current = true;
        setIsPrePlanning(true);
        fetchFastRoutePlanning({variables: {warehouseId, areaId: record.areaId}}).then(res => {
            const result = (res.data as any)?.[fastPlanKey];
            if (result) setOrderList(result);
        }).finally(() => setIsPrePlanning(false));
    }, [splitMode, prePlanningCompleted, warehouseId, record, fetchFastRoutePlanning]);

    // 抽屉关闭时重置快速规划状态
    useEffect(() => {
        if (!visible) {
            hasFetchedQuickPlan.current = false;
            setIsPrePlanning(false);
        }
    }, [visible]);

    const {data: driversData} = useQuery<FindDriversByDspIdsResponse, FindDriversByDspIdsVariables>(
        FIND_DRIVERS_BY_DSP_IDS,
        {
            variables: {dspIds: [record?.dspId!]},
            skip: !visible || !record?.dspId,
        }
    );

    const {data: warehouseData} = useQuery<GetWarehouseByIdResponse>(
        GET_WAREHOUSE_BY_ID,
        {
            variables: {id: warehouseId},
            skip: !visible || !warehouseId,
        }
    );

    const whsLat = warehouseData?.warehouse?.location?.latitude ?? undefined;
    const whsLng = warehouseData?.warehouse?.location?.longitude ?? undefined;

    const whsDriverAddress = useMemo((): DriverAddressInput | null => {
        const w = warehouseData?.warehouse;
        if (!w) return null;
        return {
            streetAddress: w.addressLine1 ?? null,
            city: w.city ?? null,
            state: w.state ?? null,
            zipCode: w.zipcode ?? null,
            latitude: w.location?.latitude ?? null,
            longitude: w.location?.longitude ?? null,
        };
    }, [warehouseData]);

    const isWhsAddress = useCallback((addr: DriverAddressInput | null | undefined): boolean => {
        if (!whsDriverAddress || !addr) return false;
        // 优先用坐标比较，坐标不存在时用街道字段兜底
        if (addr.latitude != null && whsLat != null) {
            return addr.latitude === whsLat && addr.longitude === whsLng;
        }
        return addr.streetAddress === whsDriverAddress.streetAddress &&
            addr.city === whsDriverAddress.city &&
            addr.state === whsDriverAddress.state &&
            addr.zipCode === whsDriverAddress.zipCode;
    }, [whsDriverAddress, whsLat, whsLng]);

    const driverOptions = useMemo(() => {
        const drivers = driversData?.findDriversByDspIds ?? [];
        return drivers
            .filter((d: DriverType) => d.isEmployed)
            .map((d: DriverType) => {
                const isVerified = d.verificationStatus === 2;
                const isBlacklisted = !!d.isBlacklist;
                return {
                    label: (
                        <span>
                            <span style={{fontSize: 14}}>{d.phone}</span>
                            <span style={{fontSize: 12, color: '#666', marginLeft: 4}}>({d.name})</span>
                            {isBlacklisted ? (
                                <span style={{fontSize: 12, color: '#faad14', marginLeft: 8}}>{t('delivery.routes.label.driverBlacklistedTag')}</span>
                            ) : isVerified ? (
                                <span style={{fontSize: 12, color: '#52c41a', marginLeft: 8}}>{t('splitOrder.drawer.verified')}</span>
                            ) : (
                                <span style={{fontSize: 12, color: '#faad14', marginLeft: 8}}>{t('splitOrder.drawer.unverified')}</span>
                            )}
                        </span>
                    ),
                    value: d.accountId.toString(),
                    disabled: isBlacklisted || !isVerified,
                    name: d.name,
                    phone: d.phone,
                };
            });
    }, [driversData]);

    // 缓存恢复的 segments 中可能残留黑名单司机，driverOptions 就绪后清空
    const validDriverIds = useMemo(() => new Set(driverOptions.filter(d => !d.disabled).map(d => d.value)), [driverOptions]);
    useEffect(() => {
        const clearBlacklisted = <T extends { driverAccountId: string }>(seg: T): T =>
            seg.driverAccountId && !validDriverIds.has(seg.driverAccountId)
                ? { ...seg, driverAccountId: '' }
                : seg;
        setMapSegments(prev => {
            const next = prev.map(clearBlacklisted);
            return next.some((s, i) => s !== prev[i]) ? next : prev;
        });
        setZipcodeSegments(prev => {
            const next = prev.map(clearBlacklisted);
            return next.some((s, i) => s !== prev[i]) ? next : prev;
        });
        setStopSegments(prev => {
            const next = prev.map(clearBlacklisted);
            return next.some((s, i) => s !== prev[i]) ? next : prev;
        });
        setAlgorithmSegments(prev => {
            const next = prev.map(clearBlacklisted);
            return next.some((s, i) => s !== prev[i]) ? next : prev;
        });
    }, [validDriverIds, isPublished]);

    const zipcodeOptions = useMemo(() => {
        const counts = new Map<string, number>();
        orderList.forEach(o => {
            if (o.zipcode) counts.set(o.zipcode, (counts.get(o.zipcode) ?? 0) + 1);
        });
        return Array.from(counts.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([zip, count]) => ({label: `${zip} (${t('splitOrder.drawer.parcelsCount', { count })})`, value: zip}));
    }, [orderList]);

    const driverFilterOption = (input: string, option: any) => {
        const text = input.toLowerCase();
        return option?.phone?.includes(text) || option?.name?.toLowerCase().includes(text);
    };

    const startEditSegment = (segId: string) => {
        const polys = segPolygonRefs.current.get(segId);
        if (!polys?.length) return;
        polys.forEach(p => p.setEditable(true));
        setEditingSegId(segId);
    };

    const finishEditSegment = () => {
        if (!editingSegId) return;
        const polys = segPolygonRefs.current.get(editingSegId);
        if (!polys) { setEditingSegId(null); return; }
        polys.forEach(p => p.setEditable(false));
        const newPolygons: google.maps.LatLngLiteral[][] = polys.map(p =>
            p.getPath().getArray().map((ll: google.maps.LatLng) => ({lat: ll.lat(), lng: ll.lng()}))
        );
        updateMapSegment(editingSegId, {polygons: newPolygons});
        setEditingSegId(null);
    };

    const isPointInPolygon = (point: google.maps.LatLngLiteral, polygon: google.maps.LatLngLiteral[]): boolean => {
        // 优先用 Google geometry 库（球面几何，精确）
        if (google.maps.geometry?.poly?.containsLocation) {
            const gmPoly = new google.maps.Polygon({paths: polygon});
            return google.maps.geometry.poly.containsLocation(
                new google.maps.LatLng(point.lat, point.lng),
                gmPoly,
            );
        }
        // 降级：射线投射算法
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const xi = polygon[i].lng, yi = polygon[i].lat;
            const xj = polygon[j].lng, yj = polygon[j].lat;
            if (((yi > point.lat) !== (yj > point.lat)) &&
                (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi) + xi)) {
                inside = !inside;
            }
        }
        return inside;
    };

    /**
     * 完成画图事件
     */
    const completeDrawing = useCallback((pathsOverride?: {lat: number; lng: number}[]) => {
        const paths = pathsOverride ?? polygonPaths;
        if (paths.length < 3) {
            setIsDrawing(false);
            setPolygonPaths([]);
            setMousePos(null);
    
            return;
        }

        const areaName = record?.areaName ?? 'Area';

        const alreadyAssigned = new Set(mapSegments.flatMap(s => s.assignedOrderIds));
        const enclosed = matchOrderIdsByPolygons([paths], alreadyAssigned);

        setMapSegments(prev => {
            const used = new Set(prev.map(s => s.colorIndex));
            const colorIdx = Array.from({length: SEGMENT_COLOR_PALETTE.length}, (_, i) => i)
                    .find(i => !used.has(i))
                ?? prev.length % SEGMENT_COLOR_PALETTE.length;
            return [...prev, {
                id: `seg-${Date.now()}`,
                name: `${areaName}-${prev.length + 1}`,
                colorIndex: colorIdx,
                driverAccountId: '',
                startAddress: null,
                endAddress: null,
                polygons: [[...paths]],
                assignedOrderIds: enclosed,
                totalParcelCount: enclosed.length,
            }];
        });
        setIsDrawing(false);
        setPolygonPaths([]);
        setMousePos(null);

    }, [polygonPaths, orderList, record, mapSegments]);

    const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();

        // 标点模式
        if (pinningTarget) {
            const target = pinningTarget;
            setPinningTarget(null);
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({location: {lat, lng}}, (results, status) => {
                let driverAddr: DriverAddressInput | null = null;
                if (status === 'OK' && results && results.length > 0) {
                    // 遍历所有结果，各字段独立取最优值
                    let bestStreetAddress = '';
                    let bestCity = '';
                    let bestState = '';
                    let bestZipCode = '';
                    let bestCounty = '';
                    for (const r of results) {
                        const get = (type: string) =>
                            r.address_components.find((c: any) => c.types.includes(type))?.long_name ?? '';
                        const getShort = (type: string) =>
                            r.address_components.find((c: any) => c.types.includes(type))?.short_name ?? '';
                        const streetNumber = get('street_number');
                        const route = get('route');
                        const city = get('locality') || get('sublocality');
                        const state = getShort('administrative_area_level_1');
                        const zipCode = get('postal_code');
                        const county = get('administrative_area_level_2');
                        const streetAddress = [streetNumber, route].filter(Boolean).join(' ');
                        if (streetAddress && !bestStreetAddress) bestStreetAddress = streetAddress;
                        if (city && !bestCity) bestCity = city;
                        if (state && !bestState) bestState = state;
                        if (zipCode && !bestZipCode) bestZipCode = zipCode;
                        if (county && !bestCounty) bestCounty = county;
                    }
                    if (bestCity || bestStreetAddress) {
                        driverAddr = {
                            streetAddress: bestStreetAddress || null,
                            city: bestCity || null,
                            state: bestState || null,
                            zipCode: bestZipCode || null,
                            county: bestCounty || null,
                            latitude: lat,
                            longitude: lng,
                        };
                    }
                }
                if (!driverAddr) {
                    console.warn('[Pin] 无法解析有效地址，status=', status);
                    notification.error({
                        message: t('splitOrder.drawer.getAddressFailed'),
                        description: t('splitOrder.drawer.invalidAddress'),
                        placement: 'topLeft',
                        duration: 3,
                    });
                    return;
                }
                const isStart = target.field === 'start';
                const updates = isStart
                    ? {startAddress: driverAddr}
                    : {endAddress: driverAddr};
                if (target.segType === 'map') updateMapSegment(target.segId, updates);
                else if (target.segType === 'zipcode') updateZipcodeSegment(target.segId, updates);
                else if (target.segType === 'stop') updateStopSegment(target.segId, updates);
                else updateAlgorithmSegment(target.segId, updates);
            });
            return;
        }

        // 绘制模式
        if (!isDrawing) return;
        setPolygonPaths(prev => [...prev, {lat, lng}]);
    }, [isDrawing, pinningTarget, updateMapSegment, updateZipcodeSegment, updateStopSegment]);

    const handleMapRightClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (!isDrawing) return;
        e.stop?.();
        completeDrawing();
    }, [isDrawing, completeDrawing]);

    // 双击结束绘制：dblclick 触发前已有两次 click 加点，需去掉最后一个重复点
    const handleMapDblClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (!isDrawing) return;
        e.stop?.();
        completeDrawing(polygonPaths.slice(0, -1));
    }, [isDrawing, completeDrawing, polygonPaths]);

    const handleMouseMove = useCallback((e: google.maps.MapMouseEvent) => {
        if (isDrawing && e.latLng && polygonPaths.length > 0) {
            setMousePos({lat: e.latLng.lat(), lng: e.latLng.lng()});
        }

        // Zipcode 围栏 hover 检测
        if (!showZipcodeBoundary || !e.latLng || !zipcodeBoundaryData.length) {
            if (hoveredZipcodeBoundary) setHoveredZipcodeBoundary(null);
            return;
        }
        const latLng = e.latLng;
        const domEvent = e.domEvent as MouseEvent;
        let found = false;
        for (const entry of zipcodeBoundaryData) {
            if (entry.paths.length >= 3 && google.maps.geometry?.poly?.containsLocation?.(
                latLng, new google.maps.Polygon({ paths: entry.paths })
            )) {
                setHoveredZipcodeBoundary({
                    zipcode: entry.zipcode,
                    parcelCount: zipcodeParcelCountMap.get(entry.zipcode) ?? 0,
                    x: domEvent.clientX,
                    y: domEvent.clientY,
                });
                found = true;
                break;
            }
        }
        if (!found && hoveredZipcodeBoundary) setHoveredZipcodeBoundary(null);
    }, [isDrawing, polygonPaths.length, showZipcodeBoundary, zipcodeBoundaryData, hoveredZipcodeBoundary, zipcodeParcelCountMap]);

    const {isLoaded, loadError} = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        preventGoogleFontsLoading: true
    });

    // 动态加载 geometry 库（避免与其他 loader 冲突）
    useEffect(() => {
        if (isLoaded) {
            (google.maps as any).importLibrary?.('geometry').catch(() => {
            });
        }
    }, [isLoaded]);

    const [hoveredSegId, setHoveredSegId] = useState<string | null>(null);

    const mapStats = useMemo(() => {
        const assignedIds = new Set<number>();
        mapSegments.forEach(seg => {
            if (seg.driverAccountId) {
                seg.assignedOrderIds.forEach(id => assignedIds.add(id));
            }
        });
        return {
            segments: mapSegments.length,
            drivers: new Set(mapSegments.map(s => s.driverAccountId).filter(Boolean)).size,
            assigned: assignedIds.size,
            unassigned: orderList.length - assignedIds.size,
        };
    }, [mapSegments, orderList]);

    const zipcodeStats = useMemo(() => {
        const assignedIds = new Set<number>();
        zipcodeSegments.forEach(seg => {
            if (seg.driverAccountId && seg.zipcodes.length > 0) {
                const zipcodeSet = new Set(seg.zipcodes);
                orderList.forEach(o => {
                    if (o.orderId != null && o.zipcode && zipcodeSet.has(o.zipcode)) assignedIds.add(o.orderId);
                });
            }
        });
        return {
            segments: zipcodeSegments.length,
            drivers: new Set(zipcodeSegments.map(s => s.driverAccountId).filter(Boolean)).size,
            assigned: assignedIds.size,
            unassigned: orderList.length - assignedIds.size,
        };
    }, [zipcodeSegments, orderList]);

    const stopStats = useMemo(() => {
        const assignedIds = new Set<number>();
        stopSegments.forEach(seg => {
            if (seg.driverAccountId) {
                seg.stopRanges.forEach(range => {
                    if (range.parcelNumberStart != null && range.parcelNumberEnd != null) {
                        const rangeStart = Number(range.parcelNumberStart);
                        const rangeEnd = Number(range.parcelNumberEnd);
                        orderList.forEach(o => {
                            if (o.orderId != null && o.stopNumber != null &&
                                o.stopNumber >= rangeStart && o.stopNumber <= rangeEnd) {
                                assignedIds.add(o.orderId);
                            }
                        });
                    }
                });
            }
        });
        return {
            segments: stopSegments.length,
            drivers: new Set(stopSegments.map(s => s.driverAccountId).filter(Boolean)).size,
            assigned: assignedIds.size,
            unassigned: orderList.length - assignedIds.size,
        };
    }, [stopSegments, orderList]);

    // algo 模式：派生每个分段的活跃包裹 ID（assignedOrderIds ∩ orderList），不修改 state，避免竞态
    const algoActiveIdsMap = useMemo((): Map<string, number[]> => {
        const activeIds = new Set(orderList.map(o => o.orderId).filter(Boolean).map(Number));
        const map = new Map<string, number[]>();
        algorithmSegments.forEach(seg => {
            if (algoPhase === 'editing') {
                // 编辑态未分堆，无归属
                map.set(seg.id, []);
            } else {
                map.set(seg.id, seg.assignedOrderIds.filter(id => activeIds.has(Number(id))));
            }
        });
        return map;
    }, [algorithmSegments, orderList, algoPhase]);

    const algoStats = useMemo(() => {
        const totalActive = [...algoActiveIdsMap.values()].reduce((sum, ids) => sum + ids.length, 0);
        return {
            segments: algorithmSegments.length,
            drivers: new Set(algorithmSegments.map(s => s.driverAccountId).filter(Boolean)).size,
            assigned: totalActive,
            unassigned: orderList.length - totalActive,
        };
    }, [algorithmSegments, orderList, algoActiveIdsMap]);

    const currentStats = splitMode === 'map' ? mapStats : splitMode === 'zipcode' ? zipcodeStats : splitMode === 'stop' ? stopStats : algoStats;

    // 计算每个订单归属的分段颜色（框选中的包裹排除，显示灰色）
    const rectSelectedSet = useMemo(() => new Set(rectSelectedOrderIds.map(Number)), [rectSelectedOrderIds]);
    const orderColorMap = useMemo((): Map<number, string> => {
        const result = new Map<number, string>();
        const segs = splitMode === 'map' ? mapSegments : splitMode === 'zipcode' ? zipcodeSegments : splitMode === 'stop' ? stopSegments : algorithmSegments;
        segs.forEach(seg => {
            const palette = getSegPalette(seg);
            // algo 模式用派生的活跃 ID，其他模式用 assignedOrderIds（已通过 orderList 匹配）
            const ids = splitMode === 'algo' ? (algoActiveIdsMap.get(seg.id) ?? []) : seg.assignedOrderIds;
            ids.forEach((id) => {
                if (!rectSelectedSet.has(Number(id))) {
                    result.set(Number(id), palette.accent);
                }
            });
        });
        return result;
    }, [splitMode, mapSegments, zipcodeSegments, stopSegments, algorithmSegments, rectSelectedSet, algoActiveIdsMap]);

    // routePlanningOrderList 的 orderId→stopNumber 映射，用于水滴标号和表格排序
    const routePlanningStopMap = useMemo(() => {
        const map = new Map<number, number>();
        const addOrders = (orders: any[]) => {
            orders.forEach(o => {
                const id = o.orderId != null ? Number(o.orderId) : null;
                if (id != null && !isNaN(id) && o.stopNumber != null) {
                    map.set(id, o.stopNumber);
                }
            });
        };
        // 根级 routePlanningOrderList（单分段发布时数据在此）
        addOrders(taskDetail?.routePlanningOrderList ?? []);
        // 子任务 routePlanningOrderList（多分段发布时数据在此）
        (taskDetail?.childTaskList ?? []).forEach(child => {
            addOrders(child.routePlanningOrderList ?? []);
        });
        return map;
    }, [taskDetail]);

    const orderMarkers = useMemo(() => {
        if (!isLoaded) return null;
        const GRAY_FILL = '#9CA3AF';
        const GRAY_STROKE = '#DEDFE0';

        return orderList
            .filter(o => {
                if (o.latitude == null || o.longitude == null) return false;
                // 开关关闭时隐藏已发布的包裹
                if (!showPublishedParcels && o.orderId != null && publishedOrderIdSet.has(Number(o.orderId))) return false;
                return true;
            })
            .map(o => {
                const orderId = o.orderId != null ? Number(o.orderId) : null;

                // 填充色：分段归属决定
                const color = (orderId != null ? orderColorMap.get(orderId) : undefined) ?? GRAY_FILL;
                // 边框色：已发布包裹白色，其他灰色
                const isPublishedOrder = isPublished && orderId != null && publishedOrderIdSet.has(orderId);
                const strokeColor = isPublishedOrder ? 'white' : GRAY_STROKE;

                // stopNumber 展示规则：当前模式与发布方式一致时才展示（stop 始终展示）
                let stopNum: number | null = null;
                if (orderId != null) {
                    if (splitMode === 'stop') {
                        stopNum = o.stopNumber ?? null;
                    } else if (splitMode === 'map' && isPublished && taskDetail?.assignType === AssignType.BY_MAP) {
                        stopNum = routePlanningStopMap.get(orderId) ?? null;
                    } else if (splitMode === 'zipcode' && isPublished && taskDetail?.assignType === AssignType.BY_ZIPCODE) {
                        stopNum = routePlanningStopMap.get(orderId) ?? null;
                    } else if (splitMode === 'algo' && (algoPhase === 'routePlanned' || algoPhase === 'published')) {
                        stopNum = algoStopMap.get(orderId) ?? null;
                    }
                }
                const label = stopNum != null ? String(stopNum) : '';
                const icon: google.maps.Icon = {
                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="35" viewBox="0 0 36 46">
                            <path d="M18 2C10.3 2 4 8.3 4 16c0 10.5 14 28 14 28s14-17.5 14-28c0-7.7-6.3-14-14-14z" fill="${color}" stroke="${strokeColor}" stroke-width="3"/>
                            ${label ? `<text x="18" y="20" font-size="10" fill="white" text-anchor="middle" font-weight="bold" font-family="Arial">${label}</text>` : ''}
                        </svg>
                    `),
                    scaledSize: {width: 32, height: 40} as google.maps.Size,
                    anchor: {x: 16, y: 40} as google.maps.Point,
                };
                return (
                    <Marker
                        key={`${o.orderId}-${color}`}
                        position={{lat: o.latitude!, lng: o.longitude!}}
                        icon={icon}
                        clickable={false}
                    />
                );
            });
    }, [isLoaded, orderList, orderColorMap, isPublished, publishedOrderIdSet, splitMode, routePlanningStopMap, showPublishedParcels, taskDetail?.assignType, algoPhase, algoStopMap]);

    // Stop 路径连线：有 stop 数据时按 stopNumber 从小到大连线
    // - 已发布（map/zipcode/stop/algo）或 stop 模式未发布：按分段各自连线
    // - stop 模式未发布且无分段归属：全部包裹连一条线
    // - algo routePlanned/published：按分段连线
    const routePolylines = useMemo((): Array<{path: google.maps.LatLngLiteral[]; color: string}> | null => {
        // 构建 orderId → stopNumber 映射
        const stopMap = new Map<number, number>();
        if (splitMode === 'stop') {
            orderList.forEach(o => {
                if (o.orderId != null && o.stopNumber != null) stopMap.set(Number(o.orderId), o.stopNumber);
            });
        } else if (splitMode === 'map' && isPublished && taskDetail?.assignType === AssignType.BY_MAP) {
            routePlanningStopMap.forEach((stop, id) => stopMap.set(id, stop));
        } else if (splitMode === 'zipcode' && isPublished && taskDetail?.assignType === AssignType.BY_ZIPCODE) {
            routePlanningStopMap.forEach((stop, id) => stopMap.set(id, stop));
        } else if (splitMode === 'algo' && (algoPhase === 'routePlanned' || algoPhase === 'published')) {
            algoStopMap.forEach((stop, id) => stopMap.set(id, stop));
        }
        if (stopMap.size === 0) return null;

        // 辅助：从 orderId 列表构建排序后的 path
        const buildPath = (ids: number[]): google.maps.LatLngLiteral[] => {
            const points: Array<{lat: number; lng: number; stop: number}> = [];
            ids.forEach(id => {
                const numId = Number(id);
                const o = orderList.find(item => Number(item.orderId) === numId);
                const stop = stopMap.get(numId);
                if (o && o.latitude != null && o.longitude != null && stop != null) {
                    points.push({lat: o.latitude, lng: o.longitude, stop});
                }
            });
            points.sort((a, b) => a.stop - b.stop);
            return points.map(p => ({lat: p.lat, lng: p.lng}));
        };

        // 判断是否按分段连线（发布方式和当前 tab 一致时才按分段）
        const shouldUseSeg =
            (splitMode === 'map' && isPublished && taskDetail?.assignType === AssignType.BY_MAP) ||
            (splitMode === 'zipcode' && isPublished && taskDetail?.assignType === AssignType.BY_ZIPCODE) ||
            (splitMode === 'stop' && isPublished && taskDetail?.assignType === AssignType.BY_STOP) ||
            (splitMode === 'algo' && (algoPhase === 'routePlanned' || algoPhase === 'published'));

        if (shouldUseSeg) {
            // 已发布 或 algo routePlanned/published：按分段各自连线
            const segs: Array<{assignedOrderIds: number[]; colorIndex: number; startAddress?: any; endAddress?: any}> =
                splitMode === 'map' ? mapSegments
                : splitMode === 'zipcode' ? zipcodeSegments
                : splitMode === 'stop' ? stopSegments
                : algorithmSegments;
            return segs
                .filter(seg => seg.assignedOrderIds.length > 0)
                .map(seg => {
                    const path = buildPath(seg.assignedOrderIds);
                    // 起点加到最前面
                    if (seg.startAddress?.latitude != null && seg.startAddress?.longitude != null) {
                        path.unshift({lat: seg.startAddress.latitude, lng: seg.startAddress.longitude});
                    }
                    // 终点加到最后面
                    if (seg.endAddress?.latitude != null && seg.endAddress?.longitude != null) {
                        path.push({lat: seg.endAddress.latitude, lng: seg.endAddress.longitude});
                    }
                    return {path, color: getSegPalette(seg).accent};
                })
                .filter(line => line.path.length > 1);
        }

        // stop 模式未发布：全部包裹连一条线
        if (splitMode === 'stop') {
            const allIds = orderList
                .filter(o => o.orderId != null && stopMap.has(Number(o.orderId)))
                .map(o => Number(o.orderId!));
            const path = buildPath(allIds);
            if (path.length < 2) return null;
            return [{path, color: SEGMENT_COLOR_PALETTE[0].accent}];
        }

        return null;
    }, [splitMode, isPublished, algoPhase, orderList, mapSegments, zipcodeSegments, stopSegments, algorithmSegments, routePlanningStopMap, algoStopMap, taskDetail?.assignType]);

    const [map, setMap] = useState<google.maps.Map | null>(null);
    const onLoad = useCallback((m: google.maps.Map) => setMap(m), []);
    const [panExpanded, setPanExpanded] = useState(false);

    // 命令式管理路径连线 Polyline
    const routePolylinesRef = useRef<google.maps.Polyline[]>([]);
    useEffect(() => {
        // 先清除上一轮
        routePolylinesRef.current.forEach(p => p.setMap(null));
        routePolylinesRef.current = [];

        if (!map || !showNavTrack || !routePolylines) return;

        routePolylinesRef.current = routePolylines.map(line =>
            new google.maps.Polyline({
                path: line.path,
                strokeColor: line.color,
                strokeWeight: 2,
                strokeOpacity: 0.7,
                clickable: false,
                map,
            })
        );

        return () => {
            routePolylinesRef.current.forEach(p => p.setMap(null));
            routePolylinesRef.current = [];
        };
    }, [map, showNavTrack, routePolylines]);

    /**
     * ===== 矩形框选改派逻辑 =====
     * 矩形框选：在地图容器上叠加透明 div 拦截鼠标事件
     */
    const rectDivRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!map || !isRectSelecting) return;
        map.setOptions({draggable: false, scrollwheel: false});

        const mapDiv = (map as any).getDiv() as HTMLElement;
        // 创建覆盖 div
        const overlay = document.createElement('div');
        overlay.style.cssText = 'position:absolute;inset:0;z-index:1;cursor:crosshair;';
        mapDiv.style.position = 'relative';
        mapDiv.appendChild(overlay);
        rectDivRef.current = overlay;

        // 画框用的 div
        const box = document.createElement('div');
        box.style.cssText = 'position:absolute;border:2px solid #6366f1;background:rgba(99,102,241,0.15);pointer-events:none;display:none;';
        overlay.appendChild(box);

        let startX = 0, startY = 0;
        let dragging = false;

        const onDown = (e: MouseEvent) => {
            startX = e.offsetX;
            startY = e.offsetY;
            dragging = true;
            box.style.left = `${startX}px`;
            box.style.top = `${startY}px`;
            box.style.width = '0';
            box.style.height = '0';
            box.style.display = 'block';
        };

        const onMove = (e: MouseEvent) => {
            if (!dragging) return;
            const curX = e.offsetX, curY = e.offsetY;
            box.style.left = `${Math.min(startX, curX)}px`;
            box.style.top = `${Math.min(startY, curY)}px`;
            box.style.width = `${Math.abs(curX - startX)}px`;
            box.style.height = `${Math.abs(curY - startY)}px`;
        };

        const onUp = (e: MouseEvent) => {
            if (!dragging) return;
            dragging = false;
            box.style.display = 'none';

            const endX = e.offsetX, endY = e.offsetY;
            // 像素坐标转经纬度
            const projection = (map as any).getProjection();
            const bounds = map.getBounds();
            if (!projection || !bounds) return;

            const topRight = projection.fromLatLngToPoint(bounds.getNorthEast())!;
            const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest())!;
            const mapWidth = mapDiv.offsetWidth;
            const mapHeight = mapDiv.offsetHeight;

            const pxToLatLng = (px: number, py: number): google.maps.LatLngLiteral => {
                const worldX = bottomLeft.x + (px / mapWidth) * (topRight.x - bottomLeft.x);
                const worldY = topRight.y + (py / mapHeight) * (bottomLeft.y - topRight.y);
                const latLng = projection.fromPointToLatLng(new google.maps.Point(worldX, worldY))!;
                return {lat: latLng.lat(), lng: latLng.lng()};
            };

            const corner1 = pxToLatLng(Math.min(startX, endX), Math.min(startY, endY));
            const corner2 = pxToLatLng(Math.max(startX, endX), Math.max(startY, endY));
            const minLat = Math.min(corner1.lat, corner2.lat);
            const maxLat = Math.max(corner1.lat, corner2.lat);
            const minLng = Math.min(corner1.lng, corner2.lng);
            const maxLng = Math.max(corner1.lng, corner2.lng);

            const selected = orderList.filter(o =>
                o.orderId != null && o.latitude != null && o.longitude != null &&
                o.latitude >= minLat && o.latitude <= maxLat &&
                o.longitude >= minLng && o.longitude <= maxLng
            ).map(o => o.orderId!);

            // 拖拽距离太小视为点击，忽略
            if (Math.abs(endX - startX) < 5 && Math.abs(endY - startY) < 5) {
                return;
            }

            setRectSelectedOrderIds(selected);

            // 画完后在地图上显示矩形
            if (rectOverlayRef.current) rectOverlayRef.current.setMap(null);
            if (selected.length > 0) {
                rectOverlayRef.current = new google.maps.Rectangle({
                    map,
                    bounds: new google.maps.LatLngBounds(
                        {lat: minLat, lng: minLng},
                        {lat: maxLat, lng: maxLng},
                    ),
                    fillColor: '#6366f1',
                    fillOpacity: 0.15,
                    strokeColor: '#6366f1',
                    strokeWeight: 2,
                    clickable: false,
                });
                // 选中包裹后移除覆盖层，显示改派浮窗
                overlay.remove();
                rectDivRef.current = null;
                map.setOptions({draggable: true, scrollwheel: true});
            } else {
                // 没选中包裹，提示并保持可继续框选
                notification.warning({message: t('splitOrder.drawer.rectSelectEmpty'), duration: 2});
            }
        };

        overlay.addEventListener('mousedown', onDown);
        overlay.addEventListener('mousemove', onMove);
        overlay.addEventListener('mouseup', onUp);

        return () => {
            overlay.remove();
            rectDivRef.current = null;
            map.setOptions({draggable: true, scrollwheel: true});
            if (rectOverlayRef.current) {
                rectOverlayRef.current.setMap(null);
                rectOverlayRef.current = null;
            }
        };
    }, [map, isRectSelecting, orderList]);

    const cancelRectSelect = useCallback(() => {
        setIsRectSelecting(false);
        setRectSelectedOrderIds([]);
        if (rectOverlayRef.current) {
            rectOverlayRef.current.setMap(null);
            rectOverlayRef.current = null;
        }
    }, []);

    const [fetchIsTeamPickup] = useLazyQuery<IsTeamPickupWarehouseResponse, IsTeamPickupWarehouseVariables>(
        IS_TEAM_PICKUP_WAREHOUSE, {fetchPolicy: 'network-only'}
    );

    // 抽屉打开时批量预查司机配送能力，缓存为 Map
    const allDriverAccountIds = useMemo(() =>
        driverOptions.map((d: any) => Number(d.value)).filter(Boolean),
    [driverOptions]);

    const { data: driverCapacitiesData } = useQuery<ListDriverCapacitiesResponse>(
        LIST_DRIVER_CAPACITIES,
        {
            variables: { driverAccountIds: allDriverAccountIds },
            skip: !visible || allDriverAccountIds.length === 0,
            fetchPolicy: 'cache-and-network',
        }
    );

    const driverCapacityMap = useMemo((): Map<string, { capacityMin: number; capacityMax: number }> => {
        const map = new Map<string, { capacityMin: number; capacityMax: number }>();
        (driverCapacitiesData?.listDriverCapacities ?? []).forEach(item => {
            map.set(String(item.driverAccountId), { capacityMin: item.capacityMin, capacityMax: item.capacityMax });
        });
        return map;
    }, [driverCapacitiesData]);

    /**
     * 手动调整包裹结束事件：调整包裹归属
     */
    const handleRectReassign = useCallback(async (targetDriverAccountId: string) => {
        if (rectSelectedOrderIds.length === 0) return;

        // 前置查询：是否小组取货模式
        let isTeamPickup = false;
        if (warehouseId && record?.dspId) {
            try {
                const {data} = await fetchIsTeamPickup({variables: {warehouseId, dspId: record.dspId}});
                isTeamPickup = data?.isTeamPickupWarehouse ?? false;
            } catch {
                // 查询失败默认非小组取货
            }
        }

        const selectedSet = new Set(rectSelectedOrderIds.map(Number));
        setAlgorithmSegments(prev => {
            // 1. 从所有分段移除被选中的包裹
            let updated = prev.map(seg => {
                const remaining = seg.assignedOrderIds.filter(id => !selectedSet.has(Number(id)));
                return {...seg, assignedOrderIds: remaining, totalParcelCount: remaining.length};
            });
            // 2. 目标司机有分段→合并，无分段→新建
            const targetSeg = updated.find(s => s.driverAccountId === targetDriverAccountId);
            if (targetSeg) {
                updated = updated.map(s => {
                    if (s.id !== targetSeg.id) return s;
                    const merged = [...s.assignedOrderIds, ...rectSelectedOrderIds.map(Number)];
                    return {...s, assignedOrderIds: merged, totalParcelCount: merged.length};
                });
            } else {
                const areaName = record?.areaName ?? 'Area';
                const cap = driverCapacityMap.get(targetDriverAccountId);
                updated = [...updated, {
                    id: `seg-${Date.now()}`,
                    name: `${areaName}-${updated.length + 1}`,
                    colorIndex: updated.length % SEGMENT_COLOR_PALETTE.length,
                    driverAccountId: targetDriverAccountId,
                    capacityMin: cap?.capacityMin ?? null,
                    capacityMax: cap?.capacityMax ?? null,
                    preferredZipcodes: [],
                    assignedOrderIds: [...rectSelectedOrderIds],
                    totalParcelCount: rectSelectedOrderIds.length,
                }];
            }
            // 3. 过滤空分段
            updated = updated.filter(s => s.assignedOrderIds.length > 0);

            // 4. 重算 parcelNumberStart/End
            if (isTeamPickup) {
                // 小组取货：分段连续编号
                let offset = 0;
                updated = updated.map(seg => {
                    const start = offset + 1;
                    const end = offset + seg.assignedOrderIds.length;
                    offset = end;
                    return {...seg, parcelNumberStart: start, parcelNumberEnd: end};
                });
            } else {
                // 非小组取货：每个分段从 1 开始
                updated = updated.map(seg => ({
                    ...seg,
                    parcelNumberStart: 1,
                    parcelNumberEnd: seg.assignedOrderIds.length,
                }));
            }
            return updated;
        });
        cancelRectSelect();
    }, [rectSelectedOrderIds, record, warehouseId, cancelRectSelect, fetchIsTeamPickup, driverCapacityMap]);

    // 命令式同步多边形实例：仅 map 模式显示，其他 tab 全部清除
    useEffect(() => {
        if (!map) return;
        if (splitMode !== 'map') {
            segPolygonRefs.current.forEach((polys: google.maps.Polygon[]) => polys.forEach(p => p.setMap(null)));
            segPolygonRefs.current.clear();
            return;
        }
        const currentIds = new Set(mapSegments.map((s: MapSegment) => s.id));
        segPolygonRefs.current.forEach((polys: google.maps.Polygon[], id: string) => {
            if (!currentIds.has(id)) {
                polys.forEach(p => p.setMap(null));
                segPolygonRefs.current.delete(id);
            }
        });
        mapSegments.forEach((seg: MapSegment, idx: number) => {
            // 每次都重建
            segPolygonRefs.current.get(seg.id)?.forEach(p => p.setMap(null));
            segPolygonRefs.current.delete(seg.id);

            const validPolygons = seg.polygons.filter(p => p.length >= 3);
            if (validPolygons.length === 0) return;

            const palette = getSegPalette(seg);

            // 前面所有分段的所有 polygon，用于裁剪
            const prevPolygons = mapSegments
                .slice(0, idx)
                .flatMap((s: MapSegment) => s.polygons.filter(p => p.length >= 3))
                .map((p: google.maps.LatLngLiteral[]) => [p.map(pt => [pt.lng, pt.lat] as [number, number])] as polygonClipping.Polygon);

            const polyInstances: google.maps.Polygon[] = [];
            validPolygons.forEach(polygon => {
                const current: polygonClipping.Polygon = [
                    polygon.map((p: google.maps.LatLngLiteral) => [p.lng, p.lat] as [number, number])
                ];
                let paths: google.maps.LatLngLiteral[][] = [polygon];
                if (prevPolygons.length > 0) {
                    try {
                        const clipped = polygonClipping.difference(current, ...prevPolygons);
                        if (clipped.length === 0) return; // 完全被遮盖
                        paths = clipped.map(r => r[0].map(([lng, lat]) => ({lat, lng})));
                    } catch {
                        paths = [polygon];
                    }
                }
                polyInstances.push(new google.maps.Polygon({
                    paths,
                    fillColor: palette.accent,
                    fillOpacity: 0.15,
                    strokeColor: palette.accent,
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    clickable: false,
                    map,
                }));
            });
            if (polyInstances.length > 0) segPolygonRefs.current.set(seg.id, polyInstances);
        });
    }, [map, mapSegments, splitMode]);


    // 命令式管理绘制预览线（避免 @react-google-maps/api 卸载不调 setMap(null)）
    const previewPolylineRef = useRef<google.maps.Polyline | null>(null);
    const previewMouseLineRef = useRef<google.maps.Polyline | null>(null);
    const previewCloseLineRef = useRef<google.maps.Polyline | null>(null);
    const previewFillRef = useRef<google.maps.Polygon | null>(null);

    useEffect(() => {
        if (!map) return;
        const clear = () => {
            previewPolylineRef.current?.setMap(null);
            previewPolylineRef.current = null;
            previewMouseLineRef.current?.setMap(null);
            previewMouseLineRef.current = null;
            previewCloseLineRef.current?.setMap(null);
            previewCloseLineRef.current = null;
            previewFillRef.current?.setMap(null);
            previewFillRef.current = null;
        };
        if (!isDrawing) {
            clear();
            return;
        }

        // 主折线
        if (polygonPaths.length > 1) {
            if (!previewPolylineRef.current) {
                previewPolylineRef.current = new google.maps.Polyline({
                    map,
                    strokeColor: '#6366f1',
                    strokeWeight: 2,
                    clickable: false
                });
            }
            previewPolylineRef.current.setPath(polygonPaths);
            previewPolylineRef.current.setMap(map);
        } else {
            previewPolylineRef.current?.setMap(null);
            previewPolylineRef.current = null;
        }

        // 鼠标跟随线
        if (mousePos && polygonPaths.length > 0) {
            if (!previewMouseLineRef.current) {
                previewMouseLineRef.current = new google.maps.Polyline({
                    map,
                    strokeColor: '#6366f1',
                    strokeOpacity: 0.5,
                    strokeWeight: 2,
                    clickable: false
                });
            }
            previewMouseLineRef.current.setPath([polygonPaths[polygonPaths.length - 1], mousePos]);
            previewMouseLineRef.current.setMap(map);
        } else {
            previewMouseLineRef.current?.setMap(null);
            previewMouseLineRef.current = null;
        }

        // 闭合预览线
        if (mousePos && polygonPaths.length >= 2) {
            if (!previewCloseLineRef.current) {
                previewCloseLineRef.current = new google.maps.Polyline({
                    map,
                    strokeColor: '#6366f1',
                    strokeOpacity: 0.3,
                    strokeWeight: 1.5,
                    clickable: false
                });
            }
            previewCloseLineRef.current.setPath([mousePos, polygonPaths[0]]);
            previewCloseLineRef.current.setMap(map);
        } else {
            previewCloseLineRef.current?.setMap(null);
            previewCloseLineRef.current = null;
        }

        // 预览填充
        if (mousePos && polygonPaths.length >= 3) {
            if (!previewFillRef.current) {
                previewFillRef.current = new google.maps.Polygon({
                    map,
                    fillColor: '#6366f1',
                    fillOpacity: 0.1,
                    strokeOpacity: 0,
                    clickable: false
                });
            }
            previewFillRef.current.setPaths([...polygonPaths, mousePos]);
            previewFillRef.current.setMap(map);
        } else {
            previewFillRef.current?.setMap(null);
            previewFillRef.current = null;
        }
    }, [map, isDrawing, polygonPaths, mousePos]);

    useEffect(() => {
        if (!map || !isLoaded || !visible) return;
        const validPoints = orderList.filter(o => o.latitude != null && o.longitude != null);
        if (validPoints.length === 0) return;
        const timer = setTimeout(() => {
            google.maps.event.trigger(map, 'resize');
            const bounds = new google.maps.LatLngBounds();
            validPoints.forEach(o => bounds.extend({lat: o.latitude!, lng: o.longitude!}));
            map.fitBounds(bounds);
        }, 300);
        return () => clearTimeout(timer);
    }, [map, isLoaded, orderList, visible]);
    const onUnmount = useCallback(() => setMap(null), []);

    const [submitClustering] = useMutation<SubmitClusteringTaskResponse, SubmitClusteringTaskVariables>(SUBMIT_CLUSTERING_TASK);
    const [submitRoutePlanning] = useMutation<SubmitRoutePlanningTaskResponse, SubmitRoutePlanningTaskVariables>(SUBMIT_ROUTE_PLANNING_TASK);
    const [fetchRoutePlanningResult] = useLazyQuery<GetRoutePlanningTaskResultResponse, GetRoutePlanningTaskResultVariables>(
        GET_ROUTE_PLANNING_TASK_RESULT, {fetchPolicy: 'network-only'}
    );

    /**
     * 算法分单：点击「规划区域」前的校验
     */
    const validateBeforeClustering = useCallback((): string[] => {
        const segs = algorithmSegments;
        const totalParcels = realTotalParcels;
        const errors: string[] = [];

        if (segs.length < 2) {
            errors.push(t('splitOrder.drawer.algoMinTwoSegments'));
            return errors;
        }

        const noDriver = segs.filter(s => !s.driverAccountId);
        if (noDriver.length > 0)
            errors.push(t('splitOrder.drawer.noDriverAssigned', {segments: noDriver.map(s => s.name).join(', ')}));

        const noCapacity = segs.filter(s => s.capacityMin == null || s.capacityMax == null);
        if (noCapacity.length > 0)
            errors.push(t('splitOrder.drawer.algoCapacityRequired', {segments: noCapacity.map(s => s.name).join(', ')}));

        const totalMin = segs.reduce((sum, s) => sum + (s.capacityMin ?? 0), 0);
        if (totalMin > totalParcels)
            errors.push(t('splitOrder.drawer.algoTotalMinExceeds', {totalMin, total: totalParcels}));

        const totalMax = segs.reduce((sum, s) => sum + (s.capacityMax ?? 0), 0);
        if (totalMax < totalParcels)
            errors.push(t('splitOrder.drawer.algoTotalMaxInsufficient', {totalMax, total: totalParcels}));

        return errors;
    }, [algorithmSegments, realTotalParcels, t]);

    const [checkDataStale] = useLazyQuery<CheckDataStaleByPlanningPoolResponse, CheckDataStaleByPlanningPoolVariables>(
        CHECK_DATA_STALE_BY_PLANNING_POOL, {fetchPolicy: 'network-only'}
    );

    // 选择司机时从缓存回填上下限（仅在用户未手动设置时）
    const fillDriverCapacity = useCallback((driverAccountId: string, segId: string) => {
        const cap = driverCapacityMap.get(driverAccountId);
        if (cap) {
            updateAlgorithmSegment(segId, { capacityMin: cap.capacityMin, capacityMax: cap.capacityMax });
        }
    }, [driverCapacityMap, updateAlgorithmSegment]);

    /**
     * 前置校验包裹数是否与规划池一致，不一致弹窗提示刷新
     */
    const checkParcelsConsistency = useCallback(async (): Promise<boolean> => {
        if (!warehouseId || !record?.areaId) return false;
        const areaIds = [record.areaId];
        const areaDspMappings = record.dspId ? [{ areaId: record.areaId, dspId: record.dspId }] : [];
        const { data } = await checkDataStale({
            variables: { warehouseId, areaIds, totalParcels: realTotalParcels, areaDspMappings },
        });
        const result = data?.checkDataStaleByPlanningPool;
        if (result?.parcelsStale) {
            setDataStaleResult(result);
            setDataStaleVisible(true);
            return false;
        }
        return true;
    }, [warehouseId, record, realTotalParcels, checkDataStale]);

    /**
     * 算法分单：实际执行分堆逻辑（校验通过 + 确认弹窗后调用）
     */
    const doStartClustering = useCallback(async () => {
        if (!warehouseId || !record?.areaId) return;

        // 前置校验：包裹数与规划池一致性
        const consistent = await checkParcelsConsistency();
        if (!consistent) return;
        setAlgoPhase('clustering');
        algoClusteringCancelledRef.current = false;
        try {
            const {data} = await submitClustering({
                variables: {
                    request: {
                        warehouseId,
                        areaId: record.areaId,
                        drivers: algorithmSegments.map(seg => {
                            const driverOpt = driverOptions.find(d => d.value === seg.driverAccountId);
                            return {
                                driverAccountId: Number(seg.driverAccountId),
                                driverAccountName: driverOpt?.name ?? null,
                                capacityMin: seg.capacityMin!,
                                capacityMax: seg.capacityMax!,
                                preferredZipcodes: seg.preferredZipcodes,
                                startAddress: cleanAddress(seg.startAddress),
                                endAddress: cleanAddress(seg.endAddress),
                            };
                        }),
                    },
                },
            });
            const result = data?.submitClusteringTask;
            if (algoClusteringCancelledRef.current) return;
            if (result?.segments) {
                const updatedSegments = algorithmSegments.map(seg => {
                    const match = result.segments.find(s => String(s.driverAccountId) === String(seg.driverAccountId));
                    if (!match) return seg;
                    return {
                        ...seg,
                        assignedOrderIds: match.assignedOrderIds.map(Number),
                        totalParcelCount: match.totalParcelCount,
                        parcelNumberStart: match.parcelNumberStart,
                        parcelNumberEnd: match.parcelNumberEnd,
                    };
                });

                setAlgorithmSegments(updatedSegments);
                setAlgoPhase('clustered');

                // 分堆后重新拉 orderList：后端可能用了新到的包裹，前端需同步
                refetchRouteOrders();

                // 自动写缓存，退出再进来能恢复
                if (warehouseId && record?.areaId) await saveSplitRouteCache(warehouseId, Number(record.areaId), {
                    lastSplitMode: 'algo',
                    map: [], zipcode: [], stop: [],
                    algo: {
                        phase: 'clustered',
                        segments: updatedSegments,
                        routePlanningTaskId: null,
                    },
                });
            }
        } catch (err: any) {
            const graphQLErrors = err?.graphQLErrors || [];
            const first = graphQLErrors[0];
            const messageKey = first?.extensions?.messageKey;
            const details = first?.extensions?.details || {};
            let errorDescription = err?.message ?? 'Unknown error';
            if (messageKey) {
                const translated = String(t(messageKey, details));
                if (translated !== messageKey) {
                    errorDescription = translated;
                }
            }
            notification.error({
                message: t('splitOrder.drawer.algoValidationFailed'),
                description: errorDescription,
                placement: 'topLeft',
                duration: 3,
            });
            setAlgoPhase('editing');
        }
    }, [warehouseId, record, algorithmSegments, driverOptions, submitClustering, refetchRouteOrders, checkParcelsConsistency, t]);

    /**
     * 算法分单：点击「司机分区」— 先校验，再弹确认提示，最后执行分堆
     */
    const handleStartClustering = useCallback(async () => {
        const errors = validateBeforeClustering();
        if (errors.length > 0) {
            notification.error({
                message: t('splitOrder.drawer.algoValidationFailed'),
                description: errors[0],
                placement: 'topLeft',
                duration: 3,
            });
            return;
        }

        // 没有任何分段选了期望配送区域，或已勾选"不再提醒"，跳过弹窗
        const hasPreferredZipcodes = algorithmSegments.some(seg => seg.preferredZipcodes.length > 0);
        if (!hasPreferredZipcodes || localStorage.getItem('splitRoute_algoHintDismissed') === 'true') {
            doStartClustering();
            return;
        }

        // 弹出确认提示
        let dontRemind = false;
        Modal.confirm({
            title: t('splitOrder.drawer.algoHintTitle'),
            content: (
                <div>
                    <p style={{ marginBottom: 12 }}>{t('splitOrder.drawer.algoHintDesc')}</p>
                    <Checkbox onChange={e => { dontRemind = e.target.checked; }}>
                        {t('splitOrder.drawer.algoHintDontRemind')}
                    </Checkbox>
                </div>
            ),
            okText: t('splitOrder.drawer.algoHintGotIt'),
            okButtonProps: { style: { backgroundColor: '#6366f1', borderColor: '#6366f1' } },
            cancelButtonProps: { style: { display: 'none' } },
            onOk: () => {
                if (dontRemind) {
                    localStorage.setItem('splitRoute_algoHintDismissed', 'true');
                }
                doStartClustering();
            },
        });
    }, [validateBeforeClustering, doStartClustering, algorithmSegments, t]);

    /**
     * 算法分单：路径规划轮询
     */
    const warehouseIdRef = useRef(warehouseId);
    warehouseIdRef.current = warehouseId;
    const recordRef = useRef(record);
    recordRef.current = record;

    const startRoutePlanningPoll = useCallback((queries: RoutePlanningTaskQueryInput[]) => {
        setAlgoPhase('routePlanning');
        let cancelled = false;

        const poll = async () => {
            try {
                const {data} = await fetchRoutePlanningResult({variables: {queries}});
                if (cancelled) return;
                const result = data?.getRoutePlanningTaskResult;
                if (!result) return;

                if (result.status === 'COMPLETED') {
                    const currentSegments = algorithmSegmentsRef.current;
                    const currentWarehouseId = warehouseIdRef.current;
                    const currentRecord = recordRef.current;
                    const newStopMap = new Map<number, number>();
                    const updatedSegments = currentSegments.map(seg => {
                        const match = result.segments.find(s => String(s.driverAccountId) === String(seg.driverAccountId));
                        if (!match) return seg;
                        match.orders.forEach(o => newStopMap.set(Number(o.orderId), o.stopNumber));
                        const assignedOrderIds = match.orders.map(o => Number(o.orderId));
                        return {
                            ...seg,
                            assignedOrderIds,
                            totalParcelCount: assignedOrderIds.length,
                            parcelNumberStart: match.parcelNumberStart,
                            parcelNumberEnd: match.parcelNumberEnd,
                        };
                    });
                    setAlgorithmSegments(updatedSegments);
                    setAlgoStopMap(newStopMap);
                    setAlgoPhase('routePlanned');
                    // 自动写缓存
                    if (currentWarehouseId && currentRecord?.areaId) {
                        await saveSplitRouteCache(currentWarehouseId, Number(currentRecord.areaId), {
                            lastSplitMode: 'algo',
                            map: [], zipcode: [], stop: [],
                            algo: {
                                phase: 'routePlanned',
                                segments: updatedSegments,
                                stopMap: Object.fromEntries(newStopMap),
                            },
                        });
                    }
                    clearInterval(intervalId);
                    algoPollingCleanupRef.current = null;
                } else if (result.status === 'FAILED') {
                    notification.error({
                        message: t('splitOrder.drawer.algoRoutePlanFailed'),
                        description: t('splitOrder.drawer.algoRoutePlanFailedDesc'),
                        placement: 'topLeft',
                        duration: 3,
                    });
                    setAlgoPhase('clustered');
                    clearInterval(intervalId);
                    algoPollingCleanupRef.current = null;
                }
                // PLANNING → 继续轮询
            } catch {
                if (!cancelled) {
                    notification.error({
                        message: t('splitOrder.drawer.algoRoutePlanFailed'),
                        placement: 'topLeft',
                        duration: 3,
                    });
                    setAlgoPhase('clustered');
                    clearInterval(intervalId);
                    algoPollingCleanupRef.current = null;
                }
            }
        };

        void poll();
        const intervalId = setInterval(poll, 3000);
        const cleanup = () => { cancelled = true; clearInterval(intervalId); };
        algoPollingCleanupRef.current = cleanup;
        return cleanup;
    }, [fetchRoutePlanningResult, t]);

    /**
     * 算法分单：点击「查看路径规划」
     */
    const handleStartRoutePlanning = useCallback(async () => {
        if (!warehouseId || !record?.areaId) return;

        // 前置校验：包裹数与规划池一致性
        const consistent = await checkParcelsConsistency();
        if (!consistent) return;

        const segsWithOrders = algorithmSegments.filter(s => s.assignedOrderIds.length > 0);
        if (segsWithOrders.length === 0) return;

        // 校验：是否有新包裹未被分堆
        const allAssignedIds = new Set(segsWithOrders.flatMap(s => s.assignedOrderIds.map(Number)));
        const unassignedOrders = orderList.filter(o => o.orderId != null && !allAssignedIds.has(Number(o.orderId)));
        if (unassignedOrders.length > 0) {
            Modal.confirm({
                title: t('splitOrder.drawer.algoNewParcelsDetected'),
                content: t('splitOrder.drawer.algoNewParcelsDesc', {count: unassignedOrders.length}),
                okText: t('splitOrder.drawer.algoReplan'),
                okButtonProps: {style: {backgroundColor: '#6366f1', borderColor: '#6366f1'}},
                cancelText: t('splitOrder.drawer.cancel'),
                cancelButtonProps: {style: {borderColor: '#6366f1', color: '#6366f1'}},
                onOk: () => {
                    algoSnapshotRef.current = {segments: [...algorithmSegments], phase: algoPhase, stopMap: new Map(algoStopMap)};
                    setAlgoPhase('reEdit');
                    cancelRectSelect();
                },
            });
            return;
        }

        setAlgoPhase('routePlanning');
        try {
            // 并发提交每个分段
            const taskResults = await Promise.all(
                segsWithOrders.map(seg =>
                    submitRoutePlanning({
                        variables: {
                            request: {
                                warehouseId,
                                areaId: record.areaId!,
                                driverAccountId: Number(seg.driverAccountId),
                                orderIds: seg.assignedOrderIds.map(Number),
                                startAddress: cleanAddress(seg.startAddress),
                                endAddress: cleanAddress(seg.endAddress),
                            },
                        },
                    })
                )
            );

            // 组装轮询入参 + 存 algoRecordId 到 segments
            const queries: RoutePlanningTaskQueryInput[] = taskResults.map((res, idx) => ({
                taskId: res.data!.submitRoutePlanningTask,
                parcelNumberStart: segsWithOrders[idx].parcelNumberStart ?? 1,
                parcelNumberEnd: segsWithOrders[idx].parcelNumberEnd ?? segsWithOrders[idx].totalParcelCount,
            }));
            // 把 taskId 存到对应 segment 的 algoRecordId
            const segsWithRecordId = algorithmSegments.map(seg => {
                const idx = segsWithOrders.findIndex(s => s.id === seg.id);
                if (idx === -1) return seg;
                return {...seg, algoRecordId: taskResults[idx].data!.submitRoutePlanningTask};
            });
            setAlgorithmSegments(segsWithRecordId);

            // 自动写缓存（存 queries + 含 algoRecordId 的 segments 供退出恢复）
            if (warehouseId && record?.areaId) {
                await saveSplitRouteCache(warehouseId, Number(record.areaId), {
                    lastSplitMode: 'algo',
                    map: [], zipcode: [], stop: [],
                    algo: {
                        phase: 'routePlanning',
                        segments: segsWithRecordId,
                        routePlanningQueries: queries,
                    },
                });
            }

            // 开始轮询
            startRoutePlanningPoll(queries);
        } catch (err: any) {
            const graphQLErrors = err?.graphQLErrors || [];
            const first = graphQLErrors[0];
            const messageKey = first?.extensions?.messageKey;
            const details = first?.extensions?.details || {};
            let errorDescription = err?.message ?? 'Unknown error';
            if (messageKey) {
                const translated = String(t(messageKey, details));
                if (translated !== messageKey) {
                    errorDescription = translated;
                }
            }
            notification.error({
                message: t('splitOrder.drawer.algoRoutePlanFailed'),
                description: errorDescription,
                placement: 'topLeft',
                duration: 3,
            });
            setAlgoPhase('clustered');
        }
    }, [warehouseId, record, algorithmSegments, orderList, submitRoutePlanning, startRoutePlanningPoll, checkParcelsConsistency, t]);

    /**
     * 公共校验：分段有效性、司机分配、stop 范围合理性
     * 返回错误信息列表，空数组表示校验通过
     */
    const validateSegments = useCallback((): string[] => {
        if (!record) return [t('splitOrder.drawer.missingAreaInfo')];

        const segments = (splitMode === 'map' ? mapSegments : splitMode === 'zipcode' ? zipcodeSegments : splitMode === 'stop' ? stopSegments : algorithmSegments) as Array<MapSegment & ZipcodeSegment & StopSegment & AlgorithmSegment>;
        const errors: string[] = [];

        // algo 模式：必须分堆完成才能保存/发布
        if (splitMode === 'algo' && !isAlgoViewMode) {
            errors.push(t('splitOrder.drawer.algoSaveNeedClustered'));
            return errors;
        }

        if (segments.length === 0) {
            errors.push(t('splitOrder.drawer.noSegments'));
            return errors;
        }

        // 每个分段必须有司机
        const noDriverSegments = segments.filter(s => !s.driverAccountId);
        if (noDriverSegments.length > 0) {
            errors.push(t('splitOrder.drawer.noDriverAssigned', { segments: noDriverSegments.map(s => s.name).join(', ') }));
        }

        // 每个分段必须有包裹
        const noParcelSegments = segments.filter(s => s.assignedOrderIds.length === 0);
        if (noParcelSegments.length > 0) {
            errors.push(t('splitOrder.drawer.noParcelAssigned', { segments: noParcelSegments.map(s => s.name).join(', ') }));
        }

        // 包裹覆盖完整性校验
        const orderCounts = orderList.length;
        const totalAssigned = segments.reduce((sum, s) => sum + s.assignedOrderIds.length, 0);
        if (totalAssigned !== orderCounts) {
            errors.push(t('splitOrder.drawer.parcelCountMismatch', { assigned: totalAssigned, total: orderCounts }));
        }

        // stop 模式专属校验
        if (splitMode === 'stop') {
            // 范围不完整（都为空、或只填了一半）
            const incompleteSegments = segments.filter(seg =>
                (seg as StopSegment).stopRanges.some(r =>
                    !r.parcelNumberStart || !r.parcelNumberEnd
                )
            );
            if (incompleteSegments.length > 0) {
                errors.push(t('splitOrder.drawer.stopRangeIncomplete', { segments: incompleteSegments.map(s => s.name).join(', ') }));
            }

            // 范围超过包裹总数
            const overflowSegments = segments.filter(seg =>
                (seg as StopSegment).stopRanges.some(r =>
                    (r.parcelNumberEnd && Number(r.parcelNumberEnd) > orderCounts) ||
                    (r.parcelNumberStart && Number(r.parcelNumberStart) > orderCounts)
                )
            );
            if (overflowSegments.length > 0) {
                errors.push(t('splitOrder.drawer.stopRangeOverflow', { segments: overflowSegments.map(s => s.name).join(', '), total: orderCounts }));
            }
        }

        return errors;
    }, [record, splitMode, mapSegments, zipcodeSegments, stopSegments, algorithmSegments, isAlgoViewMode, orderList]);

    /**
     * 保存配置
     */
    const saveToLocalStorage = useCallback(async () => {
        if (!warehouseId || !record?.areaId) return;

        // 公共校验（含 algo 前置校验）
        {
            const errors = validateSegments();
            if (errors.length > 0) {
                notification.error({
                    message: t('splitOrder.drawer.saveFailed'),
                    description: errors[0],
                    placement: 'topLeft',
                    duration: 3,
                });
                return;
            }
        }

        if (!warehouseId || !record?.areaId) return;
        await saveSplitRouteCache(warehouseId, Number(record.areaId), {
            lastSplitMode: splitMode,
            map: mapSegments,
            zipcode: zipcodeSegments,
            stop: stopSegments,
            algo: splitMode === 'algo' ? {
                phase: algoPhase,
                segments: algorithmSegments,
                clusteringTaskId: algoClusteringTaskId,
                routePlanningTaskId: null,
                stopMap: algoStopMap.size > 0 ? Object.fromEntries(algoStopMap) : undefined,
            } : undefined,
        });
        notification.success({
            message: t('splitOrder.drawer.saveSuccess'),
            description: t('splitOrder.drawer.configSaved'),
            placement: 'topLeft',
            duration: 3,
        });
        onSave?.();
        onClose();
    }, [warehouseId, record, splitMode, mapSegments, zipcodeSegments, stopSegments, algorithmSegments, algoPhase, algoClusteringTaskId, algoStopMap, orderList, onClose, onSave, validateSegments]);

    /**
     * 分配司机
     */
    const [assignDrivers] = useMutation<AssignDriversResponse, AssignDriversVariables>(ASSIGN_DRIVERS);


    /**
     * 检查二次发布冲突
     */
    const [checkRepublishConflict] = useLazyQuery<CheckRepublishConflictResponse, CheckRepublishConflictVariables>(
        CHECK_REPUBLISH_CONFLICT, {fetchPolicy: 'network-only'}
    );

    /**
     * 校验发布
     */
    const handleDirectPublish = useCallback(async () => {
        if (!warehouseId || !record) return;

        // 公共校验
        const errors = validateSegments();
        if (errors.length > 0) {
            notification.error({
                message: t('splitOrder.drawer.publishFailed'),
                description: errors[0],
                placement: 'topLeft',
                duration: 3,
            });
            return;
        }

        // 校验数据是否过期
        const areaIds = record.areaId ? [record.areaId] : [];
        const areaDspMappings = record.areaId && record.dspId
            ? [{ areaId: record.areaId, dspId: record.dspId }]
            : [];
        const { data: staleData } = await checkDataStale({
            variables: { warehouseId, areaIds, totalParcels: realTotalParcels, areaDspMappings },
        });
        const staleResult = staleData?.checkDataStaleByPlanningPool;
        if (staleResult?.parcelsStale || (staleResult?.dspStaleAreas?.length ?? 0) > 0) {
            setDataStaleResult(staleResult ?? null);
            setDataStaleVisible(true);
            return;
        }

        // 通过 → 打开确认弹窗，由用户选择派送日期；重发布冲突校验在选定日期后执行
        setPublishTaskDate(null);
        setConfirmModalVisible(true);
    }, [warehouseId, record, realTotalParcels, checkDataStale, validateSegments, t]);

    /**
     * 确认弹窗点「确认」：按最新仓库时间复核所选日期，再按该日期做重发布冲突校验
     *
     * @returns false 表示所选日期已不可选，弹窗清空日期并提示重新选择
     */
    const handleConfirmPublishDate = useCallback(async (selectedDate: Dayjs): Promise<boolean> => {
        if (!warehouseId || !record) return true;

        // 跨时段校验：按点击确认时的仓库时间复核（如 20:58 选当日、21:01 才确认）
        let whsNow = resolveWarehouseNow(warehouseCurrentTime);
        if (fetchWarehouseNow) {
            try {
                whsNow = await fetchWarehouseNow();
            } catch {
                // 降级：用已有仓库时间
            }
        }
        if (!isTaskDateSelectable(selectedDate, whsNow)) {
            return false;
        }
        setPublishTaskDate(selectedDate);
        setConfirmModalVisible(false);

        // 重发布冲突校验（按所选日期）
        const areaIds = record.areaId ? [record.areaId] : [];
        const {data: conflictData} = await checkRepublishConflict({
            variables: {taskDate: selectedDate.format('YYYY-MM-DD'), warehouseId, areaIds},
        });
        const conflictResult = conflictData?.checkRepublishConflict;
        if (conflictResult?.hasConflict && conflictResult.scannedOrders.length > 0) {
            setRepublishConflictOrders(conflictResult.scannedOrders);
            setRepublishConflictVisible(true);
            return true;
        }
        setValidationErrors([]);
        setPublishModalVisible(true);
        return true;
    }, [warehouseId, record, warehouseCurrentTime, fetchWarehouseNow, checkRepublishConflict]);

    /**
     * 开始发布
     */
    const handleStartPublish = useCallback(async () => {
        // 派送日期必须已在确认弹窗中选定
        if (!warehouseId || !record || !publishTaskDate) return;
        const taskDate = publishTaskDate.format('YYYY-MM-DD');

        const dspDrivers = driversByDspId[record.dspId!] ?? [];
        const resolveDriver = (accountId: string | undefined) => {
            if (!accountId) return undefined;
            return dspDrivers.find(d => d.accountId.toString() === accountId);
        };

        const curSegments = splitMode === 'algo'
            ? algorithmSegments.filter(s => s.assignedOrderIds.length > 0) as any[]
            : (splitMode === 'map' ? mapSegments : splitMode === 'zipcode' ? zipcodeSegments : stopSegments) as any[];

        const splitModeToAssignType = () => {
            if (splitMode === 'map') return AssignType.BY_MAP;
            if (splitMode === 'zipcode') return AssignType.BY_ZIPCODE;
            if (splitMode === 'algo') return AssignType.BY_ALGORITHM;
            return AssignType.BY_STOP;
        };
        const assignType = splitModeToAssignType();

        if (curSegments.length === 1) {
            // 单分段（非算法）：直接指派
            const seg = curSegments[0];
            const driver = resolveDriver(seg.driverAccountId)!;
            const allOrderIds = seg.assignedOrderIds.map(Number);
            await assignDrivers({
                variables: {
                    request: {
                        warehouseId,
                        taskDate,
                        taskList: [{
                            dspTaskId: record.dspTaskId ?? undefined,
                            areaId: record.areaId,
                            areaName: record.areaName,
                            split: false,
                            dspId: record.dspId,
                            dspName: record.dspName,
                            assignType: AssignType.DIRECT,
                            driverId: Number(driver.accountId),
                            driverName: driver.name,
                            startAddress: cleanAddress(seg.startAddress),
                            endAddress: cleanAddress(seg.endAddress),
                            parcelCount: allOrderIds.length,
                            orderIds: allOrderIds,
                        }],
                    },
                },
            });
        } else {
            // 多分段 或 算法分单：拆分指派
            const childTaskList: AssignTaskInput[] = curSegments.map((seg: any) => {
                const driver = resolveDriver(seg.driverAccountId)!;
                const allOrderIds = seg.assignedOrderIds.map(Number);
                const child: AssignTaskInput = {
                    areaId: record.areaId,
                    areaName: seg.name,
                    split: false,
                    dspId: record.dspId,
                    dspName: record.dspName,
                    assignType,
                    driverId: Number(driver.accountId),
                    driverName: driver.name,
                    startAddress: cleanAddress(seg.startAddress),
                    endAddress: cleanAddress(seg.endAddress),
                    orderIds: allOrderIds,
                    parcelCount: allOrderIds.length,
                };
                if (splitMode === 'map') {
                    child.assignTypeDetail = seg.polygons.map((poly: any) => poly.map((p: any) => `${p.lat},${p.lng}`).join(';'));
                } else if (splitMode === 'zipcode') {
                    child.assignTypeDetail = seg.zipcodes;
                } else if (splitMode === 'stop') {
                    child.assignTypeDetail = seg.stopRanges.map((r: any) => `${r.parcelNumberStart},${r.parcelNumberEnd}`);
                } else if (splitMode === 'algo') {
                    child.assignTypeDetail = [`${seg.capacityMin ?? 0},${seg.capacityMax ?? 0}`, ...seg.preferredZipcodes];
                    child.algoRecordId = seg.algoRecordId ?? undefined;
                }
                return child;
            });

            await assignDrivers({
                variables: {
                    request: {
                        warehouseId,
                        taskDate,
                        taskList: [{
                            dspTaskId: record.dspTaskId ?? undefined,
                            areaId: record.areaId,
                            areaName: record.areaName,
                            split: true,
                            dspId: record.dspId,
                            dspName: record.dspName,
                            assignType,
                            parcelCount: orderList.length,
                            childTaskList,
                        }],
                    },
                },
            });
        }

        // 发布成功后清除缓存，下次进来读后端最新数据
        if (warehouseId && record?.areaId) {
            deleteSplitRouteCache(warehouseId, Number(record.areaId));
        }
        setPublishModalVisible(false);
        onPublishSuccess?.();
        onClose();
    }, [warehouseId, record, publishTaskDate, splitMode, mapSegments, zipcodeSegments, stopSegments, algorithmSegments, driversByDspId, assignDrivers, onClose, onPublishSuccess, orderList]);

    return (
        <Drawer
            open={visible}
            onClose={onClose}
            placement="right"
            width="100vw"
            closable={false}
            bodyStyle={{padding: 0, display: 'flex', flexDirection: 'column', height: '100%'}}
        >
            {/* ===== 顶部信息栏 ===== */}
            <div className="flex py-2 border-b border-gray-200 bg-white shrink-0">

                {/* 第一块：返回 + 区域名称 */}
                <div className="flex-[3] pl-4 self-start flex items-center gap-3">
                    <Button
                        type="text"
                        onClick={confirmAndClose}
                        style={{ color: '#4f46e5', padding: '0 4px' }}
                        icon={
                            <ArrowLeftOutlined style={{
                                fontSize: 22,
                                color: '#4f46e5',
                                filter: 'drop-shadow(0 0 0.5px #4f46e5) drop-shadow(0 0 0.5px #4f46e5)',
                            }} />
                        }
                    />
                    <div className="border-l-4 border-indigo-600 pl-2">
                        <Text className="text-xl font-bold">
                            {t('splitOrder.drawer.areaSplit', { name: record?.areaName ?? '-' })}
                        </Text>
                    </div>
                </div>

                {/* 第二块：包裹统计*/}
                <div className="flex-[2] flex justify-center items-center">
                    <div className="flex flex-col gap-2">
                        <Text className="text-sm text-gray-600">
                            {t('splitOrder.drawer.areaTotalParcels')}&nbsp;<Text strong className="text-xl">{realTotalParcels}</Text>
                        </Text>
                        <div className="flex gap-6">
                            <Text className="text-sm text-gray-600">{t('splitOrder.drawer.pendingDrivers')}&nbsp;<Text
                                strong>{currentStats.unassigned}</Text></Text>
                            <Text className="text-sm text-gray-600">{t('splitOrder.drawer.assignedDrivers')}&nbsp;<Text
                                strong>{currentStats.assigned}</Text></Text>
                        </div>
                    </div>
                </div>

                {/* 第三块：拆分统计*/}
                <div className="flex-[2] flex flex-col gap-3 justify-center items-center">
                    <div className="flex flex-col gap-2">
                        <Text className="text-sm text-gray-600">
                            {t('splitOrder.drawer.splitSegments')}&nbsp;<Text strong className="text-xl">{currentStats.segments}</Text>
                        </Text>
                        <Text className="text-sm text-gray-600">
                            {t('splitOrder.drawer.assignedDriverCount')}&nbsp;<Text strong>{currentStats.drivers}</Text>
                        </Text>
                    </div>
                </div>

                {/* 第四块：操作按钮*/}
                <div className="flex-[4] flex justify-center items-center gap-12 px-4">
                    <Button onClick={confirmAndClose}>{t('splitOrder.drawer.cancel')}</Button>
                    <Button type="primary" onClick={saveToLocalStorage}>{t('splitOrder.drawer.saveConfig')}</Button>
                    <Button type="primary" onClick={handleDirectPublish}>{t('splitOrder.drawer.directPublish')}</Button>
                </div>
            </div>

            {/* ===== 主体区域 ===== */}
            <div className="flex flex-1 overflow-hidden relative">

                {/* 数据加载中遮罩 */}
                {dataLoading && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/60">
                        <Spin size="large" />
                    </div>
                )}

                {/* 左侧操作面板 */}
                <div className="shrink-0 border-r border-gray-200 overflow-y-auto flex flex-col bg-white" style={{ width: 'auto', minWidth: 340, maxWidth: 420 }}>

                    {/* 拆分方式选择 */}
                    <div className="px-4 pt-4 pb-3 border-b border-gray-100 shrink-0">
                        <div className="text-base font-semibold text-gray-900 mb-3">{t('splitOrder.drawer.selectSplitMode')}</div>
                        <div className="flex">
                            {availableModes.map((mode, idx) => {
                                const labels: Record<SplitMode, string> = {
                                    map: t('splitOrder.drawer.mapSplit'),
                                    zipcode: t('splitOrder.drawer.zipcodeSplit'),
                                    stop: t('splitOrder.drawer.stopSplit'),
                                    algo: t('splitOrder.drawer.algorithmSplit'),
                                };
                                const active = splitMode === mode;
                                const isFirst = idx === 0;
                                const isLast = idx === availableModes.length - 1;
                                return (
                                    <button
                                        key={mode}
                                        onClick={() => { setSplitMode(mode); setHoveredSegId(null); cancelDrawing(); cancelRectSelect(); maybeShowAlgoGuide(mode); }}
                                        className={`relative flex-1 py-1.5 text-sm border transition-colors
                                            ${active
                                            ? 'border-indigo-500 text-indigo-600 font-medium bg-indigo-50'
                                            : 'border-gray-200 text-gray-500 bg-white hover:bg-gray-50'
                                        }
                                            ${isFirst ? 'rounded-l' : ''}
                                            ${isLast ? 'rounded-r' : ''}
                                        `}
                                    >
                                        {labels[mode]}
                                        {/* 算法拆分常显「推荐」角标，引导使用算法分单 */}
                                        {mode === 'algo' && (
                                            <span className="absolute -top-2.5 -right-1 z-10 px-1 rounded text-[10px] leading-4 font-normal text-white bg-red-500 whitespace-nowrap pointer-events-none">
                                                {t('splitOrder.drawer.algorithmRecommended')}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* 视频教程引导条：仅算法分单 / 地图分单展示 */}
                        {showVideoTutorialEntry && (
                            <button
                                type="button"
                                onClick={() => handleOpenVideoTutorial()}
                                className="mt-3 flex items-center gap-2 text-left bg-transparent border-0 p-0 cursor-pointer"
                            >
                                <ReadOutlined style={{color: '#f97316', fontSize: 16}}/>
                                <span className="text-sm font-semibold">
                                    <span style={{color: '#f97316'}}>{t('splitOrder.drawer.videoGuidePrefix')}</span>
                                    <span style={{color: '#4f46e5'}}>{t('splitOrder.drawer.videoGuideLink')}</span>
                                    <span style={{color: '#f97316'}}>{t('splitOrder.drawer.videoGuideSuffix')}</span>
                                </span>
                            </button>
                        )}
                    </div>


                    {/* 分段内容区（随 tab 变化）*/}
                    <div className="flex-1 overflow-y-auto p-4 pb-0 flex flex-col gap-3">
                        {splitMode === 'map' && mapSegments.map((seg) => {
                            const palette = getSegPalette(seg);
                            return (
                                <div key={seg.id} className="rounded-lg py-6 px-4 flex flex-col gap-2.5"
                                     style={{backgroundColor: palette.bg, border: `1px solid ${palette.border}`}}>
                                    {/* 标题行 */}
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-lg font-bold text-gray-800">{seg.name}</span>
                                        <div className="flex items-center gap-6">
                                            <button
                                                className="flex items-center gap-1 text-sm"
                                                style={{color: palette.accent}}
                                                onClick={() => setHoveredSegId(seg.id)}
                                            >
                                                <InboxOutlined/>
                                                <span>{t('splitOrder.drawer.viewParcels')} {seg.assignedOrderIds.length}</span>
                                            </button>
                                            <button
                                                onClick={() => removeSegment(seg.id)}
                                                className="flex items-center gap-1 text-sm text-red-400 hover:text-red-600"
                                            >
                                                <DeleteOutlined/>
                                                <span>{t('splitOrder.drawer.deleteSegment')}</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* 选择司机 */}
                                    <div className="flex items-center gap-2 mb-2">
                                    <span className="text-sm text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                        <span className="text-red-500">*&nbsp;</span>{t('splitOrder.drawer.selectDriver')}
                                    </span>
                                        <Select
                                            showSearch
                                            placeholder={t('splitOrder.drawer.selectDriverPlaceholder')}
                                            value={seg.driverAccountId || undefined}
                                            onChange={val => {
                                                updateMapSegment(seg.id, {driverAccountId: val});
                                                fetchAndFillDriverStartEnd(val, 'map', seg.id);
                                            }}
                                            style={{flex: 1, minWidth: 0, height: 34}}
                                            popupMatchSelectWidth={false}
                                            dropdownStyle={{minWidth: 420}}
                                            options={driverOptions}
                                            filterOption={driverFilterOption}
                                        />
                                    </div>

                                    {/* 圈选区域 */}
                                    <div className="flex items-center text-sm mb-2 overflow-hidden">
                                    <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap mr-3">
                                        <span className="text-red-500">*&nbsp;</span>{t('splitOrder.drawer.drawArea')}
                                    </span>
                                        <div className="flex items-center gap-1 mr-3 font-semibold min-w-0">
                                            <span style={{color: palette.accent}}> {t('splitOrder.drawer.areaLabel')}</span>
                                            <div className="w-3.5 h-3.5 rounded-sm shrink-0"
                                                 style={{backgroundColor: palette.accent}}/>
                                            {seg.parcelNumberStart != null && seg.parcelNumberEnd != null && (
                                                <span style={{color: palette.accent}}>
                                                    &nbsp;&nbsp;STOP：{seg.parcelNumberStart}-{seg.parcelNumberEnd}
                                                </span>
                                            )}
                                        </div>

                                        <button
                                            onClick={() => startEditSegment(seg.id)}
                                            disabled={!!editingSegId || isDrawing || !seg.polygons.some(p => p.length >= 3)}
                                            className="ml-auto flex items-center gap-1 font-medium disabled:opacity-40"
                                            style={{color: palette.accent}}
                                        >
                                            <EditOutlined/>
                                            {t('splitOrder.drawer.editArea')}
                                        </button>
                                    </div>

                                    {/* 起点 */}
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.drawer.startPoint')}</span>
                                        <Input size="small" readOnly placeholder={t('splitOrder.drawer.clickInputAddress') ?? ''}
                                               value={formatAddress(seg.startAddress)}
                                               style={{flex: 1, minWidth: 0, height: 30, cursor: 'pointer'}}
                                               onClick={() => setAddressModalTarget({
                                                   segId: seg.id,
                                                   field: 'start',
                                                   segType: 'map'
                                               })}/>
                                        <Checkbox
                                            checked={isWhsAddress(seg.startAddress)}
                                            onChange={e => {
                                                updateMapSegment(seg.id, {startAddress: e.target.checked ? whsDriverAddress : null});
                                            }}>
                                            <span className="text-gray-700 font-medium">WHS</span>
                                        </Checkbox>
                                        <button className="flex items-center gap-0.5 shrink-0"
                                                style={{color: palette.accent}} onClick={() => setPinningTarget({
                                            segId: seg.id,
                                            field: 'start',
                                            segType: 'map'
                                        })}>
                                            <EnvironmentOutlined/><span>{t('splitOrder.drawer.mapPin')}</span>
                                        </button>
                                    </div>

                                    {/* 终点 */}
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.drawer.endPoint')}</span>
                                        <Input size="small" readOnly placeholder={t('splitOrder.drawer.clickInputAddress') ?? ''}
                                               value={formatAddress(seg.endAddress)}
                                               style={{flex: 1, minWidth: 0, height: 30, cursor: 'pointer'}}
                                               onClick={() => setAddressModalTarget({
                                                   segId: seg.id,
                                                   field: 'end',
                                                   segType: 'map'
                                               })}/>
                                        <Checkbox
                                            checked={isWhsAddress(seg.endAddress)}
                                            onChange={e => {
                                                updateMapSegment(seg.id, {endAddress: e.target.checked ? whsDriverAddress : null});
                                            }}>
                                            <span className="text-gray-700 font-medium">WHS</span>
                                        </Checkbox>
                                        <button className="flex items-center gap-0.5 shrink-0"
                                                style={{color: palette.accent}} onClick={() => setPinningTarget({
                                                segId: seg.id,
                                                field: 'end',
                                                segType: 'map'
                                        })}>
                                            <EnvironmentOutlined/><span>{t('splitOrder.drawer.mapPin')}</span>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                        {splitMode === 'zipcode' && ((): React.ReactNode => {
                            const usedZipcodeDriverIds = new Set(
                                zipcodeSegments.filter(s => s.driverAccountId).map(s => s.driverAccountId)
                            );
                            return zipcodeSegments.map((seg) => {
                                const palette = getSegPalette(seg);
                                const childTask = taskDetail?.childTaskList?.find(c => String(c.id) === seg.id);
                                const zipcodeDriverOptions = driverOptions.map(opt => {
                                    const isUsedByOther = usedZipcodeDriverIds.has(opt.value) && opt.value !== seg.driverAccountId;
                                    return {
                                        ...opt,
                                        disabled: opt.disabled || isUsedByOther,
                                        label: isUsedByOther ? (
                                            <span><span style={{
                                                fontSize: 12,
                                                color: '#999',
                                                marginRight: 8
                                            }}>{t('splitOrder.common.assigned')}</span>{opt.label}</span>
                                        ) : opt.label,
                                    };
                                });
                                return (
                                    <div key={seg.id} className="rounded-lg py-6 px-4 flex flex-col gap-2.5"
                                         style={{backgroundColor: palette.bg, border: `1px solid ${palette.border}`}}>
                                        {/* 标题行 */}
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-lg font-bold text-gray-800">{seg.name}</span>
                                            <div className="flex items-center gap-6">
                                                <button
                                                    className="flex items-center gap-1 text-sm"
                                                    style={{color: palette.accent}}
                                                    onClick={() => setHoveredSegId(seg.id)}
                                                >
                                                    <InboxOutlined/>
                                                    <span>{t('splitOrder.drawer.viewParcels')} {seg.assignedOrderIds.length}</span>
                                                </button>
                                                <button
                                                    onClick={() => removeSegment(seg.id)}
                                                    className="flex items-center gap-1 text-sm text-red-400 hover:text-red-600"
                                                >
                                                    <DeleteOutlined/>
                                                    <span>{t('splitOrder.drawer.deleteSegment')}</span>
                                                </button>
                                            </div>
                                        </div>

                                        {/* 选择司机 */}
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-sm text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                                <span className="text-red-500">*&nbsp;</span>{t('splitOrder.drawer.selectDriver')}
                                            </span>
                                            <Select
                                                showSearch
                                                placeholder={t('splitOrder.drawer.selectDriverPlaceholder')}
                                                value={seg.driverAccountId || undefined}
                                                onChange={val => {
                                                    updateZipcodeSegment(seg.id, {driverAccountId: val});
                                                    fetchAndFillDriverStartEnd(val, 'zipcode', seg.id);
                                                }}
                                                style={{flex: 1, minWidth: 0, height: 34}}
                                                popupMatchSelectWidth={false}
                                                dropdownStyle={{minWidth: 420}}
                                                options={zipcodeDriverOptions}
                                                filterOption={driverFilterOption}
                                            />
                                        </div>

                                        {/* 分配邮编 */}
                                        <div className="flex items-center gap-4 mb-2 overflow-hidden">
                                            <span className="text-sm text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                                <span className="text-red-500">*&nbsp;</span>{t('splitOrder.drawer.assignZipcode')}
                                            </span>
                                            {(() => {
                                                const usedZipcodes = new Set(
                                                    zipcodeSegments.filter(s => s.id !== seg.id).flatMap(s => s.zipcodes)
                                                );
                                                const opts = zipcodeOptions.map(opt => {
                                                    const isUsed = usedZipcodes.has(opt.value);
                                                    return {
                                                        ...opt,
                                                        disabled: isUsed,
                                                        label: isUsed
                                                            ? <span><span style={{
                                                                fontSize: 12,
                                                                color: '#999',
                                                                marginRight: 8
                                                            }}>{t('splitOrder.common.assigned')}</span>{opt.label}</span>
                                                            : opt.label,
                                                    };
                                                });
                                                return (
                                                    <Select
                                                        mode="multiple"
                                                        showSearch
                                                        placeholder={t('splitOrder.drawer.selectZipcodePlaceholder')}
                                                        value={seg.zipcodes}
                                                        onChange={(val: string[]) => {
                                                            const added = val.filter(z => !seg.zipcodes.includes(z));
                                                            updateZipcodeSegment(seg.id, {zipcodes: val});
                                                            if (added.length > 0) flashZipcodes(added);
                                                        }}
                                                        style={{flex: 1, minWidth: 0}}
                                                        options={opts}
                                                        filterOption={(input, opt) => String(opt?.value ?? '').includes(input)}
                                                    />
                                                );
                                            })()}
                                            {(() => {
                                                const stopRange = childTask?.parcelNumberStart != null && childTask?.parcelNumberEnd != null
                                                    ? `${childTask.parcelNumberStart}-${childTask.parcelNumberEnd}`
                                                    : '—';
                                                return <span className="text-xs font-medium shrink-0"
                                                             style={{color: palette.accent}}>STOP: {stopRange}</span>;
                                            })()}
                                        </div>

                                        {/* 起点 */}
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.drawer.startPoint')}</span>
                                            <Input size="small" readOnly placeholder={t('splitOrder.drawer.clickInputAddress') ?? ''}
                                                   value={formatAddress(seg.startAddress)}
                                                   style={{flex: 1, minWidth: 0, height: 30, cursor: 'pointer'}}
                                                   onClick={() => setAddressModalTarget({
                                                       segId: seg.id,
                                                       field: 'start',
                                                       segType: 'zipcode'
                                                   })}/>
                                            <Checkbox
                                                checked={isWhsAddress(seg.startAddress)}
                                                onChange={e => {
                                                    updateZipcodeSegment(seg.id, {startAddress: e.target.checked ? whsDriverAddress : null});
                                                }}>
                                                <span className="text-gray-700 font-medium">WHS</span>
                                            </Checkbox>
                                            <button className="flex items-center gap-0.5 shrink-0"
                                                    style={{color: palette.accent}} onClick={() => setPinningTarget({
                                                segId: seg.id,
                                                field: 'start',
                                                segType: 'zipcode'
                                            })}>
                                                <EnvironmentOutlined/><span>{t('splitOrder.drawer.mapPin')}</span>
                                            </button>
                                        </div>

                                        {/* 终点 */}
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.drawer.endPoint')}</span>
                                            <Input size="small" readOnly placeholder={t('splitOrder.drawer.clickInputAddress') ?? ''}
                                                   value={formatAddress(seg.endAddress)}
                                                   style={{flex: 1, minWidth: 0, height: 30, cursor: 'pointer'}}
                                                   onClick={() => setAddressModalTarget({
                                                       segId: seg.id,
                                                       field: 'end',
                                                       segType: 'zipcode'
                                                   })}/>
                                            <Checkbox
                                                checked={isWhsAddress(seg.endAddress)}
                                                onChange={e => {
                                                    updateZipcodeSegment(seg.id, {endAddress: e.target.checked ? whsDriverAddress : null});
                                                }}>
                                                <span className="text-gray-700 font-medium">WHS</span>
                                            </Checkbox>
                                            <button className="flex items-center gap-0.5 shrink-0"
                                                    style={{color: palette.accent}} onClick={() => setPinningTarget({
                                                segId: seg.id,
                                                field: 'end',
                                                segType: 'zipcode'
                                            })}>
                                                <EnvironmentOutlined/><span>{t('splitOrder.drawer.mapPin')}</span>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        })()}
                        {splitMode === 'stop' && ((): React.ReactNode => {
                            const usedStopDriverIds = new Set(
                                stopSegments.filter(s => s.driverAccountId).map(s => s.driverAccountId)
                            );
                            return stopSegments.map((seg) => {
                                const palette = getSegPalette(seg);
                                const stopDriverOptions = driverOptions.map(opt => {
                                    const isUsedByOther = usedStopDriverIds.has(opt.value) && opt.value !== seg.driverAccountId;
                                    return {
                                        ...opt,
                                        disabled: opt.disabled || isUsedByOther,
                                        label: isUsedByOther ? (
                                            <span><span style={{
                                                fontSize: 12,
                                                color: '#999',
                                                marginRight: 8
                                            }}>{t('splitOrder.common.assigned')}</span>{opt.label}</span>
                                        ) : opt.label,
                                    };
                                });
                                return (
                                    <div key={seg.id} className="rounded-lg py-6 px-4 flex flex-col gap-2.5"
                                         style={{backgroundColor: palette.bg, border: `1px solid ${palette.border}`}}>
                                        {/* 标题行 */}
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-lg font-bold text-gray-800">{seg.name}</span>
                                            <div className="flex items-center gap-6">
                                                <button
                                                    className="flex items-center gap-1 text-sm"
                                                    style={{color: palette.accent}}
                                                    onClick={() => setHoveredSegId(seg.id)}
                                                >
                                                    <InboxOutlined/>
                                                    <span>{t('splitOrder.drawer.viewParcels')} {seg.assignedOrderIds.length}</span>
                                                </button>
                                                <button
                                                    onClick={() => removeSegment(seg.id)}
                                                    className="flex items-center gap-1 text-sm text-red-400 hover:text-red-600"
                                                >
                                                    <DeleteOutlined/>
                                                    <span>{t('splitOrder.drawer.deleteSegment')}</span>
                                                </button>
                                            </div>
                                        </div>

                                        {/* 选择司机 */}
                                        <div className="flex items-center gap-2 mb-2">
                                        <span className="text-sm text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                        <span className="text-red-500">*&nbsp;</span>{t('splitOrder.drawer.selectDriver')}
                                    </span>
                                            <Select
                                                showSearch
                                                placeholder={t('splitOrder.drawer.selectDriverPlaceholder')}
                                                value={seg.driverAccountId || undefined}
                                                onChange={val => {
                                                    updateStopSegment(seg.id, {driverAccountId: val});
                                                    fetchAndFillDriverStartEnd(val, 'stop', seg.id);
                                                }}
                                                style={{flex: 1, minWidth: 0, height: 34}}
                                                popupMatchSelectWidth={false}
                                                dropdownStyle={{minWidth: 420}}
                                                options={stopDriverOptions}
                                                filterOption={driverFilterOption}
                                            />
                                        </div>

                                        {/* 开始-结束序号 */}
                                        {seg.stopRanges.map((range, idx) => (
                                            <div key={range.id} className="flex items-center gap-2 text-sm">
                                                {idx === 0 ? (
                                                    <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                                <span className="text-red-500">*&nbsp;</span>{t('splitOrder.drawer.startEndNumber')}
                                            </span>
                                                ) : (
                                                    <span className="text-gray-700 font-medium shrink-0 invisible">
                                                <span className="text-red-500">*&nbsp;</span>{t('splitOrder.drawer.startEndNumber')}
                                            </span>
                                                )}
                                                <Input
                                                    size="small"
                                                    value={range.parcelNumberStart}
                                                    onChange={e => {
                                                        const v = e.target.value.replace(/[^\d]/g, '');
                                                        updateStopRange(seg.id, range.id, {parcelNumberStart: v === '0' ? '' : v});
                                                    }}
                                                    style={{width: 80, height: 30}}
                                                />
                                                <span className="text-gray-400">-</span>
                                                <Input
                                                    size="small"
                                                    value={range.parcelNumberEnd}
                                                    onChange={e => {
                                                        const v = e.target.value.replace(/[^\d]/g, '');
                                                        updateStopRange(seg.id, range.id, {parcelNumberEnd: v === '0' ? '' : v});
                                                    }}
                                                    style={{width: 80, height: 30}}
                                                />
                                                {seg.stopRanges.length > 1 && (
                                                    <button
                                                        onClick={() => removeStopRange(seg.id, range.id)}
                                                        className="text-red-400 hover:text-red-600 shrink-0"
                                                    >
                                                        <DeleteOutlined style={{fontSize: 12}}/>
                                                    </button>
                                                )}
                                            </div>
                                        ))}

                                        {/* 卡片内添加分段 */}
                                        <div className="flex justify-center mt-1">
                                            <button
                                                onClick={() => addStopRange(seg.id)}
                                                className="flex items-center gap-1 text-sm"
                                                style={{color: palette.accent}}
                                            >
                                                <PlusCircleOutlined/>
                                                <span>{t('splitOrder.drawer.addRange')}</span>
                                            </button>
                                        </div>

                                        {/* 起点 */}
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.drawer.startPoint')}</span>
                                            <Input size="small" readOnly placeholder={t('splitOrder.drawer.clickInputAddress') ?? ''}
                                                   value={formatAddress(seg.startAddress)}
                                                   style={{flex: 1, minWidth: 0, height: 30, cursor: 'pointer'}}
                                                   onClick={() => setAddressModalTarget({
                                                       segId: seg.id,
                                                       field: 'start',
                                                       segType: 'stop'
                                                   })}/>
                                            <Checkbox
                                                checked={isWhsAddress(seg.startAddress)}
                                                onChange={e => {
                                                    updateStopSegment(seg.id, {startAddress: e.target.checked ? whsDriverAddress : null});
                                                }}>
                                                <span className="text-gray-700 font-medium">WHS</span>
                                            </Checkbox>
                                            <button className="flex items-center gap-0.5 shrink-0"
                                                    style={{color: palette.accent}} onClick={() => setPinningTarget({
                                                segId: seg.id,
                                                field: 'start',
                                                segType: 'stop'
                                            })}>
                                                <EnvironmentOutlined/><span>{t('splitOrder.drawer.mapPin')}</span>
                                            </button>
                                        </div>

                                        {/* 终点 */}
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.drawer.endPoint')}</span>
                                            <Input size="small" readOnly placeholder={t('splitOrder.drawer.clickInputAddress') ?? ''}
                                                   value={formatAddress(seg.endAddress)}
                                                   style={{flex: 1, minWidth: 0, height: 30, cursor: 'pointer'}}
                                                   onClick={() => setAddressModalTarget({
                                                       segId: seg.id,
                                                       field: 'end',
                                                       segType: 'stop'
                                                   })}/>
                                            <Checkbox
                                                checked={isWhsAddress(seg.endAddress)}
                                                onChange={e => {
                                                    updateStopSegment(seg.id, {endAddress: e.target.checked ? whsDriverAddress : null});
                                                }}>
                                                <span className="text-gray-700 font-medium">WHS</span>
                                            </Checkbox>
                                            <button className="flex items-center gap-0.5 shrink-0"
                                                    style={{color: palette.accent}} onClick={() => setPinningTarget({
                                                segId: seg.id,
                                                field: 'end',
                                                segType: 'stop'
                                            })}>
                                                <EnvironmentOutlined/><span>{t('splitOrder.drawer.mapPin')}</span>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        })()}
                        {/* ===== 算法分单分段卡片 ===== */}
                        {splitMode === 'algo' && ((): React.ReactNode => {
                            const usedAlgoDriverIds = new Set(
                                algorithmSegments.filter(s => s.driverAccountId).map(s => s.driverAccountId)
                            );
                            return algorithmSegments.map((seg) => {
                                const palette = getSegPalette(seg);
                                const algoDriverOptions = driverOptions.map(opt => {
                                    const isUsedByOther = usedAlgoDriverIds.has(opt.value) && opt.value !== seg.driverAccountId;
                                    return {
                                        ...opt,
                                        disabled: opt.disabled || isUsedByOther,
                                        label: isUsedByOther ? (
                                            <span><span style={{fontSize: 12, color: '#999', marginRight: 8}}>{t('splitOrder.common.assigned')}</span>{opt.label}</span>
                                        ) : opt.label,
                                    };
                                });

                                // ===== 查看态（clustered / routePlanned） =====
                                if (isAlgoViewMode || algoPhase === 'routePlanning') {
                                    const activeCount = algoActiveIdsMap.get(seg.id)?.length ?? 0;
                                    const stopRange = seg.parcelNumberStart != null && seg.parcelNumberEnd != null
                                        ? `${seg.parcelNumberStart}-${seg.parcelNumberEnd}（${activeCount}${t('splitOrder.drawer.algoParcelUnit')}）`
                                        : '—';
                                    const zipcodeText = seg.preferredZipcodes.length > 0
                                        ? seg.preferredZipcodes.join(', ')
                                        : '—';
                                    return (
                                        <div key={seg.id} className="rounded-lg py-6 px-4 flex flex-col gap-2.5"
                                             style={{backgroundColor: palette.bg, border: `1px solid ${palette.border}`}}>
                                            {/* 标题行 */}
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-lg font-bold text-gray-800">{seg.name}</span>
                                                <button
                                                    className="flex items-center gap-1 text-sm"
                                                    style={{color: palette.accent}}
                                                    onClick={() => setHoveredSegId(seg.id)}
                                                >
                                                    <InboxOutlined/>
                                                    <span>{t('splitOrder.drawer.viewParcels')} {activeCount}</span>
                                                </button>
                                            </div>

                                            {/* 选择司机（可编辑） */}
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-sm text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                                    <span className="text-red-500">*&nbsp;</span>{t('splitOrder.drawer.selectDriver')}
                                                </span>
                                                <Select
                                                    showSearch
                                                    placeholder={t('splitOrder.drawer.selectDriverPlaceholder')}
                                                    value={seg.driverAccountId || undefined}
                                                    onChange={val => {
                                                        updateAlgorithmSegment(seg.id, {driverAccountId: val});
                                                        fetchAndFillDriverStartEnd(val, 'algo', seg.id);
                                                        if (seg.capacityMin == null && seg.capacityMax == null) {
                                                            fillDriverCapacity(val, seg.id);
                                                        }
                                                    }}
                                                    style={{flex: 1, minWidth: 0, height: 34}}
                                                    popupMatchSelectWidth={false}
                                                    dropdownStyle={{minWidth: 420}}
                                                    options={algoDriverOptions}
                                                    filterOption={driverFilterOption}
                                                />
                                            </div>

                                            {/* 开始-结束序号（纯文本） */}
                                            <div className="flex items-center gap-2 text-sm mb-1">
                                                <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                                    <span className="text-red-500">*&nbsp;</span>{t('splitOrder.drawer.algoStopRange')}
                                                </span>
                                                <span className="text-gray-900">{stopRange}</span>
                                            </div>

                                            {/* 期望邮编（纯文本） */}
                                            <div className="flex items-center gap-2 text-sm mb-1">
                                                <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                                    {t('splitOrder.drawer.preferredArea')}
                                                </span>
                                                <span className="text-gray-900">{zipcodeText}</span>
                                            </div>

                                            {/* 起点（可编辑） */}
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.drawer.startPoint')}</span>
                                                <Input size="small" readOnly placeholder={t('splitOrder.drawer.clickInputAddress') ?? ''}
                                                       value={formatAddress(seg.startAddress)}
                                                       style={{flex: 1, minWidth: 0, height: 30, cursor: 'pointer'}}
                                                       onClick={() => setAddressModalTarget({segId: seg.id, field: 'start', segType: 'algo'})}/>
                                                <Checkbox checked={isWhsAddress(seg.startAddress)}
                                                    onChange={e => updateAlgorithmSegment(seg.id, {startAddress: e.target.checked ? whsDriverAddress : null})}>
                                                    <span className="text-gray-700 font-medium">WHS</span>
                                                </Checkbox>
                                                <button className="flex items-center gap-0.5 shrink-0" style={{color: palette.accent}}
                                                    onClick={() => setPinningTarget({segId: seg.id, field: 'start', segType: 'algo'})}>
                                                    <EnvironmentOutlined/><span>{t('splitOrder.drawer.mapPin')}</span>
                                                </button>
                                            </div>

                                            {/* 终点（可编辑） */}
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.drawer.endPoint')}</span>
                                                <Input size="small" readOnly placeholder={t('splitOrder.drawer.clickInputAddress') ?? ''}
                                                       value={formatAddress(seg.endAddress)}
                                                       style={{flex: 1, minWidth: 0, height: 30, cursor: 'pointer'}}
                                                       onClick={() => setAddressModalTarget({segId: seg.id, field: 'end', segType: 'algo'})}/>
                                                <Checkbox checked={isWhsAddress(seg.endAddress)}
                                                    onChange={e => updateAlgorithmSegment(seg.id, {endAddress: e.target.checked ? whsDriverAddress : null})}>
                                                    <span className="text-gray-700 font-medium">WHS</span>
                                                </Checkbox>
                                                <button className="flex items-center gap-0.5 shrink-0" style={{color: palette.accent}}
                                                    onClick={() => setPinningTarget({segId: seg.id, field: 'end', segType: 'algo'})}>
                                                    <EnvironmentOutlined/><span>{t('splitOrder.drawer.mapPin')}</span>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                }

                                // ===== 编辑态（editing / rePlanning / clustering） =====
                                return (
                                <div key={seg.id} className="rounded-lg py-6 px-4 flex flex-col gap-2.5"
                                     style={{backgroundColor: palette.bg, border: `1px solid ${palette.border}`, opacity: algoPhase === 'clustering' ? 0.5 : 1}}>
                                    {/* 标题行 */}
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-lg font-bold text-gray-800">{seg.name}</span>
                                        <button
                                            onClick={() => removeSegment(seg.id)}
                                            disabled={algoPhase === 'clustering'}
                                            className="flex items-center gap-1 text-sm text-red-400 hover:text-red-600 disabled:opacity-40"
                                        >
                                            <DeleteOutlined/>
                                            <span>{t('splitOrder.drawer.deleteSegment')}</span>
                                        </button>
                                    </div>

                                    {/* 选择司机 */}
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-sm text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                            <span className="text-red-500">*&nbsp;</span>{t('splitOrder.drawer.selectDriver')}
                                        </span>
                                        <Select
                                            showSearch
                                            disabled={algoPhase === 'clustering'}
                                            placeholder={t('splitOrder.drawer.selectDriverPlaceholder')}
                                            value={seg.driverAccountId || undefined}
                                            onChange={val => {
                                                updateAlgorithmSegment(seg.id, {driverAccountId: val});
                                                fetchAndFillDriverStartEnd(val, 'algo', seg.id);
                                                if (seg.capacityMin == null && seg.capacityMax == null) {
                                                    fillDriverCapacity(val, seg.id);
                                                }
                                            }}
                                            style={{flex: 1, minWidth: 0, height: 34}}
                                            popupMatchSelectWidth={false}
                                            dropdownStyle={{minWidth: 420}}
                                            options={algoDriverOptions}
                                            filterOption={driverFilterOption}
                                        />
                                    </div>

                                    {/* 司机配送能力 */}
                                    {(() => {
                                        const minErr = seg.capacityMin != null && seg.capacityMin < 20
                                            ? t('splitOrder.drawer.algoCapacityMinTooLow', {segments: seg.name})
                                            : seg.capacityMin != null && seg.capacityMin > realTotalParcels
                                            ? t('splitOrder.drawer.algoCapacityMaxTooHigh', {segments: seg.name, total: realTotalParcels})
                                            : null;
                                        const maxErr = seg.capacityMax != null && seg.capacityMin != null && seg.capacityMax < seg.capacityMin
                                            ? t('splitOrder.drawer.algoCapacityInverted', {segments: seg.name})
                                            : seg.capacityMax != null && seg.capacityMax > realTotalParcels
                                            ? t('splitOrder.drawer.algoCapacityMaxTooHigh', {segments: seg.name, total: realTotalParcels})
                                            : null;
                                        const hasError = !!minErr || !!maxErr;
                                        return (
                                            <>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-sm text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                                        <span className="text-red-500">*&nbsp;</span>{t('splitOrder.drawer.driverCapacity')}
                                                    </span>
                                                    <Input
                                                        size="small"
                                                        disabled={algoPhase === 'clustering'}
                                                        placeholder={t('splitOrder.drawer.capacityMin') ?? ''}
                                                        value={seg.capacityMin != null ? String(seg.capacityMin) : ''}
                                                        onChange={e => {
                                                            const v = e.target.value.replace(/[^\d]/g, '');
                                                            updateAlgorithmSegment(seg.id, {capacityMin: v ? Number(v) : null});
                                                        }}
                                                        onBlur={() => {
                                                            if (minErr) updateAlgorithmSegment(seg.id, {capacityMin: null});
                                                        }}
                                                        status={minErr ? 'error' : undefined}
                                                        style={{width: 110, height: 30}}
                                                    />
                                                    <span className="text-gray-400">~</span>
                                                    <Input
                                                        size="small"
                                                        disabled={algoPhase === 'clustering'}
                                                        placeholder={t('splitOrder.drawer.capacityMax') ?? ''}
                                                        value={seg.capacityMax != null ? String(seg.capacityMax) : ''}
                                                        onChange={e => {
                                                            const v = e.target.value.replace(/[^\d]/g, '');
                                                            updateAlgorithmSegment(seg.id, {capacityMax: v ? Number(v) : null});
                                                        }}
                                                        onBlur={() => {
                                                            if (maxErr) updateAlgorithmSegment(seg.id, {capacityMax: null});
                                                        }}
                                                        status={maxErr ? 'error' : undefined}
                                                        style={{width: 110, height: 30}}
                                                    />
                                                </div>
                                                {hasError && (
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-sm shrink-0 whitespace-nowrap" style={{visibility: 'hidden'}}>
                                                            <span>*&nbsp;</span>{t('splitOrder.drawer.driverCapacity')}
                                                        </span>
                                                        <span className="text-xs text-red-500">{minErr || maxErr}</span>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                    <div className="text-xs text-red-500 mb-2 ml-1" style={{textDecoration: 'underline'}}>
                                        {t('splitOrder.drawer.capacityHint')}
                                    </div>

                                    {/* 期望配送区域 */}
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-sm text-gray-700 font-medium shrink-0 whitespace-nowrap">
                                            {t('splitOrder.drawer.preferredArea')}
                                        </span>
                                        <Select
                                            mode="multiple"
                                            showSearch
                                            disabled={algoPhase === 'clustering'}
                                            placeholder={t('splitOrder.drawer.selectZipcodePlaceholder')}
                                            value={seg.preferredZipcodes}
                                            onChange={(val: string[]) => {
                                                const added = val.filter(z => !seg.preferredZipcodes.includes(z));
                                                updateAlgorithmSegment(seg.id, {preferredZipcodes: val});
                                                if (added.length > 0) flashZipcodes(added);
                                            }}
                                            style={{flex: 1, minWidth: 0}}
                                            options={zipcodeOptions}
                                            filterOption={(input, opt) => String(opt?.value ?? '').includes(input)}
                                        />
                                    </div>

                                    {/* 起点 */}
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.drawer.startPoint')}</span>
                                        <Input size="small" readOnly disabled={algoPhase === 'clustering'}
                                               placeholder={t('splitOrder.drawer.clickInputAddress') ?? ''}
                                               value={formatAddress(seg.startAddress)}
                                               style={{flex: 1, minWidth: 0, height: 30, cursor: 'pointer'}}
                                               onClick={() => algoPhase !== 'clustering' && setAddressModalTarget({segId: seg.id, field: 'start', segType: 'algo'})}/>
                                        <Checkbox disabled={algoPhase === 'clustering'} checked={isWhsAddress(seg.startAddress)}
                                            onChange={e => updateAlgorithmSegment(seg.id, {startAddress: e.target.checked ? whsDriverAddress : null})}>
                                            <span className="text-gray-700 font-medium">WHS</span>
                                        </Checkbox>
                                        <button className="flex items-center gap-0.5 shrink-0" style={{color: palette.accent}}
                                            disabled={algoPhase === 'clustering'}
                                            onClick={() => setPinningTarget({segId: seg.id, field: 'start', segType: 'algo'})}>
                                            <EnvironmentOutlined/><span>{t('splitOrder.drawer.mapPin')}</span>
                                        </button>
                                    </div>

                                    {/* 终点 */}
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="text-gray-700 font-medium shrink-0 whitespace-nowrap ml-1">{t('splitOrder.drawer.endPoint')}</span>
                                        <Input size="small" readOnly disabled={algoPhase === 'clustering'}
                                               placeholder={t('splitOrder.drawer.clickInputAddress') ?? ''}
                                               value={formatAddress(seg.endAddress)}
                                               style={{flex: 1, minWidth: 0, height: 30, cursor: 'pointer'}}
                                               onClick={() => algoPhase !== 'clustering' && setAddressModalTarget({segId: seg.id, field: 'end', segType: 'algo'})}/>
                                        <Checkbox disabled={algoPhase === 'clustering'} checked={isWhsAddress(seg.endAddress)}
                                            onChange={e => updateAlgorithmSegment(seg.id, {endAddress: e.target.checked ? whsDriverAddress : null})}>
                                            <span className="text-gray-700 font-medium">WHS</span>
                                        </Checkbox>
                                        <button className="flex items-center gap-0.5 shrink-0" style={{color: palette.accent}}
                                            disabled={algoPhase === 'clustering'}
                                            onClick={() => setPinningTarget({segId: seg.id, field: 'end', segType: 'algo'})}>
                                            <EnvironmentOutlined/><span>{t('splitOrder.drawer.mapPin')}</span>
                                        </button>
                                    </div>
                                </div>
                            );
                            })
                        })()}

                        {/* 底部按钮区 */}
                        <div
                            className="sticky bottom-0 z-10 py-2 pb-4 flex justify-around flex-wrap bg-white border-t border-gray-100 mt-1"
                            style={{ fontSize: 13 }}>
                            {/* 编辑态：添加分段 + 规划区域 */}
                            {(splitMode !== 'algo' || isAlgoEditing || algoPhase === 'clustering') && (
                                <>
                                    <Button
                                        onClick={addSegment}
                                        disabled={(splitMode === 'map' && isDrawing) || algoPhase === 'clustering'}
                                        style={{
                                            color: '#6366f1',
                                            borderColor: '#6366f1',
                                            backgroundColor: '#fff',
                                            borderWidth: 1.5,
                                            paddingInline: 14,
                                            fontSize: 13,
                                        }}
                                    >
                                        {t('splitOrder.drawer.addSegment')}
                                    </Button>
                                    {splitMode === 'algo' && (
                                        <Button
                                            type="primary"
                                            onClick={handleStartClustering}
                                            loading={algoPhase === 'clustering'}
                                            disabled={algoPhase === 'clustering'}
                                            style={{
                                                backgroundColor: '#4f46e5',
                                                borderColor: '#4f46e5',
                                                paddingInline: 14,
                                                fontSize: 13,
                                            }}
                                        >
                                            {t('splitOrder.drawer.algoPlanArea')}
                                        </Button>
                                    )}
                                    {splitMode === 'algo' && algoPhase === 'reEdit' && (
                                        <Button
                                            onClick={() => {
                                                if (algoSnapshotRef.current) {
                                                    setAlgorithmSegments(algoSnapshotRef.current.segments);
                                                    setAlgoPhase(algoSnapshotRef.current.phase);
                                                    setAlgoStopMap(algoSnapshotRef.current.stopMap);
                                                    algoSnapshotRef.current = null;
                                                } else {
                                                    setAlgoPhase('clustered');
                                                }
                                            }}
                                            style={{paddingInline: 14, fontSize: 13}}
                                        >
                                            {t('splitOrder.drawer.algoExitEdit')}
                                        </Button>
                                    )}
                                </>
                            )}
                            {/* 查看态：重新规划（仅 clustered）+ 手动调整包裹 */}
                            {splitMode === 'algo' && (isAlgoViewMode || algoPhase === 'routePlanning') && (
                                <>
                                    {(algoPhase === 'clustered' || algoPhase === 'routePlanned' || algoPhase === 'published') && (
                                        <Button
                                            onClick={() => {
                                                algoSnapshotRef.current = {segments: [...algorithmSegments], phase: algoPhase, stopMap: new Map(algoStopMap)};
                                                setAlgoPhase('reEdit');
                                                cancelRectSelect();
                                            }}
                                            style={{
                                                color: '#6366f1',
                                                borderColor: '#6366f1',
                                                backgroundColor: '#fff',
                                                borderWidth: 1.5,
                                                paddingInline: 14,
                                                fontSize: 13,
                                            }}
                                        >
                                            {t('splitOrder.drawer.algoReplan')}
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() => {
                                            // routePlanned/published 时先回退到 clustered，清掉 stop + algoRecordId
                                            if (algoPhase === 'routePlanned' || algoPhase === 'published') {
                                                setAlgoPhase('clustered');
                                                setAlgoStopMap(new Map());
                                                setAlgorithmSegments(prev => prev.map(s => ({...s, algoRecordId: null})));
                                            }
                                            setIsRectSelecting(true);
                                        }}
                                        disabled={algoPhase === 'routePlanning'}
                                        style={{
                                            color: '#6366f1',
                                            borderColor: '#6366f1',
                                            backgroundColor: '#fff',
                                            borderWidth: 1.5,
                                            paddingInline: 14,
                                            fontSize: 13,
                                        }}
                                    >
                                        {t('splitOrder.drawer.algoManualAdjust')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        onClick={handleStartRoutePlanning}
                                        loading={algoPhase === 'routePlanning'}
                                        disabled={algoPhase === 'routePlanning'}
                                        style={{
                                            backgroundColor: '#4f46e5',
                                            borderColor: '#4f46e5',
                                            paddingInline: 14,
                                            fontSize: 13,
                                        }}
                                    >
                                        {t('splitOrder.drawer.algoViewRoutePlan')}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* 右侧地图区域 */}
                <div className="flex-1 min-w-0 flex flex-col">
                    {/* 算法分单进度条 */}
                    {splitMode === 'algo' && (
                        <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-2">
                            <ConfigProvider theme={{components: {Steps: {iconSize: 26, fontSize: 13, iconFontSize: 13}}}}>
                                <Steps
                                    current={
                                        algoPhase === 'published' ? 4
                                        : algoPhase === 'routePlanned' ? 3
                                        : algoPhase === 'routePlanning' ? 2
                                        : isAlgoViewMode ? 2
                                        : 0
                                    }
                                    items={[
                                        {title: <span className="font-semibold">{t('splitOrder.drawer.algoStep1')}</span>},
                                        {title: <span className="font-semibold">{t('splitOrder.drawer.algoStep2')}</span>},
                                        {title: <span className="font-semibold">{t('splitOrder.drawer.algoStep3')}</span>,
                                            description: <span className="text-xs text-red-400 whitespace-nowrap">{t('splitOrder.drawer.algoStep3Hint')}</span>},
                                        {title: <span className="font-semibold">{t('splitOrder.drawer.algoStep4')}</span>},
                                    ]}
                                />
                            </ConfigProvider>
                        </div>
                    )}
                    {/* 算法分单：clustering 状态提示条 */}
                    {splitMode === 'algo' && algoPhase === 'clustering' && (
                        <div className="shrink-0 flex items-center justify-between px-6 py-4 text-base" style={{backgroundColor: '#dcfce7'}}>
                            <span className="text-green-700 font-medium">
                                {t('splitOrder.drawer.algoClusteringHint')}
                            </span>
                            <button
                                className="text-red-500 hover:text-red-700 font-semibold shrink-0 ml-6 text-base"
                                onClick={() => {
                                    algoClusteringCancelledRef.current = true;
                                    setAlgoPhase('editing');
                                }}
                            >
                                {t('splitOrder.drawer.algoAbandonPlan')}
                            </button>
                        </div>
                    )}
                    {/* 算法分单：routePlanning 状态提示条 */}
                    {splitMode === 'algo' && algoPhase === 'routePlanning' && (
                        <div className="shrink-0 flex items-center px-6 py-4 text-base" style={{backgroundColor: '#dcfce7'}}>
                            <Spin size="small" style={{marginRight: 8}}/>
                            <span className="text-green-700 font-medium">
                                {t('splitOrder.drawer.algoRoutePlanningHint')}
                            </span>
                        </div>
                    )}
                    <div className="flex-1 min-h-0 relative">
                    {isPrePlanning && (
                        <div className="absolute inset-x-0 top-0 z-10 flex items-center gap-2 bg-indigo-50 border-b border-indigo-200 px-4 py-2 text-indigo-700 text-sm">
                            <Spin size="small"/>
                            <span>{t('splitOrder.drawer.prePlanningLoading')}</span>
                        </div>
                    )}
                    {loadError ? (
                        <div className="flex items-center justify-center h-full text-red-500">{t('splitOrder.drawer.mapLoadFailed')}</div>
                    ) : !isLoaded ? (
                        <div className="flex items-center justify-center h-full">
                            <Spin size="large" tip="Loading Map..."/>
                        </div>
                    ) : (
                        <>
                            <GoogleMap
                                mapContainerStyle={{
                                    width: '100%',
                                    height: '100%',
                                    cursor: (isDrawing || pinningTarget || isRectSelecting) ? 'crosshair' : ''
                                }}
                                center={defaultCenter}
                                zoom={13}
                                onLoad={onLoad}
                                onUnmount={onUnmount}
                                onClick={handleMapClick}
                                onRightClick={handleMapRightClick}
                                onDblClick={handleMapDblClick}
                                onMouseMove={handleMouseMove}
                                clickableIcons={false}
                                options={{
                                    disableDefaultUI: true,
                                    draggable: !isDrawing,
                                    draggableCursor: isDrawing ? 'crosshair' : undefined,
                                    disableDoubleClickZoom: true,
                                    styles: hideMapLabels ? [
                                        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                        { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                        { featureType: 'administrative.locality', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                        { featureType: 'landscape', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                        { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                                    ] : [],
                                }}
                            >
                                {/* 多边形和预览线均由 useEffect 命令式管理 */}

                                {/* Zipcode 围栏叠加层 */}
                                {showZipcodeBoundary && zipcodeBoundaryData.map(entry => {
                                    const isHovered = !isDrawing && (hoveredZipcodeBoundary?.zipcode === entry.zipcode || highlightedZipcodes.has(entry.zipcode));
                                    return (
                                        <Polygon
                                            key={`zb-${entry.zipcode}`}
                                            paths={entry.paths}
                                            options={{
                                                fillColor: '#7c3aed',
                                                fillOpacity: isHovered ? 0.3 : 0.12,
                                                strokeColor: '#7c3aed',
                                                strokeOpacity: isHovered ? 1 : 0.6,
                                                strokeWeight: isHovered ? 2.5 : 1.5,
                                                zIndex: 1,
                                                clickable: false,
                                            }}
                                        />
                                    );
                                })}

                                {showParcelLocation && orderMarkers}

                                {/* 各分段起终点 marker */}
                                {(segments as Array<MapSegment & ZipcodeSegment & StopSegment>).map(seg => {
                                    const palette = getSegPalette(seg);
                                    const startLat = seg.startAddress?.latitude;
                                    const startLng = seg.startAddress?.longitude;
                                    const endLat = seg.endAddress?.latitude;
                                    const endLng = seg.endAddress?.longitude;
                                    return (
                                        <React.Fragment key={seg.id}>
                                            {showStartPoint && startLat != null && startLng != null && (
                                                <Marker
                                                    position={{lat: startLat, lng: startLng}}
                                                    icon={{
                                                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
                                                            `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="28" viewBox="0 0 32 28"><polygon points="16,2 30,26 2,26" fill="${palette.accent}" stroke="#22c55e" stroke-width="3" stroke-linejoin="round"/></svg>`
                                                        ),
                                                        scaledSize: new google.maps.Size(32, 28),
                                                        anchor: new google.maps.Point(16, 28),
                                                    }}
                                                    clickable={false}
                                                />
                                            )}
                                            {showEndPoint && endLat != null && endLng != null && (
                                                <Marker
                                                    position={{lat: endLat, lng: endLng}}
                                                    icon={{
                                                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
                                                            `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="28" viewBox="0 0 32 28"><polygon points="16,2 30,26 2,26" fill="${palette.accent}" stroke="#ef4444" stroke-width="3" stroke-linejoin="round"/></svg>`
                                                        ),
                                                        scaledSize: new google.maps.Size(32, 28),
                                                        anchor: new google.maps.Point(16, 28),
                                                    }}
                                                    clickable={false}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </GoogleMap>

                            {/* Zipcode 围栏 hover tooltip */}
                            {hoveredZipcodeBoundary && (
                                <div
                                    style={{
                                        position: 'fixed',
                                        left: hoveredZipcodeBoundary.x + 12,
                                        top: hoveredZipcodeBoundary.y - 40,
                                        pointerEvents: 'none',
                                        zIndex: 1000,
                                    }}
                                >
                                    <div style={{
                                        background: 'rgba(0,0,0,0.75)',
                                        color: '#fff',
                                        padding: '6px 10px',
                                        borderRadius: 6,
                                        fontSize: 13,
                                        lineHeight: '1.5',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        <div style={{ fontWeight: 600 }}>{hoveredZipcodeBoundary.zipcode}</div>
                                        <div>{t('splitOrder.drawer.zipcodeParcels')}: {hoveredZipcodeBoundary.parcelCount}</div>
                                    </div>
                                </div>
                            )}

                            {/* 自定义地图控件：右上角缩放 + 方向 */}
                            <div className="absolute top-3 right-3 z-10 flex flex-col items-center gap-1.5 pointer-events-auto">
                                {/* 方向控件 */}
                                <div className="flex flex-col items-center">
                                    {panExpanded && (
                                        <button
                                            className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-600 hover:bg-gray-50 mb-0.5"
                                            onClick={() => map?.panBy(0, -80)}
                                        >▲</button>
                                    )}
                                    <div className={`flex items-center gap-0.5 ${panExpanded ? '' : ''}`}>
                                        {panExpanded && (
                                            <button
                                                className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-600 hover:bg-gray-50"
                                                onClick={() => map?.panBy(-80, 0)}
                                            >◀</button>
                                        )}
                                        <button
                                            className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-500 hover:bg-gray-50"
                                            title={t('splitOrder.drawer.directionControl') ?? ''}
                                            onClick={() => setPanExpanded(v => !v)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                <polygon points="8,1 10,5 8,4 6,5"/>
                                                <polygon points="8,15 10,11 8,12 6,11"/>
                                                <polygon points="1,8 5,6 4,8 5,10"/>
                                                <polygon points="15,8 11,6 12,8 11,10"/>
                                                <circle cx="8" cy="8" r="1.5"/>
                                            </svg>
                                        </button>
                                        {panExpanded && (
                                            <button
                                                className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-600 hover:bg-gray-50"
                                                onClick={() => map?.panBy(80, 0)}
                                            >▶</button>
                                        )}
                                    </div>
                                    {panExpanded && (
                                        <button
                                            className="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-gray-600 hover:bg-gray-50 mt-0.5"
                                            onClick={() => map?.panBy(0, 80)}
                                        >▼</button>
                                    )}
                                </div>
                                {/* 缩放控件 */}
                                <button
                                    className="w-8 h-8 bg-white rounded-t shadow flex items-center justify-center text-lg text-gray-600 hover:bg-gray-50 leading-none"
                                    onClick={() => map && map.setZoom((map.getZoom() ?? 13) + 1)}
                                >+</button>
                                <button
                                    className="w-8 h-8 bg-white rounded-b shadow flex items-center justify-center text-lg text-gray-600 hover:bg-gray-50 leading-none -mt-px"
                                    onClick={() => map && map.setZoom((map.getZoom() ?? 13) - 1)}
                                >−</button>
                            </div>

                            {/* 绘制提示语：整个绘制过程中都展示 */}
                            {isDrawing && (
                                <div
                                    className="absolute inset-0 flex items-start justify-center pt-6 pointer-events-none z-10">
                                    <div className="bg-black/60 text-white text-sm px-4 py-2 rounded-full">
                                        {t('splitOrder.drawer.drawingHint')}
                                    </div>
                                </div>
                            )}

                            {/* 标点提示语 */}
                            {pinningTarget && (
                                <div
                                    className="absolute inset-0 flex items-start justify-center pt-6 z-10 pointer-events-none">
                                    <div
                                        className="flex items-center gap-3 bg-black/60 text-white text-sm px-4 py-2 rounded-full pointer-events-auto">
                                        <span>{pinningTarget.field === 'start' ? t('splitOrder.drawer.pinStartHint') : t('splitOrder.drawer.pinEndHint')}</span>
                                        <button
                                            className="text-white/70 hover:text-white underline text-xs"
                                            onClick={() => setPinningTarget(null)}
                                        >
                                            {t('splitOrder.drawer.cancel')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* 地图图层控制面板 */}
                            <div
                                className="absolute bottom-6 left-4 z-10 bg-white rounded-xl shadow-lg px-4 py-3 flex flex-col gap-2 min-w-[160px]"
                                style={{border: '1px solid #e5e7eb'}}>
                                {[
                                    {
                                        label: t('splitOrder.drawer.zipcodeBoundary'),
                                        icon: <span style={{
                                            display: 'inline-block',
                                            width: 14,
                                            height: 14,
                                            background: '#7c3aed',
                                            borderRadius: 3,
                                            opacity: 0.5,
                                        }}/>,
                                        value: showZipcodeBoundary,
                                        setter: setShowZipcodeBoundary
                                    },
                                    {
                                        label: t('splitOrder.drawer.routeLine'),
                                        icon: <span style={{fontSize: 14, color: '#6b7280'}}>〜</span>,
                                        value: showNavTrack && !!routePolylines,
                                        setter: setShowNavTrack,
                                        disabled: !routePolylines
                                    },
                                    {
                                        label: t('splitOrder.drawer.hideMapLabels'),
                                        icon: <span style={{fontSize: 14, color: '#6b7280'}}>🏷</span>,
                                        value: hideMapLabels,
                                        setter: setHideMapLabels
                                    },
                                    {
                                        label: t('splitOrder.drawer.parcelLocation'),
                                        icon: <span style={{fontSize: 14, color: '#6b7280'}}>📍</span>,
                                        value: showParcelLocation,
                                        setter: setShowParcelLocation
                                    },
                                    {
                                        label: t('splitOrder.drawer.deliveryStart'),
                                        icon: <svg width="14" height="14" viewBox="0 0 14 14">
                                            <polygon points="7,1 13,13 1,13" fill="#22c55e"/>
                                        </svg>,
                                        value: showStartPoint,
                                        setter: setShowStartPoint
                                    },
                                    {
                                        label: t('splitOrder.drawer.deliveryEnd'),
                                        icon: <svg width="14" height="14" viewBox="0 0 14 14">
                                            <polygon points="7,1 13,13 1,13" fill="#ef4444"/>
                                        </svg>,
                                        value: showEndPoint,
                                        setter: setShowEndPoint
                                    },
                                ].map(({label, icon, value, setter, disabled}: any) => (
                                    <div key={label} className={`flex items-center justify-between gap-4 ${disabled ? 'opacity-40' : ''}`}>
                                        <div className="flex items-center gap-2">
                                            <span className="flex items-center justify-center w-4 h-4">{icon}</span>
                                            <span className="text-xs text-gray-700 whitespace-nowrap">{label}</span>
                                        </div>
                                        <button
                                            onClick={() => !disabled && setter((v: boolean) => !v)}
                                            className={`relative flex-shrink-0 ${disabled ? 'cursor-not-allowed' : ''}`}
                                            style={{width: 36, height: 20}}
                                        >
                                            <span style={{
                                                display: 'block', width: 36, height: 20, borderRadius: 10,
                                                background: value ? '#7c3aed' : '#d1d5db',
                                                transition: 'background 0.2s',
                                            }}/>
                                            <span style={{
                                                position: 'absolute', top: 3, left: value ? 19 : 3,
                                                width: 14, height: 14, borderRadius: '50%',
                                                background: 'white', transition: 'left 0.2s',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                            }}/>
                                        </button>
                                    </div>
                                ))}

                                {/* 已发布/新进包裹图例 */}
                                {(
                                    <>
                                        <div style={{borderTop: '1px solid #e5e7eb', margin: '4px 0'}}/>
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-2">
                                                <svg width="16" height="20" viewBox="0 0 36 46">
                                                    <path d="M18 2C10.3 2 4 8.3 4 16c0 10.5 14 28 14 28s14-17.5 14-28c0-7.7-6.3-14-14-14z" fill="#3B82F6" stroke="white" strokeWidth="5"/>
                                                </svg>
                                                <span className="text-xs text-gray-700 whitespace-nowrap">{t('splitOrder.drawer.publishedParcel')}</span>
                                            </div>
                                            <button onClick={() => setShowPublishedParcels(v => !v)} className="relative flex-shrink-0" style={{width: 36, height: 20}}>
                                                <span style={{display: 'block', width: 36, height: 20, borderRadius: 10, background: showPublishedParcels ? '#7c3aed' : '#d1d5db', transition: 'background 0.2s'}}/>
                                                <span style={{position: 'absolute', top: 3, left: showPublishedParcels ? 19 : 3, width: 14, height: 14, borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'}}/>
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <svg width="16" height="20" viewBox="0 0 36 46">
                                                <path d="M18 2C10.3 2 4 8.3 4 16c0 10.5 14 28 14 28s14-17.5 14-28c0-7.7-6.3-14-14-14z" fill="#3B82F6" stroke="#DEDFE0" strokeWidth="5"/>
                                            </svg>
                                            <span className="text-xs text-gray-700 whitespace-nowrap">{t('splitOrder.drawer.newParcels')}</span>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* 绘制中取消按钮 */}
                            {isDrawing && (
                                <div className="absolute top-4 right-4 z-10">
                                    <Button
                                        danger
                                        onClick={() => {
                                            setIsDrawing(false);
                                            setPolygonPaths([]);
                                            setMousePos(null);
                                    
                                        }}
                                    >
                                        {t('splitOrder.drawer.cancelDrawing')}
                                    </Button>
                                </div>
                            )}

                            {/* 编辑多边形完成按钮 */}
                            {editingSegId && (() => {
                                const seg = mapSegments.find(s => s.id === editingSegId);
                                const palette = SEGMENT_COLOR_PALETTE[seg?.colorIndex ?? 0];
                                return (
                                    <div className="absolute top-4 right-4 z-10">
                                        <Button
                                            onClick={finishEditSegment}
                                            style={{
                                                backgroundColor: palette.accent,
                                                borderColor: palette.accent,
                                                color: '#fff'
                                            }}
                                        >
                                            {t('splitOrder.drawer.finishEdit')}
                                        </Button>
                                    </div>
                                );
                            })()}

                            {/* 框选中提示 */}
                            {isRectSelecting && rectSelectedOrderIds.length === 0 && (
                                <div className="absolute inset-0 flex items-start justify-center pt-6 z-10 pointer-events-none">
                                    <div className="flex items-center gap-3 bg-black/60 text-white text-sm px-4 py-2 rounded-full pointer-events-auto">
                                        <span>{t('splitOrder.drawer.rectSelectHint')}</span>
                                        <button
                                            className="text-white/70 hover:text-white underline text-xs"
                                            onClick={cancelRectSelect}
                                        >
                                            {t('splitOrder.drawer.cancel')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* 框选完成：改派浮窗 */}
                            {rectSelectedOrderIds.length > 0 && (
                                <div className="absolute top-6 left-1/2 -translate-x-1/2 z-10 bg-white rounded-xl shadow-xl px-5 py-4 flex flex-col gap-3 min-w-[280px]"
                                     style={{border: '1px solid #e5e7eb'}}>
                                    <div className="text-sm font-semibold text-gray-800">
                                        {t('splitOrder.drawer.selectedParcels', {count: rectSelectedOrderIds.length})}
                                    </div>
                                    <Select
                                        showSearch
                                        placeholder={t('splitOrder.drawer.reassignDriver')}
                                        style={{width: '100%'}}
                                        options={driverOptions}
                                        filterOption={driverFilterOption}
                                        onChange={(val: string) => handleRectReassign(val)}
                                    />
                                    <button
                                        className="text-sm text-gray-500 hover:text-gray-700 underline self-center"
                                        onClick={cancelRectSelect}
                                    >
                                        {t('splitOrder.drawer.cancelSelect')}
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* 查看包裹悬浮表格 */}
                    {hoveredSegId && (() => {
                        let segDriverName = '';
                        let enclosedOrders: typeof orderList = [];
                        const segs: {id: string; driverAccountId: string; assignedOrderIds: number[]}[] =
                            splitMode === 'map' ? mapSegments : splitMode === 'zipcode' ? zipcodeSegments : splitMode === 'stop' ? stopSegments : algorithmSegments;
                        const seg = segs.find(s => s.id === hoveredSegId);
                        segDriverName = seg?.driverAccountId ?? '';
                        const idSet = new Set((seg?.assignedOrderIds ?? []).map(Number));
                        enclosedOrders = orderList.filter(o => o.orderId != null && idSet.has(Number(o.orderId)));
                        const driverDisplayName = driverOptions.find(d => d.value === segDriverName)?.name ?? segDriverName ?? '—';
                        return (
                            <div
                                className="absolute top-0 left-0 h-full flex flex-col bg-white shadow-xl z-10 border-r border-gray-200"
                                style={{width: 460}}>

                                {/* 关闭按钮 */}
                                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white shrink-0">
                                    <span className="font-semibold text-gray-800">{t('splitOrder.drawer.parcelList')} ({enclosedOrders.length})</span>
                                    <button onClick={() => setHoveredSegId(null)}
                                            className="text-gray-400 hover:text-gray-600 transition-colors">
                                        <CloseOutlined/>
                                    </button>
                                </div>

                                {/* 表格 */}
                                <div className="flex-1 overflow-y-auto" ref={el => { if (el) el.scrollTop = 0; }}>
                                    <Table
                                        size="small"
                                        rowKey="orderId"
                                        pagination={false}
                                        dataSource={[...enclosedOrders].sort((a, b) => {
                                            const getStop = (o: typeof orderList[0]) => {
                                                if (splitMode === 'algo' && (algoPhase === 'routePlanned' || algoPhase === 'published') && o.orderId != null) return algoStopMap.get(Number(o.orderId)) ?? Infinity;
                                                if (isPublished && splitMode !== 'stop') return (o.orderId != null ? routePlanningStopMap.get(Number(o.orderId)) : undefined) ?? Infinity;
                                                if (splitMode === 'stop' || isPublished) return o.stopNumber ?? Infinity;
                                                return Infinity;
                                            };
                                            return getStop(a) - getStop(b);
                                        })}
                                        locale={{emptyText: t('splitOrder.drawer.noData')}}
                                        columns={[
                                            {
                                                title: t('splitOrder.drawer.columnZipcode'),
                                                dataIndex: 'zipcode',
                                                width: 90,
                                                render: (val: string) => val ?? '—',
                                            },
                                            {
                                                title: t('splitOrder.drawer.columnStop'),
                                                key: 'stop',
                                                width: 90,
                                                render: (_: any, o: typeof orderList[0]) => {
                                                    let stopDisplay: number | undefined;
                                                    if (splitMode === 'algo' && (algoPhase === 'routePlanned' || algoPhase === 'published') && o.orderId != null) {
                                                        stopDisplay = algoStopMap.get(Number(o.orderId));
                                                    } else if (isPublished && splitMode !== 'stop') {
                                                        stopDisplay = o.orderId != null ? routePlanningStopMap.get(Number(o.orderId)) : undefined;
                                                    } else if (splitMode === 'stop') {
                                                        stopDisplay = o.stopNumber ?? undefined;
                                                    }
                                                    return stopDisplay ?? '—';
                                                },
                                            },
                                            {
                                                title: t('splitOrder.drawer.columnTrackingNo'),
                                                key: 'trackingNumber',
                                                render: (_: any, o: typeof orderList[0]) => {
                                                    const isNewOrder = isPublished && publishedOrderIdSet.size > 0 && o.orderId != null && !publishedOrderIdSet.has(Number(o.orderId));
                                                    return (
                                                        <div className="flex items-center gap-1.5">
                                                            {isNewOrder && (
                                                                <span className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 leading-none shrink-0">{t('splitOrder.drawer.newTag')}</span>
                                                            )}
                                                            <span className="font-mono text-sm text-indigo-500">{o.trackingNumber}</span>
                                                            <button
                                                                className="text-gray-400 hover:text-indigo-500 transition-colors shrink-0"
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(o.trackingNumber ?? '');
                                                                    notification.success({ message: t('splitOrder.drawer.copySuccess'), placement: 'topLeft', duration: 1.5 });
                                                                }}
                                                            >
                                                                <CopyOutlined style={{fontSize: 14}}/>
                                                            </button>
                                                        </div>
                                                    );
                                                },
                                            },
                                            {
                                                title: t('splitOrder.drawer.currentDriver'),
                                                key: 'driver',
                                                width: 150,
                                                render: () => driverDisplayName,
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        );
                    })()}
                </div>
                </div>
            </div>

            {/* 发布确认弹窗 */}
            <SplitRoutePublishConfirmModal
                visible={confirmModalVisible}
                onCancel={() => setConfirmModalVisible(false)}
                onConfirm={handleConfirmPublishDate}
                warehouseNow={resolveWarehouseNow(warehouseCurrentTime)}
                areaName={record?.areaName ?? ''}
                stats={currentStats}
            />

            {/* 异步发布进度弹窗（含校验错误展示） */}
            <SplitOrderPublishAllModal
                visible={publishModalVisible && !!warehouseId}
                onCancel={() => {
                    setPublishModalVisible(false);
                    setValidationErrors([]);
                }}
                selectedTaskDate={publishTaskDate}
                warehouseId={warehouseId!}
                tableData={record ? [{
                    key: record.key,
                    areaName: record.areaName,
                    dspName: record.dspName,
                    totalParcels: realTotalParcels
                }] : []}
                onStartPublish={handleStartPublish}
                isAsyncPublishing={false}
                asyncPublishProgress={undefined}
                validationErrors={validationErrors}
            />

            {/* 数据过期弹窗 */}
            <DataStaleModal
                visible={dataStaleVisible}
                parcelsStale={dataStaleResult?.parcelsStale}
                dspStaleAreas={dataStaleResult?.dspStaleAreas}
                areaNameMap={record?.areaId && record?.areaName ? { [record.areaId]: record.areaName } : {}}
                dspNameMap={record?.dspId && record?.dspName ? { [record.dspId]: record.dspName } : {}}
                onRefresh={() => {
                    setDataStaleVisible(false);
                    const hasDspStale = (dataStaleResult?.dspStaleAreas?.length ?? 0) > 0;
                    setDataStaleResult(null);
                    if (hasDspStale) {
                        // DSP 变更需要回到主页面刷新
                        onClose();
                    }
                    // 抽屉内刷新 orderList + 通知主页面同步刷新
                    void refetchRouteOrders();
                    onDataStaleRefresh?.();
                }}
            />

            {/* 二次发布冲突弹窗 */}
            <RepublishConflictModal
                visible={republishConflictVisible}
                orders={republishConflictOrders}
                onStillPublish={() => {
                    // 派送日期已在确认弹窗中选定，直接进入发布
                    setRepublishConflictVisible(false);
                    setValidationErrors([]);
                    setPublishModalVisible(true);
                }}
                onCancel={() => setRepublishConflictVisible(false)}
            />

            <AddressInputModal
                open={!!addressModalTarget}
                onConfirm={handleAddressConfirm}
                onCancel={() => setAddressModalTarget(null)}
            />

            {/* 算法分单引导弹窗*/}
            <Modal
                open={algoGuideVisible}
                onCancel={() => setAlgoGuideVisible(false)}
                footer={null}
                width={480}
                style={{ top: 120 }}
                maskClosable={false}
            >
                <div className="pt-2 px-1">
                    <div className="text-sm font-semibold mb-2" style={{ color: '#4f46e5' }}>
                        {t('splitOrder.algoGuide.heading')}
                    </div>
                    <div className="text-2xl font-bold text-gray-900 leading-snug mb-6 whitespace-pre-line">
                        {t('splitOrder.algoGuide.title')}
                    </div>

                    <div className="flex flex-col gap-5 mb-8">
                        {[
                            { label: t('splitOrder.algoGuide.item1Label'), pre: t('splitOrder.algoGuide.item1DescPre'), hl: t('splitOrder.algoGuide.item1DescHl') },
                            { label: t('splitOrder.algoGuide.item2Label'), pre: t('splitOrder.algoGuide.item2DescPre'), hl: t('splitOrder.algoGuide.item2DescHl') },
                            { label: t('splitOrder.algoGuide.item3Label'), pre: t('splitOrder.algoGuide.item3Desc'), hl: '' },
                        ].map((it, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                                <CheckCircleFilled style={{ color: '#22c55e', fontSize: 18, marginTop: 2 }} />
                                <div>
                                    <div className="text-base font-bold text-gray-900">{it.label}</div>
                                    <div className="text-sm text-gray-400 mt-0.5">
                                        {it.pre}
                                        {it.hl && <span className="font-bold" style={{ color: '#ef4444' }}>{it.hl}</span>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <Button
                        type="primary"
                        block
                        size="large"
                        style={{ height: 52, fontSize: 16, fontWeight: 600, backgroundColor: '#4f46e5', borderColor: '#4f46e5' }}
                        onClick={() => {
                            setAlgoGuideVisible(false);
                            setSplitMode('algo');
                            setHoveredSegId(null);
                            cancelDrawing();
                            cancelRectSelect();
                        }}
                    >
                        {t('splitOrder.algoGuide.switchToAlgo')}
                    </Button>
                    <div className="flex items-center justify-center gap-3 mt-3">
                        <button
                            type="button"
                            className="text-sm font-semibold bg-transparent border-0 cursor-pointer"
                            style={{ color: '#4f46e5' }}
                            onClick={() => {
                                setAlgoGuideVisible(false);
                                handleOpenVideoTutorial('ALGO');
                            }}
                        >
                            {t('splitOrder.algoGuide.viewGuide')}
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                            type="button"
                            className="text-sm text-gray-400 bg-transparent border-0 cursor-pointer hover:text-gray-600"
                            onClick={() => setAlgoGuideVisible(false)}
                        >
                            {t('splitOrder.algoGuide.later')}
                        </button>
                    </div>
                </div>
            </Modal>
        </Drawer>
    );
};

export default SplitRouteDrawer;
